import React, { useState, useEffect, useMemo, useCallback, useRef, Suspense } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  BackgroundVariant,
  type Node,
  type Edge,
  useNodesState,
  useEdgesState,
  type EdgeChange,
  type Connection,
  useReactFlow,
  useStore,
  type Viewport,
  SelectionMode
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';


import SchemaEditorHeader from './SchemaEditorHeader';
import { getSchemaTypeFromPath } from './SchemaTypeConfig';
import { createSchemaManager, type SchemaType } from './SchemaManager';
import { createSchema } from '../../api/schemas';
import CableInstanceEditModal from '../modals/CableInstanceEditModal';
import CableSpecEditModal from '../modals/CableSpecEditModal';
import DeviceInstanceEditModal from '../modals/DeviceInstanceEditModal';
import DeviceSpecEditModal from '../modals/DeviceSpecEditModal';
// NEW: Import specialized node components for schema isolation
import {
  MainSchemaDeviceNode,
  MainSchemaActuatorNode,
  MainSchemaTerminalNode,
  TerminalSchemaDeviceNode
} from './nodes';
import { getElectricalPanels, getElectricalPanelById } from '../../api/electricalPanels';
import { getAvailableDevicesForSchemaType, getAvailableCablesForSchemaType, getAllProjectConnections, getAvailableTerminalCables, updateDeviceMainSchemaStatus, getDeviceInstanceById } from '../../api/deviceInstances';
import { getDeviceTypes } from '../../api/deviceTypes';
import { getSpecificationItem, type Specification } from '../../api/specification';
import { getConnections, createConnection, updateConnection, deleteConnection, getDeviceContacts, getCableToTerminalConnections } from '../../api/connections';
import { addDeviceToSchema, addDeviceToSchemaWithIsolation, updateDevicePositionByIds, getSchemaLayoutWithIsolation, updateDevicePositionWithIsolation, getSchema } from '../../api/schemas';
import TerminalBlockNode from '../TerminalBlockNode';
import SingleTerminalInstanceNode from '../SingleTerminalInstanceNode';
import type { DeviceContact } from '../../types/connections';
import SmartLinePanel from '../SmartLinePanel';
import { resolveOverlappingRoutes, type RoutePoint } from '../../utils/edgeRouting';
import CustomEdge from '../CustomEdge';
import { EditableEdgeComponent } from '../../edges/EditableEdge';
import { Algorithm } from '../../edges/EditableEdge/constants';

// Helper to map wire properties to Algorithm
const getAlgorithmFromWireProps = (type?: string): Algorithm => {
  switch (type) {
    case 'step': return Algorithm.Step;
    case 'straight': return Algorithm.Linear;
    case 'smoothstep': return Algorithm.CatmullRom;
    case 'bezier':
    default: return Algorithm.BezierCatmullRom;
  }
};

const getWireTypeFromAlgorithm = (algorithm?: Algorithm): string | undefined => {
  switch (algorithm) {
    case Algorithm.Step: return 'step';
    case Algorithm.Linear: return 'straight';
    case Algorithm.CatmullRom: return 'smoothstep';
    case Algorithm.BezierCatmullRom: return 'bezier';
    default: return undefined;
  }
};

// Helper to map path points to ControlPointData
const mapPathPointsToControlPoints = (points: RoutePoint[]): any[] => {
  return points.map((p, i) => ({ x: p.x, y: p.y, id: String(i) }));
};
// import TestCustomEdge from '../TestCustomEdge';

import ManualRouteEditor from '../ManualRouteEditor';
import { useAuth } from '../../AuthContext';
import { PermissionRow } from '../../api/admin';
import { apiClient } from '../../api/client';
// import CableSymbol from '../CableSymbol'; // Temporarily disabled for testing

// NEW: DeviceNode removed - replaced with specialized schema components
// (MainSchemaDeviceNode, TerminalSchemaDeviceNode, KnxSchemaDeviceNode, Rs485SchemaDeviceNode)

// DEBUG MODE CONFIGURATION
// Set to true to filter elements for debugging, false to show all elements
const GLOBAL_DEBUG_MODE = false;
 // Cable 1135, Actuator 1390

// Feature flags
const USE_CUSTOM_SELECTION_OVERLAY = true;

// Performance profiling: set to true to log timings in console (selection, drag, connectionEdges, onConnect)
const PERF_DEBUG = false;


interface UniversalSchemaEditorProps {
  // Allow overriding schema type for specific use cases
  forceSchemaType?: string;
}

interface SchemaEditorContentProps {
  schemaType: SchemaType;
  projectId: number;
  schemaId: number;
  electricalPanelId?: number;
  blockId?: number;
  shouldShowCreateModal?: boolean;
  /** Device instance ID to focus and center on when schema loads (from ?focus= URL param) */
  initialFocusNodeId?: string;
  initialSchema?: import('../../types').Schema;
}

interface EdgeLabelData {
  id: string;
  text: string;
  position: number;
}

// Background pattern mapping
const getBackgroundVariant = (pattern: string): BackgroundVariant => {
  switch (pattern) {
    case 'dots': return BackgroundVariant.Dots;
    case 'grid': return BackgroundVariant.Lines;
    case 'lines': return BackgroundVariant.Cross;
    default: return BackgroundVariant.Dots;
  }
};

// NEW: Node types mapping with specialized components for schema isolation
const getNodeTypes = (schemaType: string) => {
  const baseTypes = {
    device: MainSchemaDeviceNode,
    actuator: MainSchemaActuatorNode, // NEW: Specialized component for actuators
    terminalBlock: TerminalBlockNode,
    terminalInstance: SingleTerminalInstanceNode, // NEW: Individual terminal instances
    autoTerminal: MainSchemaTerminalNode, // NEW: Auto-placed terminal blocks
    cable: MainSchemaDeviceNode
  };

  switch (schemaType) {
    case 'terminal':
      return { 
        device: TerminalSchemaDeviceNode,
        terminalBlock: TerminalBlockNode,
        terminalInstance: SingleTerminalInstanceNode, // NEW: Individual terminal instances
        cable: TerminalSchemaDeviceNode
      };
    case 'knx':
    case 'rs485':
    case 'breaker':
    case 'ethernet':
      return baseTypes; // Используем стандартные компоненты для breaker и ethernet
    case 'main':
    default:
      return baseTypes;
  }
};

function SchemaEditorContent({ schemaType, projectId, schemaId, electricalPanelId, blockId, shouldShowCreateModal, initialFocusNodeId, initialSchema }: SchemaEditorContentProps) {
  // console.log('🔵 CustomEdge imported successfully:', typeof CustomEdge);
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  
  // Add schema-editor-page class to body for mobile CSS fixes
  useEffect(() => {
    document.body.classList.add('schema-editor-page');
    return () => {
      document.body.classList.remove('schema-editor-page');
    };
  }, []);

  // ============= PERMISSIONS CHECK =============
  // Загрузка прав пользователя на проект и схему
  const { data: projectAccessData } = useQuery({
    queryKey: ['my-project-access', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return { access: [] }
      const response = await apiClient.get('/api/auth/my-project-access')
      return response.data as { access: Array<{ project_id: number; role_in_project: string }> }
    },
    enabled: !!currentUser?.id && currentUser.role !== 'system_admin',
  })

  // Загрузка всех permissions для текущего пользователя и проекта
  const { data: permissionsData } = useQuery({
    queryKey: ['my-permissions', currentUser?.id, projectId],
    queryFn: async () => {
      if (!currentUser?.id || !projectId) return { permissions: [] }
      const response = await apiClient.get('/api/auth/my-permissions', {
        params: { project_id: Number(projectId) },
      })
      return response.data as { permissions: PermissionRow[] }
    },
    enabled: !!currentUser?.id && !!projectId && currentUser.role !== 'system_admin',
  })

  const allPermissions: PermissionRow[] = permissionsData?.permissions ?? []

  // Helper функция для получения эффективного права на ресурс (с учётом наследования)
  const getEffectivePermission = useCallback((
    resourceType: 'electrical_panel' | 'schema',
    resourceId: number | null,
  ): string | null => {
    if (currentUser?.role === 'system_admin') {
      return 'manage'
    }

    if (!projectId || !currentUser?.id) return null

    // Проверяем явное permission
    const explicit = allPermissions.find(
      (p) =>
        p.project_id === Number(projectId) &&
        p.resource_type === resourceType &&
        p.resource_id === (resourceId ? String(resourceId) : null) &&
        p.permission !== 'deny',
    )
    if (explicit) {
      return explicit.permission
    }

    // Проверяем наследование от роли проекта
    const access = projectAccessData?.access?.find(
      (a) => a.project_id === Number(projectId),
    )
    if (access && access.role_in_project) {
      const roleMap: Record<string, string> = {
        viewer: 'view',
        editor: 'edit',
        project_admin: 'manage',
      }
      return roleMap[access.role_in_project] || null
    }

    return null
  }, [currentUser, projectId, allPermissions, projectAccessData])

  // Проверка, есть ли только просмотр (без edit или manage)
  const isReadOnly = useMemo(() => {
    if (currentUser?.role === 'system_admin') return false
    if (!schemaId) return false
    
    const perm = getEffectivePermission('schema', schemaId)
    // Если разрешение только 'view' (без 'edit' или 'manage') - режим только просмотр
    const result = perm === 'view'
    // Debug log для проверки режима только просмотр
    if (result) {
      console.log('🔒 Read-only mode enabled for schema:', schemaId, 'permission:', perm, 'user:', currentUser?.email)
    }
    return result
  }, [currentUser, schemaId, getEffectivePermission])

  // NEW: Helper functions to determine schema behavior
  const shouldLoadLayoutData = useMemo(() => {
    // Load layout for all schemas except terminal (all use same layout system)
    return schemaType !== 'terminal' && !!schemaId;
  }, [schemaType, schemaId]);

  const shouldLoadLibraryData = useMemo(() => {
    // Load library for all schemas except terminal
    return schemaType !== 'terminal' && !!projectId && !!electricalPanelId;
  }, [schemaType, projectId, electricalPanelId]);

  const shouldShowMainSchemaFeatures = useMemo(() => {
    // Show main schema features for all schemas except terminal
    return schemaType !== 'terminal';
  }, [schemaType]);

  const shouldShowTerminalFeatures = useMemo(() => {
    // Show terminal-specific features (auto-terminals) on all schemas EXCEPT terminal block schemas
    return schemaType !== 'terminal';
  }, [schemaType]);
  
  
  // OLD: const config = getSchemaConfig(schemaType);
  // NEW: SchemaManager for full isolation
  const schemaManager = useMemo(() => {
    const currentSchemaId = schemaId || blockId || 1;
    return createSchemaManager(schemaType as SchemaType, currentSchemaId, {
      projectId,
      userId: currentUser?.id,
    });
  }, [schemaType, schemaId, blockId, projectId, currentUser?.id]);
  
  // Keep backward compatibility
  const config = schemaManager.getConfig();
  // NEW: Use schema type for specialized node components
  const nodeTypes = useMemo(() => ({
    ...getNodeTypes(schemaType)
  }), [schemaType]);
  
  // Define edge types - empty for now, use built-in types
  const edgeTypes = useMemo(() => ({
    custom: CustomEdge,
    'editable-edge': EditableEdgeComponent
  }), []);
  
  // State
  const [nodes, setNodes, originalOnNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, originalOnEdgesChange] = useEdgesState<Edge>([]);
  
  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    // Check if changes are actually modifying anything
    const meaningfulChanges = changes.filter(change => {
      if (change.type === 'select') return true;
      if (change.type === 'remove') return true;
      if (change.type === 'add') return true;
      if (change.type === 'replace') return true;
      return false;
    });
    
    if (meaningfulChanges.length > 0) {
      originalOnEdgesChange(meaningfulChanges);
    }
  }, [originalOnEdgesChange]);

  const previousEdgesRef = useRef<Edge[]>([]);
  const edgeSaveTimerRef = useRef<number | null>(null);
  const edgePropertiesSaveTimersRef = useRef<Record<string, number>>({});
  const edgeLocalSavePendingRef = useRef<Record<string, boolean>>({});






  
  const [showCableLibrary, setShowCableLibrary] = useState(false);
  
  // NEW: Collapse states for main schema library sections (collapsed by default after page refresh)
  const [showCablesSection, setShowCablesSection] = useState(false);

  
  // NEW: Collapse states for device type sections
  const [showDeviceTypeSections, setShowDeviceTypeSections] = useState<Record<string, boolean>>({
    lighting: false,
    outlet: false,
    switch: false,
    sensor: false,
    actuator: false,
    breaker: false,
    other: false
  });
  
  // NEW: Library search query state
  const [librarySearchQuery, setLibrarySearchQuery] = useState('');
  

  
  // Track elements on canvas
  const [cablesOnCanvas, setCablesOnCanvas] = useState<Set<number>>(new Set());
  const [devicesOnCanvas, setDevicesOnCanvas] = useState<Set<number>>(new Set());
  
  // NEW: Missing states for electrical panel management  
  const [selectedElectricalPanel, setSelectedElectricalPanel] = useState<number | null>(2); // Default to panel 2
  const [showCreateSchemaModal, setShowCreateSchemaModal] = useState(shouldShowCreateModal || false);
  
  // Device contacts cache for main schemas
  const [deviceContactsCache, setDeviceContactsCache] = useState<Record<number, DeviceContact[]>>({});
  
  // Smart Line Editing states
  const [selectedEdge, setSelectedEdge] = useState<any>(null);
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [selectedEdges, setSelectedEdges] = useState<Edge[]>([]);
  
  // PDF Export loading state
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  
  // Grid snapping states with localStorage persistence
  const [isGridSnappingEnabled, setIsGridSnappingEnabled] = useState(() => {
    const saved = localStorage.getItem('universal-schema-grid-snapping-enabled');
    return saved ? JSON.parse(saved) : false;
  });
  const [gridStep, setGridStep] = useState(() => {
    const saved = localStorage.getItem('universal-schema-grid-step');
    return saved ? parseInt(saved) : 20;
  });
  const [showGridSettings, setShowGridSettings] = useState(false);
  
  // Layers states
  const [showLayersPanel, setShowLayersPanel] = useState(false);
  const [layerFilters, setLayerFilters] = useState<Record<string, boolean>>({
    showBreakers: true,
    showSensors: true,
    showCables: true,
    showControllers: true,
    showReserve: true // по умолчанию показываем все
  });
  
      // Grid snapping preview states - убираем, так как больше не нужны при резком примагничивании
    // const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
    // const [gridPreviewPosition, setGridPreviewPosition] = useState<{ x: number; y: number } | null>(null);
  

  // Keep single selected edge in sync when using custom selection
  useEffect(() => {
    if (selectedEdges.length > 0) {
      setSelectedEdge(selectedEdges[0]);
    } else {
      setSelectedEdge(null);
    }
    // Sync CSS classes for visual selection state
    setTimeout(() => {
      document.querySelectorAll('.react-flow__edge').forEach(el => el.classList.remove('selected'));
      selectedEdges.forEach(e => {
        const el = document.querySelector(`[data-id="${e.id}"]`);
        if (el) el.classList.add('selected');
      });
    }, 0);
  }, [selectedEdges]);

  // Handle click outside grid settings popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const gridButton = target.closest('button[title*="Примагничивание"]');
      const gridPopup = target.closest('.grid-settings-popup');
      
      if (!gridButton && !gridPopup && showGridSettings) {
        setShowGridSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showGridSettings]);

  // Save grid snapping settings to localStorage
  useEffect(() => {
    localStorage.setItem('universal-schema-grid-snapping-enabled', JSON.stringify(isGridSnappingEnabled));
  }, [isGridSnappingEnabled]);

  useEffect(() => {
    localStorage.setItem('universal-schema-grid-step', gridStep.toString());
  }, [gridStep]);

  // Live preview selection during box-drag (adds/removes 'selected-preview' class only)
  const isBoxSelectingRef = React.useRef<boolean>(false);
  const previewRafRef = React.useRef<number | null>(null);
  // Preview cache to avoid heavy DOM reads on every move
  type PreviewEntry = { id: string; el: HTMLElement; bbox: { left:number; right:number; top:number; bottom:number }; points: { x:number; y:number }[] };
  const previewCacheRef = React.useRef<PreviewEntry[] | null>(null);
  const lastPreviewIdsRef = React.useRef<Set<string>>(new Set());
  const buildPreviewCache = React.useCallback(() => {
    const containerEl = flowWrapperRef.current;
    if (!containerEl) return;
    const entries: PreviewEntry[] = [];
    const cBox = containerEl.getBoundingClientRect();
    const paths = (document.querySelectorAll('.react-flow__edge-path') as NodeListOf<SVGPathElement>);
    paths.forEach((p) => {
      const wrapper = p.closest('.react-flow__edge') as HTMLElement | null;
      if (!wrapper) return;
      const id = wrapper.getAttribute('data-id');
      if (!id) return;
      const pBox = p.getBoundingClientRect();
      const bbox = { left: pBox.left - cBox.left, right: pBox.right - cBox.left, top: pBox.top - cBox.top, bottom: pBox.bottom - cBox.top };
      const points: {x:number;y:number}[] = [];
      try {
        const total = p.getTotalLength();
        const samples = Math.min(30, Math.max(8, Math.floor(total / 20)));
        for (let i = 0; i <= samples; i++) {
          const d = (total * i) / samples;
          const pt = p.getPointAtLength(d);
          points.push({ x: pt.x + pBox.left - cBox.left, y: pt.y + pBox.top - cBox.top });
        }
      } catch { /* non-SVG path? skip samples */ }
      entries.push({ id, el: wrapper, bbox, points });
    });
    previewCacheRef.current = entries;
  }, []);

  const handleSelectionPreview = React.useCallback(() => {
    if (previewRafRef.current != null) return;
    previewRafRef.current = window.requestAnimationFrame(() => {
      previewRafRef.current = null;
      // Try built-in selection box first
      let rectEl = document.querySelector('.react-flow__selection') as HTMLElement | null;
      if (!rectEl && flowWrapperRef.current) {
        // Fallback to our overlay rect when flag is on
        rectEl = USE_CUSTOM_SELECTION_OVERLAY ? (flowWrapperRef.current.querySelector('.custom-selection-rect') as HTMLElement | null) : null;
      }
      if (!rectEl) return;
      const rect = rectEl.getBoundingClientRect();
      const containerEl = flowWrapperRef.current;
      if (!containerEl) return;
      const cBox = containerEl.getBoundingClientRect();
      const sel = { left: rect.left - cBox.left, right: rect.right - cBox.left, top: rect.top - cBox.top, bottom: rect.bottom - cBox.top };
      if (!previewCacheRef.current) buildPreviewCache();
      const cache = previewCacheRef.current || [];
      const nextIds: string[] = [];
      for (const entry of cache) {
        // bbox early check
        let intersects = !(entry.bbox.right <= sel.left || entry.bbox.left >= sel.right || entry.bbox.bottom <= sel.top || entry.bbox.top >= sel.bottom);
        if (intersects && entry.points.length) {
          intersects = false;
          for (const pt of entry.points) {
            if (pt.x > sel.left && pt.x < sel.right && pt.y > sel.top && pt.y < sel.bottom) { intersects = true; break; }
          }
        }
        if (intersects) nextIds.push(entry.id);
      }
      // Diff classes to minimize DOM ops
      const prev = lastPreviewIdsRef.current;
      const next = new Set(nextIds);
      if (cache.length) {
        for (const entry of cache) {
          const shouldHave = next.has(entry.id);
          const had = prev.has(entry.id);
          if (shouldHave && !had) entry.el.classList.add('selected-preview');
          else if (!shouldHave && had) entry.el.classList.remove('selected-preview');
        }
      }
      lastPreviewIdsRef.current = next;
    });
  }, [buildPreviewCache]);

  // Enable built-in selection rectangle and hook edges into it
  const onSelectionStart = useCallback(() => {
    isBoxSelectingRef.current = true;
  }, []);

  // While selection is dragging, update preview
  const onSelectionChangePreview = useCallback(() => {
    if (!isBoxSelectingRef.current) return;
    handleSelectionPreview();
  }, [handleSelectionPreview]);
  const onSelectionEnd = useCallback(() => {
    // After ReactFlow finishes the drag-selection, compute edges inside the selection rect
    setTimeout(() => {
      isBoxSelectingRef.current = false;
      // Cleanup preview classes
      document.querySelectorAll('.react-flow__edge').forEach(el => el.classList.remove('selected-preview'));
      // no listeners to remove
      const selectionRect = document.querySelector('.react-flow__selection') as HTMLElement | null;
      if (!selectionRect) return;
      const rect = selectionRect.getBoundingClientRect();
      let selected: Edge[] = [];
      const prev = selectedEdges;
      const pathNodes = document.querySelectorAll('.react-flow__edge-path');
      pathNodes.forEach((p: Element) => {
        const edgeWrapper = p.closest('.react-flow__edge') as HTMLElement | null;
        if (!edgeWrapper) return;
        const edgeId = edgeWrapper.getAttribute('data-id');
        if (!edgeId) return;
        const pBoxAbs = (p as HTMLElement).getBoundingClientRect();
        let intersects = pBoxAbs.right > rect.left && pBoxAbs.left < rect.right && pBoxAbs.bottom > rect.top && pBoxAbs.top < rect.bottom;
        if (!intersects && (p instanceof SVGPathElement)) {
          try {
            const total = (p as SVGPathElement).getTotalLength();
            const step = Math.max(4, Math.floor(total / 50));
            for (let d = 0; d <= total; d += step) {
              const pt = (p as SVGPathElement).getPointAtLength(d);
              const px = pt.x + pBoxAbs.left;
              const py = pt.y + pBoxAbs.top;
              if (px > rect.left && px < rect.right && py > rect.top && py < rect.bottom) { intersects = true; break; }
            }
          } catch {}
        }
        if (intersects) {
          const edge = edgesRef.current.find((e: Edge) => e.id === edgeId);
          if (edge && !selected.some(e => e.id === edge.id)) selected.push(edge);
        }
      });
      // Merge with previous if Shift/Ctrl/meta was pressed during drag
      const merged = (window.event && ((window.event as MouseEvent).shiftKey || (window.event as MouseEvent).ctrlKey || (window.event as MouseEvent).metaKey))
        ? [...prev, ...selected.filter(e => !prev.some(pe => pe.id === e.id))]
        : selected;
      setSelectedEdges(merged);
      // Sync CSS class
      setTimeout(() => {
        document.querySelectorAll('.react-flow__edge').forEach(el => el.classList.remove('selected'));
        merged.forEach(e => {
          const el = document.querySelector(`[data-id="${e.id}"]`);
          if (el) el.classList.add('selected');
        });
      }, 0);
    }, 10);
  }, [selectedEdges]);
  // Custom box selection overlay state and container ref
  const flowWrapperRef = useRef<HTMLDivElement>(null);
  const [selectionRect, setSelectionRect] = useState<{ x1: number; y1: number; x2: number; y2: number; active: boolean }>({ x1: 0, y1: 0, x2: 0, y2: 0, active: false });
  // Refs to always have fresh nodes/edges inside global listeners
  const nodesRef = useRef<Node[]>([]);
  const edgesRef = useRef<Edge[]>([]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  // NOTE: sync edgesRef later, after connectionEdges is declared below
  
  // Manual route editing state
  const [showManualRouteEditor, setShowManualRouteEditor] = useState(false);
  const [selectedEdgeForRouting, setSelectedEdgeForRouting] = useState<Edge | null>(null);
  
  // Manual route waypoints state for real-time editing
  const [manualRoutes, setManualRoutes] = useState<Record<string, any[]>>({});
  // Local overrides for wire properties applied immediately from SmartLinePanel
  const manualRoutesRef = useRef<Record<string, any[]>>({});
  const handleWaypointUpdateRef = useRef<((edgeId: string, newWaypoints: RoutePoint[]) => void) | null>(null);
  const handleEdgeLabelsUpdateRef = useRef<((edgeId: string, labels: EdgeLabelData[]) => void) | null>(null);
  
  // Function to determine edge type based on connected device types
  const getEdgeTypeForConnection = useCallback((fromInstanceId: number, toInstanceId: number, nodes: Node[]): string => {
    // Find source and target nodes
    const sourceNode = nodes.find(node => node.id === fromInstanceId.toString());
    const targetNode = nodes.find(node => node.id === toInstanceId.toString());
    
    if (!sourceNode || !targetNode) return 'default'; // fallback
    
    const sourceType = sourceNode.data?.deviceType || 'unknown';
    const targetType = targetNode.data?.deviceType || 'unknown';
    
      // Actuator to Actuator connections should be step (Г-образные с прямыми углами)
  // Using 'step' instead of 'smoothstep' to prevent automatic routing conflicts
  if (sourceType === 'actuator' && targetType === 'actuator') {
    return 'step';
  }
  
  // All other connections should use default (bezier curves)
  return 'default';
  }, [nodes]);
  // Debounced saving of routes
  const routeSaveTimersRef = useRef<Record<string, number>>({});
  const lastWaypointsRef = useRef<Record<string, RoutePoint[]>>({});
  
  // Debug state changes
  useEffect(() => {
    // console.log('ManualRouteEditor state changed:', { showManualRouteEditor, selectedEdgeForRouting: selectedEdgeForRouting?.id });
  }, [showManualRouteEditor, selectedEdgeForRouting]);

  // Grid snapping helper function
  const snapToGrid = useCallback((x: number, y: number): { x: number; y: number } => {
    if (!isGridSnappingEnabled) {
      return { x, y };
    }
    
    const snappedX = Math.round(x / gridStep) * gridStep;
    const snappedY = Math.round(y / gridStep) * gridStep;
    
    return { x: snappedX, y: snappedY };
  }, [isGridSnappingEnabled, gridStep]);
  



  
  // Search functionality states
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Element info panel state (left sidebar instead of modal)
  const [showElementInfo, setShowElementInfo] = useState(false);
  
  // Modal states for editing
  const [showInstanceEditModal, setShowInstanceEditModal] = useState(false);
  const [showSpecEditModal, setShowSpecEditModal] = useState(false);
  const [selectedSpecification, setSelectedSpecification] = useState<any>(null);
  

  
  const [searchResults, setSearchResults] = useState<Array<{
    id: string;
    name: string;
    description?: string;
    type: 'cable' | 'device' | 'terminal_block';
  }>>([]);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  
  // Cable library search state
  const [cableSearchQuery, setCableSearchQuery] = useState('');
  
  // Element info panel state
  const [selectedElement, setSelectedElement] = useState<{
    id: string;
    type: 'cable' | 'device' | 'terminal';
    deviceInstanceId: number;
    specificationId: number;
    deviceName: string;
    specName: string;
    deviceTypeId?: number;
    manufacturer?: string;
    model?: string;
    knxAddress?: string;
    description?: string;
    isReserve?: boolean;
    layoutId?: number; // For terminal schema removal
    terminalBlockId?: number; // For terminal blocks
    terminalBlockDescription?: string; // For terminal blocks modal description
    terminalInstanceId?: number; // For terminal auto nodes update
  } | null>(null);

  // Full specification data for element info panel
  const [selectedElementSpecification, setSelectedElementSpecification] = useState<Specification | null>(null);

  // Load full specification data when element is selected
  useEffect(() => {
    if (selectedElement && selectedElement.specificationId) {
      getSpecificationItem(selectedElement.specificationId)
        .then(spec => {
          setSelectedElementSpecification(spec);
        })
        .catch(error => {
          console.error('Error loading specification for element info:', error);
          setSelectedElementSpecification(null);
        });
    } else {
      setSelectedElementSpecification(null);
    }
  }, [selectedElement]);

  // Load full cable instance data when editing cable
  const { data: cableInstanceData } = useQuery({
    queryKey: ['cableInstance', selectedElement?.deviceInstanceId],
    queryFn: () => getDeviceInstanceById(selectedElement!.deviceInstanceId),
    enabled: showInstanceEditModal && selectedElement?.type === 'cable' && !!selectedElement?.deviceInstanceId,
  });

  // Compute instance to pass to modal - use useMemo to ensure it updates when cableInstanceData changes
  const cableInstanceForModal = useMemo(() => {
    if (cableInstanceData) {
      return cableInstanceData;
    }
    if (selectedElement?.type === 'cable' && selectedElement) {
      return {
        id: selectedElement.deviceInstanceId,
        position_label: selectedElement.deviceName,
        specification_id: selectedElement.specificationId,
        device_type: 'cable',
        is_reserve: selectedElement.isReserve ? 1 : 0,
      } as any;
    }
    return null;
  }, [cableInstanceData, selectedElement]);

  // Compute modal key - use useMemo to ensure it updates when cableInstanceForModal changes
  const cableModalKey = useMemo(() => {
    if (selectedElement?.type === 'cable' && cableInstanceForModal) {
      return `cable-edit-${selectedElement.deviceInstanceId}-loaded-${cableInstanceForModal.id}-${String(cableInstanceForModal.room_from || 'null')}-${String(cableInstanceForModal.room_to || 'null')}-${String(cableInstanceForModal.description || 'null')}-${String(cableInstanceForModal.length_meters || 'null')}`;
    }
    if (selectedElement?.type === 'cable') {
      return `cable-edit-${selectedElement.deviceInstanceId}-loading`;
    }
    return '';
  }, [selectedElement, cableInstanceForModal]);



  // Close and reopen modal when key changes to force component remount
  const [prevModalKey, setPrevModalKey] = useState<string>('');
  useEffect(() => {
    if (showInstanceEditModal && selectedElement?.type === 'cable' && cableModalKey && prevModalKey && prevModalKey !== cableModalKey) {
      setShowInstanceEditModal(false);
      setTimeout(() => {
        setShowInstanceEditModal(true);
      }, 0);
    }
    if (cableModalKey) {
      setPrevModalKey(cableModalKey);
    }
  }, [cableModalKey, showInstanceEditModal, selectedElement, prevModalKey]);

  // Multiple selection states
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [initialNodePositions, setInitialNodePositions] = useState<Map<string, { x: number; y: number }>>(new Map());
  
  // Selected node state for highlighting
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // DEBUG: Edge interaction area visualization mode
  const [debugEdgeInteraction, setDebugEdgeInteraction] = useState(false);

  // State for connection building highlight - TEMPORARILY COMMENTED
  // const [isConnecting, setIsConnecting] = useState(false);

  // DEBUG: Manage debug mode CSS classes
  useEffect(() => {
    // Найти все элементы react-flow
    const reactFlowElements = document.querySelectorAll('.react-flow');
    reactFlowElements.forEach((element) => {
      if (debugEdgeInteraction) {
        element.classList.add('debug-mode');
      } else {
        element.classList.remove('debug-mode');
      }
    });
    
    // Также добавить класс к body для глобального применения
    if (debugEdgeInteraction) {
      document.body.classList.add('debug-mode');
    } else {
      document.body.classList.remove('debug-mode');
    }
  }, [debugEdgeInteraction]);
  
  // Drag & Drop refs
  const dragRef = useRef<any>(null);

  // NEW: Viewport persistence using SchemaManager (isolated storage)
  const { setViewport, getViewport, setCenter, screenToFlowPosition } = useReactFlow();
  const canPersistViewportRef = useRef(false);
  const viewportRestoreAttemptedRef = useRef(false);
  
  // Calculate viewport center for element placement
  const getViewportCenter = useCallback(() => {
    const viewport = getViewport();
    // Get viewport dimensions (approximate visible area)
    const viewportWidth = window.innerWidth * 0.6; // Approximate canvas width (60% of screen)
    const viewportHeight = window.innerHeight * 0.8; // Approximate canvas height (80% of screen)
    
    // Calculate center in world coordinates
    const centerX = (-viewport.x + viewportWidth / 2) / viewport.zoom;
    const centerY = (-viewport.y + viewportHeight / 2) / viewport.zoom;
    
    return { x: centerX, y: centerY };
  }, [getViewport]);

  // Use SchemaManager for isolated viewport storage
  const saveViewport = useCallback((viewport: Viewport) => {
    if (!currentUser?.id) return;
    schemaManager.saveViewport(viewport);
  }, [schemaManager, currentUser?.id]);
  
  // Handle viewport changes - now isolated per schema type
  const onMove = useCallback((_: any, viewport: Viewport) => {
    if (!canPersistViewportRef.current) return;
    saveViewport(viewport);
  }, [saveViewport]);

  // Убираем useEffect для индикатора - он больше не нужен при резком примагничивании
  


  const onPaneClick = useCallback(() => {
    setSelectedEdge(null);
    setActivePopup(null);
    // Clear node selection
    setSelectedNodeId(null);
    setSelectedElement(null);
    setShowElementInfo(false);
    // Clear multiple selection
    if (selectedNodes.size > 0 || selectedEdges.length > 0) {
      setSelectedNodes(new Set());
      setSelectedEdges([]);
      
      // Update visual state
      setNodes((currentNodes) =>
        currentNodes.map((n) => ({
          ...n,
          selected: false
        }))
      )
    }
    
    // Clear selection rectangle
    setTimeout(() => {
      const selectionElements = document.querySelectorAll('.react-flow__selection');
      selectionElements.forEach(element => {
        if (element instanceof HTMLElement) {
          element.style.display = 'none';
        }
      });
    }, 50);
  }, [selectedNodes, selectedEdges, setNodes]);

  // Handle mouse down on pane for right-click selection
  const onMouseDown = useCallback((event: React.MouseEvent) => {
    // Right click - start selection mode
    if (event.button === 2) {
      event.preventDefault()
      // Start selection box logic here if needed
  
    }
  }, [])



  // Removed built-in selection rectangle cleanup (handled by custom overlay)

  // Disable context menu
  const onContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
  }, [])



  // TEMPORARILY DISABLED: Update node classes when highlightedNodeId changes
  // useEffect(() => {
  //   if (nodes.length > 0) {
  //     // Check if any node actually needs className update to prevent unnecessary re-renders
  //     const needsUpdate = nodes.some(node => {
  //       const shouldBeHighlighted = node.id === highlightedNodeId;
  //       const isCurrentlyHighlighted = node.className === 'highlighted';
  //       return shouldBeHighlighted !== isCurrentlyHighlighted;
  //     });
  //     
  //     if (needsUpdate) {
  //       setNodes(prevNodes => 
  //         prevNodes.map(node => ({
  //           ...node,
  //           className: node.id === highlightedNodeId ? 'highlighted' : ''
  //         }))
  //       );
  //     }
  //   }
  // }, [highlightedNodeId, nodes.length]);

  // PDF Export Function


  // Queries
  // Load electrical panel info for browser tab title
  const { data: electricalPanel } = useQuery({
    queryKey: ['electrical-panel', electricalPanelId],
    queryFn: () => electricalPanelId ? getElectricalPanelById(electricalPanelId) : null,
    enabled: !!electricalPanelId,
  })

  // Set browser tab title
  useEffect(() => {
    if (electricalPanel?.proj_id) {
      document.title = `СХ. ${electricalPanel.proj_id} - ESS-2`
    } else {
      document.title = 'Система электрических схем ESS-2'
    }
    
    return () => {
      document.title = 'Система электрических схем ESS-2'
    }
  }, [electricalPanel?.proj_id])

  const { data: electricalPanels } = useQuery({
    queryKey: ['electricalPanels', projectId],
    queryFn: () => getElectricalPanels(projectId),
    enabled: config.electricalPanelRequired || config.availablePanels,
  });

  const schemaEditorHeader = (
    <SchemaEditorHeader
      projectId={projectId}
      schemaId={schemaId}
      electricalPanelId={electricalPanelId}
      schemaName={initialSchema?.name}
      panelName={electricalPanel?.name}
    />
  );
  
  // Load schema data if schemaId is provided (commented out until implemented)
  // const { data: currentSchema, isLoading: schemaLoading } = useQuery({ // This line was removed as per the edit hint
  //   queryKey: ['schema', schemaId], // This line was removed as per the edit hint
  //   queryFn: () => { // This line was removed as per the edit hint
  //     // TODO: Load specific schema data // This line was removed as per the edit hint
  //     return null; // This line was removed as per the edit hint
  //   }, // This line was removed as per the edit hint
  //   enabled: !!schemaId // This line was removed as per the edit hint
  // }); // This line was removed as per the edit hint

  // UNIFIED: Load all terminal schema data in one query to prevent race conditions
  const { data: terminalData, isLoading: terminalDataLoading } = useQuery({
    queryKey: ['terminalSchemaData', blockId, projectId],
    queryFn: async () => {
      if (!blockId) return null;
      
      // Load dependent data in parallel
      const [cableInstances] = await Promise.all([
        getAvailableTerminalCables(projectId!)
      ]);
      
      return {
        terminalBlock: null,
        terminalAssignments: [],
        cableInstances
      };
    },
    enabled: schemaType === 'terminal' && !!blockId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });

  const EMPTY_ARRAY = useMemo(() => [], []);

  // Extract data from unified query
  const terminalBlock = terminalData?.terminalBlock;
  const terminalAssignments: any[] = [];
  const cableInstances = terminalData?.cableInstances;

  // Load connections for terminal schema
  const { data: connections = EMPTY_ARRAY } = useQuery({
    queryKey: ['connections', blockId], 
    queryFn: () => {
      return getConnections(blockId!);
    },
    enabled: schemaType === 'terminal' && !!blockId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  
  // ============= MAIN SCHEMA LOGIC ============= 
  
  // Load schema layout for main and breaker schemas with schema_type isolation
  const { data: schemaLayoutResponse } = useQuery({
    queryKey: ['schemaLayout', schemaType, schemaId],
    queryFn: async () => {
      // NEW: Use isolated API function with schema_type
      const result = await getSchemaLayoutWithIsolation(schemaType as SchemaType, Number(schemaId));
      return result;
    },
    enabled: shouldLoadLayoutData,
  });

  // Load all project connections (across all schemas)
  const { data: mainConnections = EMPTY_ARRAY } = useQuery({
    queryKey: ['projectConnections', projectId],
    queryFn: () => getAllProjectConnections(Number(projectId)),
    enabled: shouldLoadLayoutData,
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes (was cacheTime)
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
  
  // NEW: Load available devices for main and breaker schema library (filtered by electrical panel)
  // Проверяем, загружены ли разрешения перед выполнением запросов
  // Для system_admin разрешения не загружаются, поэтому считаем их загруженными
  const permissionsLoaded = useMemo(() => {
    if (currentUser?.role === 'system_admin') return true;
    return !!(projectAccessData && permissionsData);
  }, [currentUser, projectAccessData, permissionsData]);

  const { data: availableDevices = EMPTY_ARRAY } = useQuery({
    queryKey: ['availableDevices', projectId, electricalPanelId, schemaType, schemaId],
    queryFn: () => {
      if (schemaType !== 'main') {
        // For all non-main schemas, use schema_layout check + schema_type filtering
        return getAvailableDevicesForSchemaType(projectId, schemaType, electricalPanelId, Number(schemaId));
      } else {
        // For main schemas, use schema_layout check + schema_type filtering (same as others)
        return getAvailableDevicesForSchemaType(projectId, schemaType, electricalPanelId, Number(schemaId));
      }
    },
    enabled: shouldLoadLibraryData && !isReadOnly && permissionsLoaded, // Не загружаем библиотеку в режиме только просмотр и до загрузки разрешений
    retry: false, // Не повторяем запросы при ошибках
    retryOnMount: false, // Не повторяем при монтировании
  });
  
  // NEW: Load available cables for main and breaker schema library (filtered by electrical panel)
  const { data: availableCablesMain = EMPTY_ARRAY } = useQuery({
    queryKey: ['availableCablesMain', projectId, electricalPanelId, schemaType, schemaId],
    queryFn: () => {
      if (schemaType !== 'main') {
        // For all non-main schemas, use schema_layout check + schema_type filtering
        return getAvailableCablesForSchemaType(projectId, schemaType, electricalPanelId, Number(schemaId));
      } else {
        // For main schemas, use schema_layout check + schema_type filtering (same as others)
        return getAvailableCablesForSchemaType(projectId, schemaType, electricalPanelId, Number(schemaId));
      }
    },
    enabled: shouldLoadLibraryData && !isReadOnly && permissionsLoaded, // Не загружаем библиотеку в режиме только просмотр и до загрузки разрешений
    retry: false, // Не повторяем запросы при ошибках
    retryOnMount: false, // Не повторяем при монтировании
  });
  


  // NEW: Load cable to terminal connections for auto-placement
  const { data: cableTerminalConnections = EMPTY_ARRAY } = useQuery({
    queryKey: ['cableTerminalConnections', schemaId],
    queryFn: () => getCableToTerminalConnections(Number(schemaId)),
    enabled: !!schemaId, // Simplified condition - load for all schemas when schemaId exists
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  // Load device types for layers panel
  const { data: deviceTypes = EMPTY_ARRAY } = useQuery({
    queryKey: ['deviceTypes'],
    queryFn: () => getDeviceTypes(),
    enabled: showLayersPanel && shouldLoadLibraryData,
  });



  // Analyze cable-to-terminals connections for group dragging




  // Show loading for terminal schemas while data is being loaded
  if (schemaType === 'terminal' && terminalDataLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Загрузка терминальной схемы...</h3>
          <p className="text-gray-500">Подготовка данных блока клеммников</p>
        </div>
      </div>
    );
  }

  // Main schema connections loaded

  // Extract schema layout data
  const schemaLayout = schemaLayoutResponse?.data || [];
  
  // Calculate hidden nodes count
  const hiddenNodesCount = useMemo(() => {
    if (!schemaLayout) return 0;
    const totalNodes = schemaLayout.length;
    // Count only nodes originating from schemaLayout (exclude auto/aux nodes)
    const visibleLayoutNodes = nodes.filter((n: any) => n?.data && n.data.layoutId != null).length;
    const hidden = totalNodes - visibleLayoutNodes;
    return hidden > 0 ? hidden : 0;
  }, [schemaLayout, nodes]);

  // Detect if any layer filter is OFF (including reserve)
  const anyLayerFilterOff = useMemo(() => {
    const lf = layerFilters || {};
    // If reserve is hidden, consider filters active
    if (lf.showReserve === false) return true;
    // Any device type filter turned off
    for (const [key, value] of Object.entries(lf)) {
      if (key.startsWith('show') && key !== 'showReserve' && value === false) return true;
    }
    return false;
  }, [layerFilters]);

  // Calculate hidden nodes by type
  const hiddenNodesByType = useMemo(() => {
    if (!schemaLayout) return {};
    
    const hiddenByType: Record<string, number> = {};
    
    schemaLayout.forEach((item: any) => {
      const deviceType = item.device_type_code || item.device_type || 'device';
      const isReserve = Boolean(item.is_reserve);
      
      // Check if this node would be hidden by filters
      const deviceTypeKey = `show${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}s`;
      const isHiddenByType = !layerFilters[deviceTypeKey];
      const isHiddenByReserve = isReserve && !layerFilters.showReserve;
      
      if (isHiddenByType || isHiddenByReserve) {
        const typeKey = isHiddenByReserve ? 'reserve' : deviceType;
        hiddenByType[typeKey] = (hiddenByType[typeKey] || 0) + 1;
      }
    });
    
    return hiddenByType;
  }, [schemaLayout, layerFilters]);

  
  // Focus on node with animation (optional zoom; optional width/height to avoid relying on store at call time)
  const focusOnNode = useCallback((nodeId: string, options?: { zoom?: number; width?: number; height?: number }) => {
    const zoom = options?.zoom ?? 1;
    const useExplicitSize = options?.width != null && options?.height != null && options.width > 0 && options.height > 0;
    
    // First try to find the node in the current nodes (for elements already on canvas)
    let targetNode = nodes.find(node => node.id === nodeId);
    
    // If not found in nodes, try to find in schemaLayout and create a temporary position
    if (!targetNode && schemaLayout) {
      const schemaItem = schemaLayout.find(item => 
        item.device_instance_id?.toString() === nodeId || `item-${item.id}` === nodeId
      );
      
      if (schemaItem) {
        // Create a temporary node for focusing
        targetNode = {
          id: nodeId,
          position: { 
            x: Number(schemaItem.position_x) || 100, 
            y: Number(schemaItem.position_y) || 100 
          },
          data: {
            deviceType: schemaItem.device_type_code || 'device',
            contacts: []
          },
          type: 'device'
        } as any;
      }
    }
    
    if (!targetNode) return;
    
    // Calculate element dimensions based on type
    let elementWidth = 70;
    let elementHeight = 95;
    
    if (targetNode.data?.deviceType === 'cable' || targetNode.type === 'cable') {
      const contacts = (targetNode.data?.contacts as any[]) || [];
      const wireGroups = contacts.reduce((groups: Record<string, any[]>, contact: any) => {
        const wireNumber = contact.contact_number?.toString().split('-')[0];
        if (!groups[wireNumber]) groups[wireNumber] = [];
        groups[wireNumber].push(contact);
        return groups;
      }, {});
      
      const wireCount = Object.keys(wireGroups).length;
      elementWidth = wireCount <= 4 ? 70 : Math.max(70, wireCount * 15 + 40);
      elementHeight = 120;
    } else if (targetNode.type === 'terminalBlock') {
      elementWidth = 180;
      elementHeight = 200;
    } else {
      const contacts = (targetNode.data?.contacts as any[]) || [];
      elementWidth = contacts.length <= 4 ? 70 : Math.max(70, contacts.length * 18 + 20);
      elementHeight = 95;
    }
    
    const posX = Number(targetNode.position.x) || 0;
    const posY = Number(targetNode.position.y) || 0;
    const centerX = posX + elementWidth / 2;
    const centerY = posY + elementHeight / 2;

    if (useExplicitSize) {
      const w = options!.width!;
      const h = options!.height!;
      const newViewport = { x: w / 2 - centerX * zoom, y: h / 2 - centerY * zoom, zoom };
      setViewport(newViewport, { duration: 800 });
      saveViewport(newViewport);
    } else {
      setCenter(centerX, centerY, { zoom, duration: 800 }).then(() => {
        saveViewport(getViewport());
      });
    }
    
    // Highlight the node for 4 seconds
    setHighlightedNodeId(nodeId);
    setTimeout(() => {
      setHighlightedNodeId(null);
    }, 4000);
    
    // Close search panel
    setShowSearchPanel(false);
    setSearchQuery('');
  }, [nodes, schemaLayout, setCenter, getViewport, saveViewport, setViewport]);

  // Focus on device instance when opened with ?focus=<deviceInstanceId>. Wait for container dimensions
  // and panZoom so setCenter uses correct width/height and actually applies.
  const initialFocusDoneRef = useRef(false);
  const flowWidth = useStore((s) => s.width);
  const flowHeight = useStore((s) => s.height);
  const panZoomReady = useStore((s) => !!s.panZoom);

  useEffect(() => {
    canPersistViewportRef.current = false;
    viewportRestoreAttemptedRef.current = false;
  }, [schemaManager]);

  useEffect(() => {
    if (initialFocusNodeId || viewportRestoreAttemptedRef.current) return;
    if (!currentUser?.id || flowWidth < 100 || flowHeight < 100 || !panZoomReady) return;

    viewportRestoreAttemptedRef.current = true;
    const viewport = schemaManager.restoreViewport();
    if (viewport) {
      setViewport(viewport, { duration: 0 });
    }

    window.setTimeout(() => {
      canPersistViewportRef.current = true;
    }, 0);
  }, [initialFocusNodeId, currentUser?.id, flowWidth, flowHeight, panZoomReady, schemaManager, setViewport]);

  useEffect(() => {
    if (!initialFocusNodeId || initialFocusDoneRef.current) return;
    if (!schemaLayout?.length && nodes.length === 0) return;
    if (flowWidth < 100 || flowHeight < 100 || !panZoomReady) return;
    
    initialFocusDoneRef.current = true; // Mark as done so we don't enter this block again
    
    // Execute after a short delay to ensure React Flow has rendered nodes at their positions
    setTimeout(() => {
      focusOnNode(initialFocusNodeId, { zoom: 1.5, width: flowWidth, height: flowHeight });
      canPersistViewportRef.current = true;
    }, 600);
    
    // No cleanup for the timer because we WANT it to run exactly once and not be cancelled by re-renders
  }, [initialFocusNodeId, schemaLayout, nodes.length, focusOnNode, flowWidth, flowHeight, panZoomReady]);
  
  // Update search results when query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    const searchTerm = searchQuery.toLowerCase();
    const results: Array<{
      id: string;
      name: string;
      description?: string;
      type: 'cable' | 'device' | 'terminal_block';
    }> = [];
    
    // Search through schemaLayout (all elements in schema) instead of just nodes
    if (schemaLayout && schemaLayout.length > 0) {
      schemaLayout.forEach(item => {
        const deviceName = item.position_label || '';
        const description = item.description || '';
        const specName = item.spec_name || '';
        
        // Search in position label, description, and specification name
        if (
          deviceName.toLowerCase().includes(searchTerm) ||
          description.toLowerCase().includes(searchTerm) ||
          specName.toLowerCase().includes(searchTerm)
        ) {
          results.push({
            id: item.device_instance_id?.toString() || `item-${item.id}`,
            name: deviceName || `Element #${item.device_instance_id}`,
            description: description || specName,
            type: (item.device_type_code === 'cable' || item.device_type === 'cable' || item.device_type === 'Кабель') ? 'cable' : 'device'
          });
        }
      });
    }
    
    setSearchResults(results);
  }, [searchQuery]); // Remove schemaLayout from dependencies to prevent infinite loop
  
  // Show all elements when search panel opens
  useEffect(() => {
    if (showSearchPanel && !searchQuery.trim()) {
      // When search panel opens without query, show all elements from schemaLayout
      if (schemaLayout && schemaLayout.length > 0) {
        const allResults: Array<{
          id: string;
          name: string;
          description?: string;
          type: 'cable' | 'device' | 'terminal_block';
        }> = [];
        
        schemaLayout.forEach(item => {
          const deviceName = item.position_label || '';
          const description = item.description || '';
          const specName = item.spec_name || '';
          
          allResults.push({
            id: item.device_instance_id?.toString() || `item-${item.id}`,
            name: deviceName || `Element #${item.device_instance_id}`,
            description: description || specName,
            type: (item.device_type_code === 'cable' || item.device_type === 'cable' || item.device_type === 'Кабель') ? 'cable' : 'device'
          });
        });
        
        setSearchResults(allResults);
      }
    }
  }, [showSearchPanel, searchQuery]); // Remove schemaLayout from dependencies to prevent infinite loop
  
  // ============= END MAIN SCHEMA LOGIC =============

  // Track data loading state
  useEffect(() => {
    // Cable instances and terminal block data loaded
  }, [cableInstances, schemaType, blockId]);

  // Create separate terminal instance nodes if this is a terminal schema
  useEffect(() => {
    if (schemaType === 'terminal' && terminalBlock && terminalAssignments) {
      // Create separate nodes for each terminal instance with device_instance_id as node ID
      const terminalInstanceNodes: Node[] = terminalAssignments.map((assignment, index) => ({
        id: `${assignment.device_instance_id}`, // Use device_instance_id as node ID
        type: 'terminalInstance', // Use separate node type for individual instances
        position: { 
          x: 200 + (index * 120), // Spread horizontally
          y: 200 
        },
        data: {
          terminalBlock,
          assignment: assignment, // Single assignment per node
          deviceInstanceId: assignment.device_instance_id,
          position: assignment.position_in_block
        },
        draggable: false // Make terminals non-draggable
      }));
      
      setNodes((prevNodes: Node[]) => {
        // Remove existing terminal nodes and add new ones
        const filteredNodes = prevNodes.filter((node: Node) => 
          !node.id.startsWith('terminal-block-') && 
          !terminalAssignments.some(ta => ta.device_instance_id.toString() === node.id)
        );
        
        // Only update if terminal nodes have changed
        const currentTerminalNodes = prevNodes.filter((node: Node) => !filteredNodes.includes(node));
        if (currentTerminalNodes.length === terminalInstanceNodes.length && 
            currentTerminalNodes.every((n, i) => n.id === terminalInstanceNodes[i].id)) {
          return prevNodes;
        }
        
        return [...filteredNodes, ...terminalInstanceNodes];
      });
    }
  }, [terminalBlock, terminalAssignments, schemaType]);
  
  // Defer edgesRef sync to a later effect (declared after connectionEdges)

  // Load connections as edges for both terminal and main schemas
  // NOTE: selectedEdge intentionally NOT in deps — selection applied in edgesForFlow to avoid full recompute on every click
  const connectionEdges = useMemo((): Edge[] => {
    const perfStart = PERF_DEBUG ? performance.now() : 0;
    // Prevent recalculation if data is not ready
    if (!schemaType) return [];
    
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    const terminalByInstanceAndOpposite = new Map<string, Node>();
    nodes.forEach(n => {
      if (n.id.startsWith('terminal-')) {
        const parts = n.id.split('-');
        const data = n.data as any;
        const opp = data?.terminal_opposite_contact_id;
        if (parts.length >= 3 && opp != null) terminalByInstanceAndOpposite.set(`${parts[1]}-${opp}`, n);
      }
    });

    // Get current node IDs for filtering
    const currentNodeIds = new Set(nodes.map(n => n.id));
    
    // Add terminal node IDs that will be created
    if (shouldShowTerminalFeatures && cableTerminalConnections) {
      // DEBUG MODE: Filter connections

      
      const connectionsToProcess = cableTerminalConnections;
      
      connectionsToProcess.forEach((connection: any) => {
        const terminalNodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`;
        currentNodeIds.add(terminalNodeId);
      });
    }
    
    
    
    // Terminal schema edges
    const terminalCondition = schemaType === 'terminal' && connections && connections.length > 0 && blockId;
    
    if (terminalCondition) {
      const currentTerminalSchemaId = Number(blockId);
      const byVisibility = (conn: any) => {
        const legacy = conn.visibility_legacy === undefined ? true : !!conn.visibility_legacy;
        if (legacy) return true;
        return conn.schema_id === currentTerminalSchemaId ||
          (Array.isArray(conn.visible_on_schema_ids) && conn.visible_on_schema_ids.includes(currentTerminalSchemaId));
      };
      const validConnections = connections
        .filter((conn: any) => conn.from_contact_id && conn.to_contact_id)
        .filter(byVisibility);
      
              const edges = validConnections.map((conn: any): Edge => {
        
        // Use fallbacks for invalid connections
        const toInstanceId = conn.to_instance_id || 1;
        

        
        // Parse wire_properties from database
        let wireProps: any = {};
        if (conn.wire_properties) {
          try {
            wireProps = typeof conn.wire_properties === 'string' 
              ? JSON.parse(conn.wire_properties) 
              : conn.wire_properties;
          } catch (e) {
            // console.warn('Failed to parse wire_properties for connection', conn.id, e);
          }
        }
        
        let source, sourceHandle, target, targetHandle;
        
        // FINAL: Correct format for separate terminal instance nodes
        // Terminal nodes have format: terminal-instanceId-contactId
        source = `terminal-${conn.from_instance_id}-${conn.from_contact_id}`; // Terminal node ID = terminal-instanceId-contactId
        sourceHandle = `${conn.from_contact_id}-source`; // Terminal handle = contact.id-source
        target = `${toInstanceId}`; // Cable node ID = device_instance_id (same format as addCableToCanvas)
        targetHandle = `${conn.to_contact_id}-target`; // Cable: contact.id-target
        
        const edgeId = `connection-${conn.id}`;
        
        // Debug log removed - connection working correctly
        
              // Parse path points from database or use manual routes
              let pathPoints: RoutePoint[] = [];
              
              // Check if we have manual waypoints for this edge
        if (manualRoutesRef.current[edgeId]) {
          // Live-edited manual route takes precedence and is a true manual route
          pathPoints = manualRoutesRef.current[edgeId].map(wp => ({ ...wp, type: 'waypoint' }));
          // console.log('🔵 Using manual waypoints for edge:', edgeId, pathPoints);
        } else if (conn.path_points) {
          try {
            const parsed = typeof conn.path_points === 'string' 
              ? JSON.parse(conn.path_points) 
              : conn.path_points;
            
            if (Array.isArray(parsed)) {
              // Normalize points and add type field for CustomEdge
              pathPoints = parsed.map((point: any) => ({ x: point.x, y: point.y, type: 'waypoint' }));
            }
          } catch (error) {
            // console.warn('Failed to parse path_points for connection', conn.id, error);
          }
        }

          const edge = {
          id: edgeId,
          source,
          sourceHandle,
          target,
          targetHandle,
          type: 'editable-edge',
          animated: false,
          selected: false,
          selectable: true,
          style: { 
            stroke: wireProps.color || wireProps.style_properties?.stroke || '#007bff', 
            strokeWidth: wireProps.stroke_width || wireProps.style_properties?.strokeWidth || 2,
            strokeDasharray: wireProps.stroke_dasharray || wireProps.style_properties?.strokeDasharray,
            ...wireProps.style_properties
          },
          data: {
            connectionId: conn.id,
            points: mapPathPointsToControlPoints(pathPoints),
            algorithm: getAlgorithmFromWireProps(wireProps.type),
            edgeLabels: wireProps.edge_labels || wireProps.edgeLabels || [],
            wireProperties: wireProps,
            deferLabelDragCommit: true,
            isReadOnly
          }
        };
        
        // Debug log disabled to avoid console spam
        // console.log('🔵 CREATED EDGE:', { id: edge.id, type: edge.type, source: edge.source, target: edge.target });
        
        return edge;
              });
        
        // Filter out edges where source or target node doesn't exist
        const filteredEdges = edges.filter((edge: Edge) => {
          const sourceExists = currentNodeIds.has(edge.source);
          const targetExists = currentNodeIds.has(edge.target);
          
          if (!sourceExists || !targetExists) {
            return false;
          }
          
          return sourceExists && targetExists;
        });

        return filteredEdges;
    }
    
        // Main and breaker schema edges - INLINE to avoid circular dependencies
    if (shouldShowMainSchemaFeatures) {
      const allEdges: any[] = [];
      
      
      
      // Regular main schema connections
      // console.log('🔵 mainConnections:', mainConnections?.length || 0);
      if (mainConnections && mainConnections.length > 0) {
        const currentSchemaId = Number(schemaId);
        const filteredConnections = mainConnections
          .filter((conn: any) => {
            if (!conn.from_instance_id || !conn.to_instance_id || !conn.from_contact_id || !conn.to_contact_id) {
              return false;
            }
            // Visibility: legacy = show everywhere; otherwise only on schema_id or visible_on_schema_ids
            const legacy = conn.visibility_legacy === undefined ? true : !!conn.visibility_legacy;
            if (!legacy) {
              const onHome = conn.schema_id === currentSchemaId;
              const onExtra = Array.isArray(conn.visible_on_schema_ids) && conn.visible_on_schema_ids.includes(currentSchemaId);
              if (!onHome && !onExtra) return false;
            }
            if (GLOBAL_DEBUG_MODE) return true;
            return true;
          });
        
        // console.log('🔵 filteredConnections:', filteredConnections.length);
        
        const regularEdges = filteredConnections.map((conn: any): Edge => {
          // Check if source is a terminal node
          let sourceNodeId = conn.from_instance_id?.toString() || 'unknown-source';
          let sourceHandle = `${conn.from_contact_id || 'unknown'}-source`;
          
          // Check if source is a terminal (lookup by instance + opposite contact)
          const sourceTerminalNode = terminalByInstanceAndOpposite.get(`${conn.from_instance_id}-${conn.from_contact_id}`) ?? null;
          
          // Also check cableTerminalConnections if node not found yet
          let sourceTerminalContactId: number | null = null;
          if (!sourceTerminalNode && cableTerminalConnections) {
            const terminalConn = cableTerminalConnections.find((c: any) => 
              c.terminal_instance_id === conn.from_instance_id &&
              c.terminal_opposite_contact_id === conn.from_contact_id
            );
            if (terminalConn) {
              sourceTerminalContactId = terminalConn.terminal_contact_id;
            }
          }
          
          if (sourceTerminalNode || sourceTerminalContactId) {
            // Use terminal node ID format: terminal-{instance_id}-{terminal_contact_id}
            if (sourceTerminalNode) {
              sourceNodeId = sourceTerminalNode.id;
            } else if (sourceTerminalContactId) {
              sourceNodeId = `terminal-${conn.from_instance_id}-${sourceTerminalContactId}`;
            }
            // For terminal-to-terminal connections, use top handles
            sourceHandle = `${conn.from_contact_id || 'unknown'}-top-source`;
          } else {
            // Regular device connection
            sourceHandle = `${conn.from_contact_id || 'unknown'}-source`;
          }
          
          // Check if target is a terminal node
          let targetNodeId = conn.to_instance_id?.toString() || 'unknown-target';
          let targetHandle = `${conn.to_contact_id || 'unknown'}-target`;
          
          const targetTerminalNode = terminalByInstanceAndOpposite.get(`${conn.to_instance_id}-${conn.to_contact_id}`) ?? null;
          
          // Also check cableTerminalConnections if node not found yet
          let targetTerminalContactId: number | null = null;
          if (!targetTerminalNode && cableTerminalConnections) {
            const terminalConn = cableTerminalConnections.find((c: any) => 
              c.terminal_instance_id === conn.to_instance_id &&
              c.terminal_opposite_contact_id === conn.to_contact_id
            );
            if (terminalConn) {
              targetTerminalContactId = terminalConn.terminal_contact_id;
            }
          }
          
          if (targetTerminalNode || targetTerminalContactId) {
            // Use terminal node ID format: terminal-{instance_id}-{terminal_contact_id}
            if (targetTerminalNode) {
              targetNodeId = targetTerminalNode.id;
            } else if (targetTerminalContactId) {
              targetNodeId = `terminal-${conn.to_instance_id}-${targetTerminalContactId}`;
            }
            // For terminal-to-terminal connections, use top handles
            targetHandle = `${conn.to_contact_id || 'unknown'}-top-target`;
          } else {
            // Regular device connection
            targetHandle = `${conn.to_contact_id || 'unknown'}-target`;
          }
          
          // Parse wire_properties from database
          let wireProps: any = {};
          if (conn.wire_properties) {
            try {
              wireProps = typeof conn.wire_properties === 'string' 
                ? JSON.parse(conn.wire_properties) 
                : conn.wire_properties;
            } catch (e) {
              // console.warn('Failed to parse wire_properties for main connection', conn.id, e);
            }
          }
          
          const edgeId = `connection-${conn.id}`;
          
              // Parse path points from database or use manual routes
              let pathPoints: RoutePoint[] = [];
              
              // Check if we have manual waypoints for this edge
          if (manualRoutesRef.current[edgeId]) {
            // For step/smoothstep, preserve the waypoints as waypoints but keep original edge type
            pathPoints = manualRoutesRef.current[edgeId].map(wp => ({ ...wp, type: 'waypoint' }));
            // console.log('🔵 Using manual waypoints for main edge:', edgeId, pathPoints);
          } else if (conn.path_points) {
            try {
              const parsed = typeof conn.path_points === 'string' 
                ? JSON.parse(conn.path_points) 
                : conn.path_points;
              
              if (Array.isArray(parsed)) {
                // Normalize for CustomEdge
                pathPoints = parsed.map((p: any) => ({ x: p.x, y: p.y, type: 'waypoint' }));
              }
            } catch (error) {
              // console.warn('Failed to parse path_points for main connection', conn.id, error);
            }
          }
          
          const isSelected = false;
          
          // console.log('🔵 EDGE TYPE DEBUG:', { 
          //   edgeId, 
          //   selectedEdgeId: selectedEdge?.id, 
          //   isSelected, 
          //   edgeType,
          //   wirePropsType: wireProps?.type 
          // });
          
          // Check if connection is between terminals
          const isTerminalToTerminal = (sourceNodeId.startsWith('terminal-') && targetNodeId.startsWith('terminal-'));
          
          // For terminal-to-terminal connections, use 'smoothstep' as default if no type is set (Г-образный со скруглёнными углами)
          let defaultType = getEdgeTypeForConnection(conn.from_instance_id, conn.to_instance_id, nodes);
          if (isTerminalToTerminal && !wireProps?.type) {
            defaultType = 'smoothstep';
          }
          
          // Determine edge type based on device types, but respect saved type in DB
          const preservedType = wireProps?.type as string | undefined;
          const edgeType = preservedType || defaultType;
          
          // Update wire properties while preserving DB type if present
          const updatedWireProps = {
            ...wireProps,
            type: edgeType
          };
          
          // Apply local overrides if present
          const effectiveWireProps = {
            ...updatedWireProps
          };
          const effectiveStyle = {
            stroke: effectiveWireProps.color || effectiveWireProps.style_properties?.stroke || '#007bff',
            strokeWidth: effectiveWireProps.stroke_width || effectiveWireProps.style_properties?.strokeWidth || 2,
            strokeDasharray: effectiveWireProps.stroke_dasharray || effectiveWireProps.style_properties?.strokeDasharray,
            ...effectiveWireProps.style_properties
          };
          const edge = {
            id: edgeId,
            source: sourceNodeId,
            sourceHandle,
            target: targetNodeId,
            targetHandle,
            type: 'editable-edge',
            animated: false,
            selected: isSelected,
            selectable: true,
            style: effectiveStyle,

            data: {
              connectionId: conn.id,
              points: mapPathPointsToControlPoints(pathPoints),
              algorithm: getAlgorithmFromWireProps(effectiveWireProps.type),
              edgeLabels: effectiveWireProps.edge_labels || effectiveWireProps.edgeLabels || [],
              wireProperties: effectiveWireProps,
              deferLabelDragCommit: true,
              isReadOnly
            }
          };
          
          // console.log('🔵 CREATED EDGE:', { id: edge.id, type: edge.type, source: edge.source, target: edge.target });
          
          return edge;
        });
        
        allEdges.push(...regularEdges);
        
        // Create edges for device-to-terminal connections (actuator to autoterminal)
        // This creates the BLUE lines from actuator to upper contact of autoterminal
        if (cableTerminalConnections && cableTerminalConnections.length > 0) {
          const deviceToTerminalEdges = cableTerminalConnections
            .filter((connection: any) => {
              const nodeId = connection.cable_instance_id.toString();
              const node = nodeMap.get(nodeId);
              return node && node.data && (node.data as any).deviceType !== 'cable' && (node.data as any).deviceType !== 'Кабель';
            })
            .map((connection: any): Edge => {
              // For devices, create edge FROM device TO terminal
              const deviceNodeId = connection.cable_instance_id.toString();
              const terminalNodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`;
              
              // Device's source handle connects to terminal's target handle
              const sourceHandle = `${connection.cable_contact_id}-source`;
              // For device-to-terminal, use opposite contact ID for top handle
              const targetHandle = `${connection.terminal_opposite_contact_id || connection.terminal_contact_id}-top-target`;
              
              const edgeId = `device-terminal-${connection.connection_id}`;
              
              // Debug log removed - connection working correctly
              
              // Parse wire_properties from database for device-to-terminal connections
              let wireProps: any = {};
              if (connection.wire_properties) {
                try {
                  wireProps = typeof connection.wire_properties === 'string' 
                    ? JSON.parse(connection.wire_properties) 
                    : connection.wire_properties;
                } catch (e) {
                  console.warn('Failed to parse wire_properties for device-terminal connection', connection.connection_id, e);
                }
              }
              
              // Parse path points from database
              let pathPoints: RoutePoint[] = [];
              
              // Check if we have manual waypoints for this edge
              if (manualRoutesRef.current[edgeId]) {
                pathPoints = manualRoutesRef.current[edgeId].map(wp => ({ ...wp, type: 'waypoint' }));
              } else if (connection.path_points) {
                try {
                  const parsed = typeof connection.path_points === 'string' 
                    ? JSON.parse(connection.path_points) 
                    : connection.path_points;
                  
                  if (Array.isArray(parsed)) {
                    pathPoints = parsed.map((p: any) => ({ x: p.x, y: p.y, type: 'waypoint' }));
                  }
                } catch (error) {
                  console.warn('Failed to parse path_points for device-terminal connection', connection.connection_id, error);
                }
              }
              
              // Use edge type from wire_properties if available, otherwise default to bezier
              const edgeType = wireProps.type || 'bezier';
              
              // Update wire properties with the determined type
              const updatedWireProps = {
                ...wireProps,
                type: edgeType
              };
              
              const effectiveWireProps = {
                ...updatedWireProps
              };
              const effectiveStyle = {
                stroke: effectiveWireProps.color || effectiveWireProps.style_properties?.stroke || '#007bff',
                strokeWidth: effectiveWireProps.stroke_width || effectiveWireProps.style_properties?.strokeWidth || 2,
                strokeDasharray: effectiveWireProps.stroke_dasharray || effectiveWireProps.style_properties?.strokeDasharray,
                ...effectiveWireProps.style_properties
              };
              const edge = {
                id: edgeId,
                source: deviceNodeId,
                sourceHandle,
                target: terminalNodeId,
                targetHandle,
                type: 'editable-edge',
                animated: false,
                selected: false,
                selectable: true,
                style: effectiveStyle,
                data: {
                  connectionId: connection.connection_id,
                  points: mapPathPointsToControlPoints(pathPoints),
                  algorithm: getAlgorithmFromWireProps(effectiveWireProps.type || 'bezier'),
                  edgeLabels: effectiveWireProps.edge_labels || effectiveWireProps.edgeLabels || [],
                  wireProperties: effectiveWireProps,
                  deferLabelDragCommit: true,
                  isReadOnly
                }
              };
              
              return edge;
            });
          
          allEdges.push(...deviceToTerminalEdges);
        }
        // Create edges for terminal-to-cable connections (terminal bottom to cable)
        // This creates the GREEN lines from terminal bottom contact to cable
        if (cableTerminalConnections && cableTerminalConnections.length > 0) {
          const terminalToCableEdges = cableTerminalConnections
            .filter((connection: any) => {
              const nodeId = connection.cable_instance_id.toString();
              const node = nodeMap.get(nodeId);
              return node && node.data && ((node.data as any).deviceType === 'cable' || (node.data as any).deviceType === 'Кабель');
            })
            .map((connection: any): Edge => {
              // Create terminal node ID
              const terminalNodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`;
              const cableNodeId = connection.cable_instance_id.toString();
              
              // Correct direction: Terminal (source) → Cable (target)
              // Terminal's bottom source handle connects to cable's top target handle
              const sourceHandle = `${connection.terminal_contact_id}-bottom-source`;
              const targetHandle = `${connection.cable_contact_id}-target`;
              
              const edgeId = `auto-terminal-${connection.connection_id}`;
              
              // Debug log removed - connection working correctly
              
              // Parse wire_properties from database for auto-terminal connections
              let wireProps: any = {};
              if (connection.wire_properties) {
                try {
                  wireProps = typeof connection.wire_properties === 'string' 
                    ? JSON.parse(connection.wire_properties) 
                    : connection.wire_properties;
                } catch (e) {
                  console.warn('Failed to parse wire_properties for auto-terminal connection', connection.connection_id, e);
                }
              }
              
              // Parse path points from database for auto-terminal connections
              let pathPoints: RoutePoint[] = [];
              
              // Check if we have manual waypoints for this edge
              if (manualRoutesRef.current[edgeId]) {
                pathPoints = manualRoutesRef.current[edgeId].map(wp => ({ ...wp, type: 'waypoint' }));
              } else if (connection.path_points) {
                try {
                  const parsed = typeof connection.path_points === 'string' 
                    ? JSON.parse(connection.path_points) 
                    : connection.path_points;
                  
                  if (Array.isArray(parsed)) {
                    pathPoints = parsed.map((p: any) => ({ x: p.x, y: p.y, type: 'waypoint' }));
                  }
                } catch (error) {
                  console.warn('Failed to parse path_points for auto-terminal connection', connection.connection_id, error);
                }
              }
              
              // Use edge type from wire_properties if available, otherwise default to bezier
              const edgeType = wireProps.type || 'bezier';
              
              // Update wire properties with the determined type
              const updatedWireProps = {
                ...wireProps,
                type: edgeType
              };
              
              const effectiveWireProps = {
                ...updatedWireProps
              };
              const effectiveStyle = {
                stroke: effectiveWireProps.color || effectiveWireProps.style_properties?.stroke || '#28a745',
                strokeWidth: effectiveWireProps.stroke_width || effectiveWireProps.style_properties?.strokeWidth || 2,
                strokeDasharray: effectiveWireProps.stroke_dasharray || effectiveWireProps.style_properties?.strokeDasharray,
                ...effectiveWireProps.style_properties
              };
              const edge = {
                id: edgeId,
                source: terminalNodeId,
                sourceHandle,
                target: cableNodeId,
                targetHandle,
                type: 'editable-edge',
                animated: false,
                selected: false,
                selectable: true,
                style: effectiveStyle,
                data: {
                  connectionId: connection.connection_id,
                  points: mapPathPointsToControlPoints(pathPoints),
                  algorithm: getAlgorithmFromWireProps(effectiveWireProps.type || 'bezier'),
                  edgeLabels: effectiveWireProps.edge_labels || effectiveWireProps.edgeLabels || [],
                  wireProperties: effectiveWireProps,
                  deferLabelDragCommit: true,
                  isReadOnly
                }
              };
              
              return edge;
            });
          
          allEdges.push(...terminalToCableEdges);
        }
        
        return allEdges;
      }
      
      const filteredEdges = allEdges.filter(edge =>
        nodeMap.get(edge.source) != null && nodeMap.get(edge.target) != null
      );
      
      // Apply automatic routing for non-manual routes
      const routes = resolveOverlappingRoutes(filteredEdges, nodes);
      
      const mapped = filteredEdges.map(edge => {
        const connectionId = parseInt(edge.id.replace(/^(connection-|auto-terminal-|device-terminal-)/, ''));
        const route = routes.get(connectionId);
        const edgeData = edge.data as any;
        
        if (route && !edgeData?.isManualRoute) {
          // Apply automatic routing by setting points
          return {
            ...edge,
            data: {
              ...edge.data,
              points: mapPathPointsToControlPoints(route.pathPoints)
            }
          };
        }
        
        return edge;
      });
      if (PERF_DEBUG && perfStart) {
        const d = performance.now() - perfStart;
        if (d > 16) console.log(`[PERF] connectionEdges useMemo: ${d.toFixed(1)}ms (nodes: ${nodes.length}, edges: ${mapped.length})`);
      }
      return mapped;
    }
    
    return [];
  }, [connections, mainConnections, cableTerminalConnections, schemaType, schemaId, blockId, nodes, layerFilters, isReadOnly]);

  // Sync connectionEdges to edges
  useEffect(() => {
    setEdges(prevEdges => {
      // Check if edges actually changed to avoid infinite loops
      if (prevEdges.length === connectionEdges.length && 
          prevEdges.every((e, i) => {
            const newE = connectionEdges[i];
            if (e.id !== newE.id) return false;
            if (e.source !== newE.source || e.target !== newE.target) return false;
            if (e.sourceHandle !== newE.sourceHandle || e.targetHandle !== newE.targetHandle) return false;
            if (e.style?.stroke !== newE.style?.stroke || e.style?.strokeWidth !== newE.style?.strokeWidth || e.style?.strokeDasharray !== newE.style?.strokeDasharray) return false;
            if (JSON.stringify(e.data?.points) !== JSON.stringify(newE.data?.points)) return false;
            if (e.data?.algorithm !== newE.data?.algorithm) return false;
            if (e.selected !== newE.selected) return false; // Added selected check
            if (JSON.stringify(e.data?.edgeLabels) !== JSON.stringify(newE.data?.edgeLabels)) return false;
            if (JSON.stringify(e.data?.wireProperties) !== JSON.stringify(newE.data?.wireProperties)) return false;
            return true;
          })) {
        return prevEdges;
      }
      
      const nextEdges = connectionEdges.map(newEdge => {
        const prevEdge = prevEdges.find(e => e.id === newEdge.id);
        if (prevEdge) {
          // Preserve local state like selection and points (if currently dragging)
          // But allow points to be reset to [] if newEdge explicitly has no points
          const shouldResetPoints = !newEdge.data?.points || (Array.isArray(newEdge.data.points) && newEdge.data.points.length === 0);
          const isPendingSave = !!edgePropertiesSaveTimersRef.current[newEdge.id] || !!edgeLocalSavePendingRef.current[newEdge.id] || !!prevEdge.data?.edgeLabelsPendingSave;
          
          return { 
            ...newEdge, 
            selected: prevEdge.selected,
            style: isPendingSave ? prevEdge.style : newEdge.style,
            data: {
              ...newEdge.data,
              algorithm: isPendingSave ? prevEdge.data?.algorithm : newEdge.data?.algorithm,
              edgeLabels: isPendingSave ? prevEdge.data?.edgeLabels : newEdge.data?.edgeLabels,
              wireProperties: isPendingSave ? prevEdge.data?.wireProperties : newEdge.data?.wireProperties,
              edgeLabelsPendingSave: prevEdge.data?.edgeLabelsPendingSave,
              points: shouldResetPoints ? [] : (prevEdge.data?.points || newEdge.data?.points)
            }
          };
        }
        return newEdge;
      });
      previousEdgesRef.current = nextEdges;
      return nextEdges;
    });
  }, [connectionEdges]); // Removed setEdges from deps

  // Apply selection (and type for step/smoothstep) without recomputing connectionEdges
  const selectedEdgeId = selectedEdge?.id;
  const edgesForFlow = useMemo(() => {
    return edges.map(e => ({
      ...e,
      selected: selectedEdgeId === e.id
    }));
  }, [edges, selectedEdgeId]);

  const selectedEdgeForPanel = useMemo(() => {
    if (!selectedEdgeId) return null;
    return edges.find((edge) => edge.id === selectedEdgeId) || selectedEdge;
  }, [edges, selectedEdge, selectedEdgeId]);

  // Save route to DB (debounced caller uses this)
  const commitManualRouteSaveRef = useRef<((edgeId: string, waypoints: RoutePoint[]) => Promise<void>) | null>(null);
  
  const commitManualRouteSave = useCallback(async (edgeId: string, waypoints: RoutePoint[]): Promise<void> => {
    try {
      // Parse numeric connection id from edge id formats
      const numericIdStr = edgeId.replace(/^(connection-|auto-terminal-|device-terminal-)/, '');
      const connectionId = parseInt(numericIdStr, 10);
      if (isNaN(connectionId)) return;
      
      // Find the current edge to get its wire properties
      const currentEdge = connectionEdges.find((edge: Edge) => edge.id === edgeId);
      const currentWireProperties = currentEdge?.data?.wireProperties || {};
      
      // Strip type field for DB and round coordinates
      const payloadPoints = waypoints.map((wp) => ({ x: Math.round(wp.x), y: Math.round(wp.y) }));
      
      await updateConnection(connectionId, {
        path_points: payloadPoints,
        is_manual_route: true,
        wire_properties: currentWireProperties // Preserve wire properties including type
      });
      // Invalidate caches
      if (shouldShowMainSchemaFeatures) {
        queryClient.invalidateQueries({ queryKey: ['mainConnections', schemaId] });
        queryClient.invalidateQueries({ queryKey: ['cableTerminalConnections', schemaId] });
      } else {
        const terminalSchemaId = 2;
        queryClient.invalidateQueries({ queryKey: ['connections', terminalSchemaId] });
      }
    } catch (error) {
      console.error('❌ Failed to persist manual route:', error);
    }
  }, [schemaType, schemaId, queryClient, connectionEdges]);
  
  // Store the function in ref to avoid circular dependencies
  commitManualRouteSaveRef.current = commitManualRouteSave;
  
  // Update refs to avoid circular dependencies
  manualRoutesRef.current = manualRoutes;

  // Handle waypoint updates from CustomEdge with debounce persist
  const handleWaypointUpdate = useCallback((edgeId: string, newWaypoints: RoutePoint[]): void => {
    // Re-render immediately from local state for instant UX
    setManualRoutes(prev => ({
      ...prev,
      [edgeId]: newWaypoints
    }));
    // Track last waypoints
    lastWaypointsRef.current[edgeId] = newWaypoints;
    // Debounce DB save (per edge)
    const existingTimer = routeSaveTimersRef.current[edgeId];
    if (existingTimer) {
      window.clearTimeout(existingTimer);
    }
    routeSaveTimersRef.current[edgeId] = window.setTimeout(() => {
      const latest = lastWaypointsRef.current[edgeId] || newWaypoints;
      if (commitManualRouteSaveRef.current) {
        commitManualRouteSaveRef.current(edgeId, latest);
      }
      delete routeSaveTimersRef.current[edgeId];
    }, 400);
  }, []);
  
  // Store handleWaypointUpdate in ref
  handleWaypointUpdateRef.current = handleWaypointUpdate;

    // Edges are managed through connectionEdges useMemo

  // TEMPORARILY DISABLED: Load cables from canvas on initialization
  // useEffect(() => {
//     const loadCablesFromCanvas = async () => {
//       if (!blockId || !cableInstances || !terminalBlock?.schema_id) {
//         return;
//       }
//       
//       try {
//         const schemaLayoutResponse = await getSchemaLayout(terminalBlock.schema_id);
//         const schemaLayout = schemaLayoutResponse.data || [];
//         
//         if (schemaLayout && schemaLayout.length > 0) {
//           
//           // All items in schema_layout should be displayed on canvas
//           const canvasItems = schemaLayout.filter((item: any) => {
//             return item.device_instance_id && item.position_x !== undefined && item.position_y !== undefined;
//           });
//           
//           // Create nodes for all items in schema_layout
//           const cableIds = new Set<number>();
//           
//           for (const layoutItem of canvasItems) {
//             try {
//               // Load device instance data
//               const deviceInstanceData = await getDeviceInstanceById(layoutItem.device_instance_id);
//               
//               if (deviceInstanceData) {
//                 // Load contacts for this device specification
//                 const contacts = await getDeviceContacts(deviceInstanceData.specification_id);
//                 
//                 const deviceNode: Node = {
//                   id: deviceInstanceData.id.toString(),
//                   type: 'device',
//                   position: { 
//                     x: layoutItem.position_x, 
//                     y: layoutItem.position_y 
//                   },
//                   className: highlightedNodeId === deviceInstanceData.id.toString() ? 'highlighted' : '',
//                   data: {
//                     deviceName: deviceInstanceData.position_label,
//                     deviceType: deviceInstanceData.device_type || 'cable',
//                     specName: deviceInstanceData.spec_name,
//                     description: deviceInstanceData.description || '',
//                     deviceInstanceId: deviceInstanceData.id,
//                     specificationId: deviceInstanceData.specification_id,
//                     layoutId: layoutItem.id || 0,
//                     contacts,
//                     isReserve: Boolean(deviceInstanceData.is_reserve),
//                     onRemove: schemaType === 'terminal' ? () => removeCableFromCanvas(deviceInstanceData.id, layoutItem.id) : undefined
//                   }
//                 };
//                 
//                 
//                 
//                 setNodes(prevNodes => [...prevNodes, deviceNode]);
//                 
//                 // Track cables for library filtering
//                 if (deviceInstanceData.device_type === 'cable') {
//                   cableIds.add(deviceInstanceData.id);
//                 }
//               }
//             } catch (error) {
//               console.error(`Error loading device instance ${layoutItem.device_instance_id}:`, error);
//             }
//           }
//           
//           setCablesOnCanvas(cableIds);
//         }
//         
//       } catch (error) {
//         console.error('Error loading cables from canvas:', error);
//       }
//     };
//     
//   //   loadCablesFromCanvas();
//   // }, [blockId, terminalBlock?.schema_id]); // Simplified dependencies - only essential data
// 
//   // Debug: Log nodes when they change
//   useEffect(() => {
//     if (schemaType === 'terminal' && nodes.length > 0) {
//       // Debug logging removed - element info panel is working
//     }
//   }, [nodes, schemaType]);

  // ============= MAIN SCHEMA NODES LOADING =============
  
  // Load main and breaker schema devices on canvas when contacts are loaded
  // ИСПРАВЛЕНО: восстановлено с исправленными зависимостями
  useEffect(() => {
    if (shouldShowMainSchemaFeatures && schemaLayout && schemaLayout.length > 0) {
      // Create nodes with available contacts (they will update as more contacts load)
        
              // Convert layout items to React Flow nodes
      
      
      // DEBUG MODE: Filter only specific devices


      
      const deviceNodes: Node[] = schemaLayout
        .filter((item: any) => {
          const hasValidData = item.device_instance_id && item.position_x !== undefined && item.position_y !== undefined;
          if (!hasValidData) return false;
          
          // DEBUG FILTER
          if (GLOBAL_DEBUG_MODE) {
            return true;
          }
          
          return true;
        })
        .map((item: any) => {
          // Debug log removed to prevent console spam

          // Безопасная конвертация в строку с проверкой на undefined
        const nodeId = item.device_instance_id?.toString() || `item-${item.id}`;
          
          // Determine node type based on device type
          let nodeType = 'device';
          if (item.device_type_code === 'actuator' || item.device_type === 'actuator') {
            nodeType = 'actuator';
          } else if (item.device_type_code === 'cable' || item.device_type === 'cable' || item.device_type === 'Кабель') {
            nodeType = 'cable';
          }
          
          // Get contacts from specification cache (for all devices, cables will override in DeviceNode)
          const allContacts: DeviceContact[] = deviceContactsCache[item.specification_id] || [];
          const contacts: DeviceContact[] = allContacts;
          


          
          return {
            id: nodeId,
            type: nodeType,
            position: { x: item.position_x || 100, y: item.position_y || 100 },
            className: highlightedNodeId === nodeId ? 'highlighted' : '',
            data: {
              layoutId: item.id,
              deviceInstanceId: item.device_instance_id,
              specificationId: item.specification_id,
              deviceName: item.position_label || `Device #${item.device_instance_id}`,
              deviceType: item.device_type_code || item.device_type || 'device',
              deviceTypeId: item.device_type_id,
              description: item.description || '',
              specName: item.spec_name,
              powerConsumption: item.power_consumption, // Add power consumption from API
              isReserve: Boolean(item.is_reserve), // Convert 0/1 to false/true
              knxAddress: item.knx_address, // KNX address for KNX devices
              // For cables - will be loaded separately if needed
              cableInstance: (item.device_type_code === 'cable' || item.device_type === 'cable' || item.device_type === 'Кабель') ? {
                id: item.device_instance_id,
                position_label: item.position_label,
                spec_name: item.spec_name
              } : undefined,
              contacts: contacts, // For devices: loaded from cache; for cables: loaded async
              projectId: projectId,
              // Manufacturer and model for devices
              manufacturer: item.manufacturer,
              model: item.model,
              schemaType: schemaType, // Legacy поддержка
              schemaId: schemaId // Передаем schemaId для фильтрации контактов по конкретной схеме
            },
            draggable: !isReadOnly,
          } as Node;
        });

        // ИСПРАВЛЕНО: Убрано создание терминальных клеммников из основного useEffect
        // Терминальные клеммники теперь создаются в отдельном useEffect
        
        // Filter nodes by layer filters
        const filteredNodes = deviceNodes.filter(node => {
          const deviceType = (node.data.deviceType as string) || 'device';
          const isReserve = node.data.isReserve as boolean;
          
          // Check device type filter
          const deviceTypeKey = `show${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}s`;
          if (!layerFilters[deviceTypeKey]) return false;
          
          // Check reserve filter
          if (isReserve && !layerFilters.showReserve) return false;
          
          return true;
        });
        
        // Set filtered device nodes
        setNodes(prevNodes => {
          // Compare filteredNodes with prevNodes to avoid unnecessary updates
          // Need to handle the case where prevNodes has terminal nodes that shouldn't be removed
          const prevTerminalNodes = prevNodes.filter(n => n.id.startsWith('terminal-'));
          const prevDeviceNodes = prevNodes.filter(n => !n.id.startsWith('terminal-'));
          
          if (prevDeviceNodes.length === filteredNodes.length && prevDeviceNodes.every((n, i) => {
            const fn = filteredNodes[i];
            return n.id === fn.id && 
                   n.position.x === fn.position.x && 
                   n.position.y === fn.position.y &&
                   n.selected === fn.selected &&
                   JSON.stringify(n.data) === JSON.stringify(fn.data);
          })) {
            return prevNodes;
          }
          return [...filteredNodes, ...prevTerminalNodes];
        });
    }
      }, [schemaType, schemaLayout, projectId, highlightedNodeId, deviceContactsCache, layerFilters]); // Добавлена зависимость от deviceContactsCache и layerFilters

  // ИСПРАВЛЕНО: Отдельный useEffect для терминальных клеммников
  useEffect(() => {
    if (shouldShowTerminalFeatures && cableTerminalConnections && cableTerminalConnections.length > 0 && nodes.length > 0) {
      // Создаем только терминальные клеммники
      const autoTerminalNodes: Node[] = [];
      
      // DEBUG MODE: Filter only specific terminals and connections



      
      const filteredConnections = cableTerminalConnections;
      
      // Group connections by cable instance to position terminals correctly
      const cableGroups = new Map();
      
      // Only process connections to cables (skip actuator connections)
      filteredConnections
        .filter((conn: any) => conn.cable_device_type === 'cable')
        .forEach((connection: any) => {
          const cableId = connection.cable_instance_id;
          if (!cableGroups.has(cableId)) {
            cableGroups.set(cableId, []);
          }
          cableGroups.get(cableId).push(connection);
        });
      
      // Process each cable group
      cableGroups.forEach((connections: any[]) => {
        if (connections.length === 0) return;
        
        const cableX = connections[0].cable_position_x || 100;
        const cableY = connections[0].cable_position_y || 100;
        
        // Sort connections by cable contact order
        connections.sort((a, b) => {
          const aOrder = parseInt(a.cable_contact_number?.split('-')[0]) || 0;
          const bOrder = parseInt(b.cable_contact_number?.split('-')[0]) || 0;
          return aOrder - bOrder;
        });
        
        // Calculate cable width based on number of contacts
        // Note: Must match the actual cable width in MainSchemaDeviceNode
        const cableContactsCount = connections.length;
        const cableWidth = cableContactsCount <= 4 ? 90 : Math.max(90, cableContactsCount * 18 + 20);
        
        // Calculate total width of terminal group
        const terminalGroupWidth = (connections.length - 1) * 40; // 40px spacing between terminals
        
        // Calculate starting position to center terminals over cable
        const startX = cableX + (cableWidth - terminalGroupWidth) / 2;
        

        
        // Calculate positions for each connection
        connections.forEach((connection: any, index: number) => {
          // Calculate horizontal offset based on cable contact order (not terminal contact label)
          const contactSpacing = 40; // 40px between contacts
          const horizontalOffset = index * contactSpacing;
          
          // Place terminal above the cable with proper centering
          const terminalX = startX + horizontalOffset;
          const terminalY = cableY - 160; // 160px above cable
          
          // Create unique ID for each connection
          const terminalNodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`;
          

          
          autoTerminalNodes.push({
            id: terminalNodeId,
            type: 'autoTerminal',
            position: { x: terminalX, y: terminalY },
            data: {
              terminal_instance_id: connection.terminal_instance_id,
              terminal_block_id: connection.terminal_block_id,
              terminal_block_name: connection.terminal_block_name,
              terminal_block_description: connection.terminal_block_description,
              terminal_position_in_block: connection.terminal_position_in_block,
              terminal_contact_id: connection.terminal_contact_id,
              terminal_opposite_contact_id: connection.terminal_opposite_contact_id,
              terminal_contact_label: connection.terminal_contact_label,
              terminal_opposite_contact_label: connection.terminal_opposite_contact_label,
              terminal_specification_id: connection.terminal_specification_id,
              cable_instance_id: connection.cable_instance_id,
              cable_contact_id: connection.cable_contact_id,
              connection_id: connection.connection_id,
              source_schema_id: connection.source_schema_id,
              cable_contact_number: connection.cable_contact_number
            },
            draggable: !isReadOnly,
          } as Node);
        });
      });
      
      // Добавляем терминальные клеммники к существующим nodes
      setNodes(prevNodes => {
        // Remove old terminal nodes and add new ones with calculated positions
        const filteredNodes = prevNodes.filter(node => !node.id.startsWith('terminal-'));
        
        // Only update if terminal nodes have changed
        const currentTerminalNodes = prevNodes.filter(node => node.id.startsWith('terminal-'));
        if (currentTerminalNodes.length === autoTerminalNodes.length && 
            currentTerminalNodes.every((n, i) => {
              const an = autoTerminalNodes[i];
              if (n.id !== an.id) return false;
              if (n.position.x !== an.position.x || n.position.y !== an.position.y) return false;
              if (JSON.stringify(n.data) !== JSON.stringify(an.data)) return false;
              return true;
            })) {
          return prevNodes;
        }
        
        return [...filteredNodes, ...autoTerminalNodes];
      });
    }
  }, [schemaType, cableTerminalConnections]); // Removed nodes.length to prevent infinite loop

  // NEW: Track elements on canvas for main and breaker schema library
  useEffect(() => {
    if (shouldShowMainSchemaFeatures && nodes.length > 0) {
      const deviceIds = new Set<number>();
      const cableIds = new Set<number>();
      
      nodes.forEach(node => {
        const deviceInstanceId = node.data?.deviceInstanceId;
        const deviceType = node.data?.deviceType;
        
        if (deviceInstanceId && typeof deviceInstanceId === 'number') {
          if (deviceType === 'cable' || deviceType === 'Кабель') {
            cableIds.add(deviceInstanceId);
          } else {
            deviceIds.add(deviceInstanceId);
          }
        }
      });
      
      setDevicesOnCanvas(deviceIds);
      setCablesOnCanvas(cableIds);
    }
  }, [schemaType, nodes]);

  // NEW: Track cables on terminal schema canvas
  useEffect(() => {
    if (schemaType === 'terminal' && nodes.length > 0) {
      const terminalCableIds = new Set<number>();
      
      nodes.forEach(node => {
        if (node.id.startsWith('cable-')) {
          const cableInstanceId = node.data?.deviceInstanceId;
          if (cableInstanceId && typeof cableInstanceId === 'number') {
            terminalCableIds.add(cableInstanceId);
          }
        }
      });
      
      setCablesOnCanvas(terminalCableIds);
    }
  }, [schemaType, nodes]);

  // Load and save layer filters
  useEffect(() => {
    const storageKey = `schema-layers-prefs-${currentUser?.id || 'guest'}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const savedFilters = JSON.parse(saved);
        setLayerFilters(prev => ({ ...prev, ...savedFilters }));
      } catch (e) {
        console.error('Error parsing saved layer filters:', e);
      }
    } else if (schemaId) {
      // Migrate from old per-schema setting if available
      const oldKey = `schema-layers-${schemaId}`;
      const oldSaved = localStorage.getItem(oldKey);
      if (oldSaved) {
        try {
          const savedFilters = JSON.parse(oldSaved);
          setLayerFilters(prev => ({ ...prev, ...savedFilters }));
        } catch (e) {
          console.error('Error parsing old layer filters:', e);
        }
      }
    }
  }, [currentUser?.id, schemaId]);

  // Save layer filters when they change
  useEffect(() => {
    const storageKey = `schema-layers-prefs-${currentUser?.id || 'guest'}`;
    localStorage.setItem(storageKey, JSON.stringify(layerFilters));
  }, [layerFilters, currentUser?.id]);

  // Update contacts in existing nodes when deviceContactsCache changes
  useEffect(() => {
    if (shouldShowMainSchemaFeatures && nodes.length > 0 && Object.keys(deviceContactsCache).length > 0) {
      setNodes(prevNodes => {
        let changed = false;
        const nextNodes = prevNodes.map(node => {
          const specId = node.data?.specificationId as number;
          if (specId && deviceContactsCache[specId]) {
            const allContacts: DeviceContact[] = deviceContactsCache[specId] || [];
            const contacts: DeviceContact[] = allContacts;
            
            // Only update if contacts actually changed
            const currentContactsStr = JSON.stringify(node.data.contacts || []);
            const newContactsStr = JSON.stringify(contacts);
            
            if (currentContactsStr !== newContactsStr) {
              changed = true;
              return {
                ...node,
                data: {
                  ...node.data,
                  contacts: contacts
                }
              };
            }
          }
          return node;
        });
        return changed ? nextNodes : prevNodes;
      });
    }
  }, [deviceContactsCache, schemaType]); // FIXED: Removed nodes.length to prevent infinite loop

  // ============= END MAIN SCHEMA NODES LOADING =============

  // ============= MAIN SCHEMA CONTACTS LOADING =============
  
  // Preload contacts for all devices in main and breaker schemas  
  useEffect(() => {
    if (shouldShowMainSchemaFeatures && schemaLayout && schemaLayout.length > 0) {
      const preloadContacts = async () => {
        // Get unique specification IDs (for all devices)
        const deviceSpecIds = [...new Set(
          schemaLayout
            .filter((item: any) => item.device_instance_id && item.position_x !== undefined && item.position_y !== undefined)
            .map(item => item.specification_id)
        )];
        
        // Filter out already loaded specs
        const missingSpecIds = deviceSpecIds.filter(specId => !deviceContactsCache[specId]);
        
        if (missingSpecIds.length === 0) return;
        
        // Load ALL contacts in parallel - BATCH LOADING
        // Используем schemaId вместо schemaType для фильтрации по конкретным схемам
        try {
          const contactPromises = missingSpecIds.map(specId => 
            getDeviceContacts(specId, undefined, schemaId).then(contacts => ({ specId, contacts }))
          );
          
          const results = await Promise.all(contactPromises);
          
          // Update cache with ALL results at once - SINGLE STATE UPDATE
          const newCache = results.reduce((acc, { specId, contacts }) => {
            acc[specId] = contacts;
            return acc;
          }, { ...deviceContactsCache });
          
          setDeviceContactsCache(newCache);
        } catch (error) {
          console.error('Error batch loading contacts:', error);
        }
      };
      
      preloadContacts();
    }
  }, [schemaType, schemaLayout]); // REMOVED deviceContactsCache dependency

  // Clear contacts cache when schema type changes
  useEffect(() => {
    setDeviceContactsCache({});
  }, [schemaType]);

  // ============= END MAIN SCHEMA CONTACTS LOADING =============

  // Filter available cables (not on canvas)
  const availableCables = useMemo(() => {
    if (!cableInstances || !Array.isArray(cableInstances)) return [];
    return cableInstances.filter((cable: any) => !cablesOnCanvas.has(cable.id));
  }, [cableInstances, cablesOnCanvas]);
  
  // Filter cables by search query
  const filteredCables = useMemo(() => {
    if (!cableSearchQuery.trim()) return availableCables;
    
    const query = cableSearchQuery.toLowerCase().trim();
    return availableCables.filter((cable: any) => {
      const position = cable.position_label?.toLowerCase() || '';
      const specName = cable.spec_name?.toLowerCase() || '';
      
      return position.includes(query) || specName.includes(query);
    });
  }, [availableCables, cableSearchQuery]);
  
  // NEW: Filter available devices by type for main schema (not on canvas + search)
  const availableDevicesByType = useMemo(() => {
    if (!availableDevices || !Array.isArray(availableDevices)) return {};
    
    let filtered = availableDevices.filter((device: any) => !devicesOnCanvas.has(device.id));
    
    // Apply search filter if query exists
    if (librarySearchQuery.trim()) {
      const query = librarySearchQuery.toLowerCase().trim();
      filtered = filtered.filter((device: any) => 
        (device.position_label && device.position_label.toLowerCase().includes(query)) ||
        (device.spec_name && device.spec_name.toLowerCase().includes(query)) ||
        (device.description && device.description.toLowerCase().includes(query))
      );
    }
    
    // Group devices by device_type
    const grouped: Record<string, any[]> = {
      lighting: [],
      outlet: [],
      switch: [],
      sensor: [],
      actuator: [],
      breaker: [],
      other: []
    };
    
    filtered.forEach((device: any) => {
      const deviceType = device.device_type || 'other';
      if (grouped[deviceType]) {
        grouped[deviceType].push(device);
      } else {
        grouped.other.push(device);
      }
    });
    
    return grouped;
  }, [availableDevices, devicesOnCanvas, librarySearchQuery]);
  
  // NEW: Filter available cables for main schema (not on canvas + search)  
  const availableCablesFiltered = useMemo(() => {
    if (!availableCablesMain || !Array.isArray(availableCablesMain)) return [];
    
    let filtered = availableCablesMain.filter((cable: any) => !cablesOnCanvas.has(cable.id));
    
    // Apply search filter if query exists
    if (librarySearchQuery.trim()) {
      const query = librarySearchQuery.toLowerCase().trim();
      filtered = filtered.filter((cable: any) => 
        (cable.position_label && cable.position_label.toLowerCase().includes(query)) ||
        (cable.spec_name && cable.spec_name.toLowerCase().includes(query)) ||
        (cable.description && cable.description.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [availableCablesMain, cablesOnCanvas, librarySearchQuery]);



  // Remove element from terminal schema canvas
  const removeCableFromCanvas = useCallback(async (deviceInstanceId: number, layoutId?: number) => {
    // Режим только просмотр - запрещено удаление элементов
    if (isReadOnly) {
      return;
    }
    try {
      // Remove element node from canvas (using correct ID format)
      setNodes(prevNodes => prevNodes.filter(node => node.id !== deviceInstanceId.toString()));
      
      // Update cables on canvas tracking
      setCablesOnCanvas(prev => {
        const updated = new Set([...prev]);
        updated.delete(deviceInstanceId);
        return updated;
      });

      // Delete from schema_layout table to remove from terminal schema
      if (layoutId) {
        try {
          const { removeDeviceFromSchema } = await import('../../api/schemas');
          await removeDeviceFromSchema(layoutId);
        } catch (schemaError) {
          console.error('❌ Error removing device from schema layout:', schemaError);
        }
      } else {
        // console.warn('DEBUG no layoutId provided for schema removal');
      }

      // NOTE: Connections are NOT deleted when removing device from canvas
      // Connections represent physical connections between devices in the project
      // and should remain even if device is removed from a specific schema
      
      // Refresh cable library data (connections are not invalidated since they're not deleted)
      queryClient.invalidateQueries({ queryKey: ['availableTerminalCables', projectId, blockId] });
      
    } catch (error) {
      console.error('❌ Error removing element from canvas:', error);
    }
  }, [queryClient, projectId, blockId, isReadOnly]);

  // Remove device from main schema canvas
  const removeFromMainSchema = useCallback(async (deviceInstanceId: number) => {
    // Режим только просмотр - запрещено удаление элементов
    if (isReadOnly) {
      return;
    }
    try {
      // Get device type from API before removal
      const { getDeviceInstanceById } = await import('../../api/deviceInstances');
      const deviceInstance = await getDeviceInstanceById(deviceInstanceId);
      const isCable = deviceInstance?.device_type === 'cable' || deviceInstance?.device_type === 'Кабель';
      
      // Remove device node from canvas (ID = deviceInstanceId as string)
      setNodes(prevNodes => prevNodes.filter(node => 
        node.id !== deviceInstanceId.toString()
      ));

      // Immediately update local canvas state based on device type
      if (isCable) {
        setCablesOnCanvas(prev => {
          const updated = new Set(prev);
          updated.delete(deviceInstanceId);
          return updated;
        });
      } else {
        setDevicesOnCanvas(prev => {
          const updated = new Set(prev);
          updated.delete(deviceInstanceId);
          return updated;
        });
      }

      // NOTE: Connections are NOT deleted when removing device from canvas
      // Connections represent physical connections between devices in the project
      // and should remain even if device is removed from a specific schema

      // Handle removal based on schema type - ALL schemas now use schema_layout deletion
      // Find layoutId from schemaLayoutResponse for the current schema
      const layoutItem = schemaLayoutResponse?.data?.find(item => item.device_instance_id === deviceInstanceId);
      
      if (layoutItem?.id) {
        const { removeDeviceFromSchema } = await import('../../api/schemas');
        await removeDeviceFromSchema(layoutItem.id);
        
        // Refresh schema layout and library data
        queryClient.invalidateQueries({ queryKey: ['schemaLayout', schemaType, schemaId] });
        queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId, electricalPanelId, schemaType, schemaId] });
        queryClient.invalidateQueries({ queryKey: ['availableCablesMain', projectId, electricalPanelId, schemaType, schemaId] });
      } else {
        console.warn('⚠️ No layout item found for device:', deviceInstanceId, 'in schema:', schemaId);
        
        // Fallback: force immediate refresh of appropriate library data based on device type
        if (isCable) {
          queryClient.refetchQueries({ queryKey: ['availableCablesMain', projectId] });
        } else {
          queryClient.refetchQueries({ queryKey: ['availableDevices', projectId] });
        }
      }
      // NOTE: No need to invalidate connections cache since connections are not deleted
      
    } catch (error) {
      console.error('❌ Error removing device from main schema:', error);
    }
  }, [queryClient, projectId, schemaId, schemaType, electricalPanelId, schemaLayoutResponse, isReadOnly]);

  // Add cable to canvas
  const addCableToCanvas = useCallback(async (cableInstance: any, position?: { x: number; y: number }, overrideSchemaId?: number) => {
    try {
      // Handle both formats: from drag&drop (deviceInstanceId) and from API (id)
      const deviceInstanceId = cableInstance.deviceInstanceId || cableInstance.id;
      
      // Load contacts for this cable specification
      // Используем schemaId вместо schemaType для фильтрации по конкретным схемам
      const specificationId = cableInstance.specification_id || cableInstance.specificationId;
      const contacts = await getDeviceContacts(specificationId, undefined, schemaId);
      
      // Default position (center of current viewport)
      const defaultPosition = position || getViewportCenter();
      
      const newNode: Node = {
        id: deviceInstanceId.toString(), // Use same ID format as loaded from DB
        type: 'device', // Use same type as loaded from DB  
        position: defaultPosition,
        className: highlightedNodeId === deviceInstanceId.toString() ? 'highlighted' : '',
        data: {
          deviceName: cableInstance.position_label || cableInstance.positionLabel,
          deviceType: 'cable',
          specName: cableInstance.spec_name || cableInstance.specName,
          description: '',
          deviceInstanceId: deviceInstanceId,
          layoutId: 0, // Will be updated after saving to DB
          contacts,
          specificationId: specificationId,
          isReserve: Boolean(cableInstance.is_reserve), // Add reserve status for grey display
          schemaId: schemaId, // Передаем schemaId для фильтрации контактов
          onRemove: (schemaType === 'terminal' && !isReadOnly) ? () => removeCableFromCanvas(deviceInstanceId) : undefined,
          // isConnecting - TEMPORARILY COMMENTED
        }
      };
      
      setNodes(prevNodes => [...prevNodes, newNode]);
      
      // Immediately update local canvas state to remove element from library
      setCablesOnCanvas(prev => {
        const updated = new Set([...prev, deviceInstanceId]);
        return updated;
      });
      
      setDevicesOnCanvas(prev => {
        const updated = new Set([...prev, deviceInstanceId]);
        return updated;
      });

      // Save cable position to database for persistence
      try {
                if (schemaType === 'terminal') {
          // Terminal schema: Use override schema_id or terminal block schema_id
          const terminalSchemaId = overrideSchemaId || blockId;
          
          if (!terminalSchemaId) {
            console.error('❌ No schema_id available for terminal schema operation');
            return;
          }
          
          await addDeviceToSchema(
            terminalSchemaId,
            deviceInstanceId,
            Math.round(defaultPosition.x),
            Math.round(defaultPosition.y),
            0 // rotation
          );
          
          // Note: Cable placement on terminal schema is now handled by schema_layout table
          
          // Refresh terminal cable library
          queryClient.invalidateQueries({ queryKey: ['availableTerminalCables', projectId, blockId] });
          
        } else if (shouldShowMainSchemaFeatures && schemaId) {
          // Main schema: Use the actual schemaId from URL
          await addDeviceToSchemaWithIsolation(
            schemaType as SchemaType,
            Number(schemaId),
            deviceInstanceId,
            Math.round(defaultPosition.x),
            Math.round(defaultPosition.y),
            0 // rotation
          );
          
          // Legacy on_main_schema logic removed
          
          // Refresh main schema cable library  
          queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId] });
          queryClient.invalidateQueries({ queryKey: ['schemaLayout', schemaId] });
        }
        
      } catch (error) {
        console.error('❌ Error saving cable position:', error);
      }
      
    } catch (error) {
      console.error('❌ Error adding cable to canvas:', error);
    }
  }, [queryClient, projectId, removeCableFromCanvas]); // isConnecting - TEMPORARILY COMMENTED

  // Handle cable click (add to center)
  const handleCableClick = useCallback((cableInstance: any) => {
    const schemaId = blockId;
    addCableToCanvas(cableInstance, undefined, schemaId);
  }, [addCableToCanvas, blockId]);

  // Add device to canvas (similar to addCableToCanvas but for devices)
  const addDeviceToCanvas = useCallback(async (deviceInstance: any, position?: { x: number; y: number }) => {
    try {
      // Load device contacts from specification
      // Используем schemaId вместо schemaType для фильтрации по конкретным схемам
      const contacts = await getDeviceContacts(deviceInstance.specification_id, undefined, schemaId);
      
      // Default position (center of current viewport)
      const defaultPosition = position || getViewportCenter();
      
      const newNode: Node = {
        id: deviceInstance.id.toString(), // Use same ID format as loaded from DB
        type: 'device', // Use same type as loaded from DB  
        position: defaultPosition,
        className: highlightedNodeId === deviceInstance.id.toString() ? 'highlighted' : '',
        data: {
          deviceName: deviceInstance.position_label,
          deviceType: 'device', // Correct type for devices
          specName: deviceInstance.spec_name,
          description: deviceInstance.description || '',
          deviceInstanceId: deviceInstance.id,
          layoutId: 0, // Will be updated after saving to DB
          contacts,
          specificationId: deviceInstance.specification_id,
          isReserve: Boolean(deviceInstance.is_reserve), // Add reserve status for grey display
          schemaId: schemaId, // Передаем schemaId для фильтрации контактов
          onRemove: (shouldShowMainSchemaFeatures && !isReadOnly) ? () => removeFromMainSchema(deviceInstance.id) : undefined,
          // isConnecting - TEMPORARILY COMMENTED
        }
      };
      
      setNodes(prevNodes => [...prevNodes, newNode]);
      
      // Immediately update local canvas state to remove element from library
      setDevicesOnCanvas(prev => {
        const updated = new Set([...prev, deviceInstance.id]);
        return updated;
      });
      
      // Save device position to database for persistence (main and breaker schemas)
      if (shouldShowMainSchemaFeatures && schemaId) {
        try {
          await addDeviceToSchemaWithIsolation(
            schemaType as SchemaType,
            Number(schemaId), // Use main schema ID
            deviceInstance.id,
            Math.round(defaultPosition.x),
            Math.round(defaultPosition.y),
            0 // rotation
          );
          
          // Legacy on_main_schema logic removed
          
          // Refresh device library to remove this device from available list
          queryClient.refetchQueries({ queryKey: ['availableDevices', projectId] });
          
        } catch (error) {
          console.error('❌ Error saving device to schema:', error);
          // Remove from canvas if save failed
          setNodes(prevNodes => prevNodes.filter(node => node.id !== deviceInstance.id.toString()));
          setDevicesOnCanvas(prev => {
            const updated = new Set(prev);
            updated.delete(deviceInstance.id);
            return updated;
          });
        }
      }
      
    } catch (error) {
      console.error('❌ Error adding device to canvas:', error);
    }
  }, [queryClient, projectId, schemaType, schemaId, getViewportCenter, highlightedNodeId, removeFromMainSchema]); // isConnecting - TEMPORARILY COMMENTED

  // Handle device click (add to center)
  const handleDeviceClick = useCallback((deviceInstance: any) => {
    addDeviceToCanvas(deviceInstance);
  }, [addDeviceToCanvas]);

  // Handle drop on canvas
  const handleCanvasDrop = useCallback((e: React.DragEvent) => {
    // Режим только просмотр - запрещено добавление элементов на холст
    if (isReadOnly) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    
    const position = screenToFlowPosition({
      x: e.clientX,
      y: e.clientY,
    });
    
    if (dragRef.current) {
      // Determine if it's a cable or device and use appropriate function
      if (dragRef.current.type === 'cable' || dragRef.current.deviceType === 'cable') {
        addCableToCanvas(dragRef.current, position);
      } else {
        addDeviceToCanvas(dragRef.current, position);
      }
    } else {
      // Try to get data from dataTransfer
      try {
        const itemData = e.dataTransfer.getData('text/plain');
        if (itemData) {
          const itemInstance = JSON.parse(itemData);
          // Check type and use appropriate function
          if (itemInstance.type === 'cable') {
            addCableToCanvas(itemInstance, position);
          } else if (itemInstance.type === 'device') {
            addDeviceToCanvas(itemInstance, position);
          }
        }
      } catch (error) {
        console.error('❌ Error parsing dropped data:', error);
      }
    }
    
    dragRef.current = null;
  }, [addCableToCanvas, addDeviceToCanvas, screenToFlowPosition, isReadOnly]);

  // Prevent default drag over
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  // Create connection mutation
  const createConnectionMutation = useMutation({
    mutationFn: async (connectionData: {
      schema_id: number,
      from_instance_id: number,
      from_contact_id: number,
      to_instance_id: number,
      to_contact_id: number,
      wire_type: string
    }) => {
      // Debug log removed - connection working correctly
      return createConnection(connectionData);
    },
          onSuccess: () => {
        // Invalidate project connections query to refresh all schemas
        queryClient.invalidateQueries({ queryKey: ['projectConnections', projectId] });
      // Also invalidate cable-terminal connections
      queryClient.invalidateQueries({ queryKey: ['cableTerminalConnections', schemaId] });
    },
    onError: (error) => {
      // Мутация не вызывается в режиме только просмотр, поэтому ошибки здесь быть не должно
      console.error('❌ API error creating connection:', error);
    }
  });

  // Delete connection mutation
  const deleteConnectionMutation = useMutation({
    mutationFn: async (connectionId: number) => {
              await deleteConnection(connectionId);
    },
    onSuccess: () => {
      // Invalidate project connections query to refresh all schemas
      queryClient.invalidateQueries({ queryKey: ['projectConnections', projectId] });
      // Also invalidate cable-terminal connections
      queryClient.invalidateQueries({ queryKey: ['cableTerminalConnections', schemaId] });
    },
    onError: (error) => {
      // Мутация не вызывается в режиме только просмотр, поэтому ошибки здесь быть не должно
      console.error('❌ API error deleting connection:', error);
    }
  });

  // Update connection mutation for smart line editing and route updates
  const updateConnectionMutation = useMutation({
    mutationFn: async ({ connectionId, params }: { connectionId: number; params: any }) => {
      await updateConnection(connectionId, params);
    },
    onSuccess: () => {
      // Invalidate project connections query to refresh all schemas
      queryClient.invalidateQueries({ queryKey: ['projectConnections', projectId] });
      // Also invalidate cable-terminal connections
      queryClient.invalidateQueries({ queryKey: ['cableTerminalConnections', schemaId] });
    },
    onError: (error) => {
      console.error('❌ Failed to save connection properties:', error);
    },
  });

  // Watch for local edge changes (e.g. from EditableEdgeComponent) and save to server
  useEffect(() => {
    if (edgeSaveTimerRef.current) {
      window.clearTimeout(edgeSaveTimerRef.current);
    }
    
    edgeSaveTimerRef.current = window.setTimeout(() => {
      edges.forEach(edge => {
        const prevEdge = previousEdgesRef.current.find(e => e.id === edge.id);
        if (prevEdge && prevEdge.data && edge.data) {
          const pointsChanged = JSON.stringify(prevEdge.data.points) !== JSON.stringify(edge.data.points);
          const algorithmChanged = prevEdge.data.algorithm !== edge.data.algorithm;
          const edgeLabelsChanged = JSON.stringify(prevEdge.data.edgeLabels) !== JSON.stringify(edge.data.edgeLabels);
          
          if (pointsChanged || algorithmChanged || edgeLabelsChanged) {
            // Save to server
            const connectionId = edge.data.connectionId as number;
            if (connectionId) {
              if (edgeLabelsChanged) {
                edgeLocalSavePendingRef.current[edge.id] = true;
              }
              const pathPoints = (edge.data.points as any[])?.map(p => ({ x: p.x, y: p.y, type: 'waypoint' })) || [];
              const wireProperties = {
                ...((edge.data as any).wireProperties || {}),
              };
              const edgeType = getWireTypeFromAlgorithm(edge.data.algorithm as Algorithm | undefined);
              if (edgeType && (algorithmChanged || !wireProperties.type)) {
                wireProperties.type = edgeType;
              }
              if (edgeLabelsChanged) {
                wireProperties.edge_labels = edge.data.edgeLabels || [];
                delete wireProperties.edgeLabels;
              }
              
              updateConnectionMutation.mutate({
                connectionId,
                params: {
                  ...(pointsChanged || algorithmChanged ? { path_points: pathPoints, is_manual_route: true } : {}),
                  wire_properties: wireProperties
                }
              }, {
                onSettled: () => {
                  delete edgeLocalSavePendingRef.current[edge.id];
                  setEdges(currentEdges => currentEdges.map(currentEdge => (
                    currentEdge.id === edge.id
                      ? {
                          ...currentEdge,
                          data: {
                            ...currentEdge.data,
                            edgeLabelsPendingSave: false,
                          },
                        }
                      : currentEdge
                  )));
                }
              });
            }
          }
        }
      });
      previousEdgesRef.current = edges;
    }, 500);

    return () => {
      if (edgeSaveTimerRef.current) {
        window.clearTimeout(edgeSaveTimerRef.current);
      }
    };
  }, [edges, updateConnectionMutation]);

  // Create schema mutation
  const createSchemaMutation = useMutation({
    mutationFn: async (schemaData: {
      project_id: number;
      name: string;
      description?: string;
      schema_type?: 'main' | 'terminal' | 'knx' | 'rs485';
      electrical_panel_id?: number;
    }) => {
      return createSchema(schemaData);
    },
    onSuccess: (newSchema) => {
      // Invalidate schemas query to reload data
      queryClient.invalidateQueries({ queryKey: ['schemas', projectId] });
      // Close modal and navigate to new schema
      setShowCreateSchemaModal(false);
      // Navigate to the new schema
      window.location.href = `/projects/${projectId}/schemas/${newSchema.id}`;
    },
    onError: (error) => {
      console.error('❌ Failed to create schema:', error);
    },
  });

  // Handle schema creation form submission
  const handleCreateSchema = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    // Convert schemaType string to schema_type_id
    const getSchemaTypeId = (schemaTypeStr: string): number => {
      switch (schemaTypeStr) {
        case 'main': return 1;
        case 'breaker': return 2;
        case 'knx': return 3;
        case 'rs485': return 4;
        case 'ethernet': return 5;
        case 'terminal': return 6;
        case 'floor_plan': return 8;
        default: return 1;
      }
    };

    const schemaData = {
      project_id: Number(projectId),
      name: formData.get('name') as string,
      description: formData.get('description') as string || undefined,
      schema_type_id: getSchemaTypeId(schemaType),
      electrical_panel_id: config.electricalPanelRequired 
        ? Number(formData.get('electrical_panel_id')) 
        : undefined
    };

    createSchemaMutation.mutate(schemaData);
  }, [projectId, schemaType, config.electricalPanelRequired, createSchemaMutation]);

  // Handle route updates from manual editor
  const handleRouteUpdate = useCallback(async (connectionId: number, pathPoints: RoutePoint[], isManual: boolean) => {
    try {
      // console.log('Updating route for connection:', connectionId, 'pathPoints:', pathPoints, 'isManual:', isManual);
      
      // Optmistically update selectedEdge to clear points if resetting
      if (pathPoints.length === 0) {
        setSelectedEdge((prev: any) => {
          if (prev && (prev.id === `connection-${connectionId}` || prev.id === `device-terminal-${connectionId}`)) {
            return {
              ...prev,
              data: {
                ...prev.data,
                points: []
              }
            };
          }
          return prev;
        });
      }

      await updateConnectionMutation.mutateAsync({
        connectionId,
        params: {
          path_points: pathPoints,
          is_manual_route: isManual
        }
      });
      // Force immediate cache invalidation for all connection queries
      queryClient.invalidateQueries({ queryKey: ['projectConnections', projectId] });
      if (schemaType === 'terminal' && blockId) queryClient.invalidateQueries({ queryKey: ['connections', blockId] });
      if (shouldShowMainSchemaFeatures) queryClient.invalidateQueries({ queryKey: ['cableTerminalConnections', schemaId] });
    } catch (error) {
      console.error('Failed to update route:', error);
    }
  }, [updateConnectionMutation, queryClient, projectId, schemaType, blockId, shouldShowMainSchemaFeatures, schemaId, setSelectedEdge]);

  // Handle edge click for Smart Line Panel and waypoint addition
  // Temporarily disabled to fix selection issues
  /*
  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: any) => {
      event.stopPropagation();
              // console.log('Edge clicked:', edge.id, 'detail:', event.detail, 'type:', edge.type, 'data:', edge.data);
      
      // Double click for adding waypoint to route (ALWAYS)
      if (event.detail === 2) {
        // console.log('Double click detected, adding waypoint to route');
        
        // Prevent default behavior and stop propagation
        event.preventDefault();
        event.stopPropagation();
        
        // Get click position in flow coordinates
        const { clientX, clientY } = event;
        
        // Parse connection ID
        const connectionId = parseInt(edge.id.replace(/^(connection-|auto-terminal-|device-terminal-)/, ''));
        if (!isNaN(connectionId)) {
          // Get current path points
          const currentPathPoints = edge.data?.path_points || [];
          
          // Add new waypoint (convert screen coordinates to flow coordinates)
          const newWaypoint = { x: Math.round(clientX), y: Math.round(clientY) };
          const updatedPathPoints = [...currentPathPoints, newWaypoint];
          
          // Update route in database
          handleRouteUpdate(connectionId, updatedPathPoints, true);
        }
        return;
      }
      
      // Right click for opening Smart Line Panel
      if (event.button === 2) {
        // console.log('Right click detected, opening Smart Line Panel');
        setSelectedEdge(edge);
        setActivePopup(null);
        return;
      }
      
      // Single click - allow normal selection (SmartLinePanel will open automatically via onSelectionChange)
      // Don't block the default selection behavior
    },
    [handleRouteUpdate]
  );
  */



  // Visibility for SmartLinePanel "show on schemas" (connection-* и device-terminal-* — устройство/автомат к терминалу)
  const selectedConnectionVisibility = useMemo(() => {
    const id = selectedEdge?.id;
    if (!id?.startsWith('connection-') && !id?.startsWith('device-terminal-')) return null;
    const cid = parseInt(id.replace(/^(connection-|device-terminal-)/, ''), 10);
    if (isNaN(cid)) return null;
    const schemaIdNum = schemaId ? Number(schemaId) : undefined;
    if (id.startsWith('device-terminal-')) {
      const conn = cableTerminalConnections?.find((c: any) => Number(c.connection_id) === cid || c.connection_id === cid) as any;
      return {
        connectionId: cid,
        schema_id: conn?.schema_id ?? schemaIdNum ?? 0,
        visibility_legacy: conn ? (conn.visibility_legacy === undefined ? true : !!conn.visibility_legacy) : true,
        visible_on_schema_ids: conn?.visible_on_schema_ids ?? []
      };
    }
    const list = schemaType === 'terminal' ? connections : mainConnections;
    const conn = list?.find((c: any) => Number(c.id) === cid || c.id === cid);
    return {
      connectionId: cid,
      schema_id: conn?.schema_id ?? schemaIdNum ?? 0,
      visibility_legacy: conn ? (conn.visibility_legacy === undefined ? true : !!conn.visibility_legacy) : true,
      visible_on_schema_ids: conn?.visible_on_schema_ids ?? []
    };
  }, [selectedEdge, schemaType, schemaId, connections, mainConnections, cableTerminalConnections]);

  const handleSaveVisibility = useCallback(async (connectionId: number, payload: { schema_id?: number; visibility_legacy: boolean; visible_on_schema_ids: number[] }) => {
    await updateConnection(connectionId, payload);
    queryClient.invalidateQueries({ queryKey: ['projectConnections', projectId] });
    if (schemaType === 'terminal' && blockId) queryClient.invalidateQueries({ queryKey: ['connections', blockId] });
    if (shouldShowMainSchemaFeatures) queryClient.invalidateQueries({ queryKey: ['cableTerminalConnections', schemaId] });
  }, [projectId, schemaType, blockId, schemaId, shouldShowMainSchemaFeatures, queryClient]);

  // Smart Line Panel handlers
  const updateEdgeProperties = useCallback((edgeId: string, properties: any) => {
    // Parse connection ID from different edge formats
    let connectionId: number | null = null;
    
    if (edgeId.startsWith('connection-')) {
      connectionId = parseInt(edgeId.replace('connection-', ''));
    } else if (edgeId.startsWith('device-terminal-')) {
      connectionId = parseInt(edgeId.replace('device-terminal-', ''));
    } else if (edgeId.startsWith('auto-terminal-')) {
      connectionId = parseInt(edgeId.replace('auto-terminal-', ''));
    }
    
    if (connectionId && !isNaN(connectionId)) {
      // Handle reset case - remove all custom properties
      let wireProperties: any = {};
      
      const hasEdgeLabels = Object.prototype.hasOwnProperty.call(properties, 'edgeLabels');
      const isReset = properties.type === undefined && properties.algorithm === undefined && properties.style === undefined && properties.edgeLabels === undefined;
      if (isReset) {
        // Reset case - clear all properties
        wireProperties = null; // This will clear wire_properties in DB
        
        // Reset edge visually
        setEdges(prev => prev.map(e => {
          if (e.id === edgeId) {
            return {
              ...e,
              style: undefined,
              data: {
                ...e.data,
                algorithm: Algorithm.BezierCatmullRom, // default
                edgeLabels: [],
                wireProperties: {}
              }
            };
          }
          return e;
        }));

        if (selectedEdge && selectedEdge.id === edgeId) {
          const updatedEdge = {
            ...selectedEdge,
            type: 'editable-edge',
            style: undefined,
            data: {
              ...selectedEdge.data,
              algorithm: Algorithm.BezierCatmullRom, // default
              edgeLabels: [],
              wireProperties: {}
            }
          } as any;
          setSelectedEdge(updatedEdge);
        }
      } else {
        // Normal update case
        // Find existing wire properties for this edge to preserve unspecified fields
        const currentEdge = edges.find((e: any) => e.id === edgeId) || connectionEdges.find((e: any) => e.id === edgeId);
        const existingWireProps = (currentEdge?.data?.wireProperties) || (selectedEdge?.id === edgeId ? (selectedEdge.data as any)?.wireProperties : {}) || {};
        
        const { strokeDasharray, ...otherStyleProps } = properties.style || {};
        
        const existingWirePropsAny = existingWireProps as any;
        
        // Map Algorithm enum back to wire property type if algorithm is provided
        let newType = properties.type !== undefined ? properties.type : existingWirePropsAny?.type;
        if (properties.algorithm !== undefined) {
          newType = getWireTypeFromAlgorithm(properties.algorithm);
        } else if (newType === undefined) {
          const currentAlgorithm = currentEdge?.data?.algorithm || (selectedEdge?.id === edgeId ? selectedEdge.data?.algorithm : undefined);
          const inferredType = getWireTypeFromAlgorithm(currentAlgorithm as Algorithm | undefined);
          if (inferredType !== undefined) {
            newType = inferredType;
          }
        }

        wireProperties = {
          ...existingWirePropsAny,
          color: properties.style?.stroke ?? existingWirePropsAny?.color,
          stroke_width: properties.style?.strokeWidth ?? existingWirePropsAny?.stroke_width,
          // preserve existing type if not explicitly changed
          type: newType,
          // merge style_properties shallowly
          style_properties: {
            ...(existingWirePropsAny?.style_properties || {}),
            ...otherStyleProps
          }
        } as any;

        if (hasEdgeLabels) {
          wireProperties.edge_labels = properties.edgeLabels;
          delete wireProperties.edgeLabels;
        }
        
        // Handle stroke_dasharray explicitly: set, remove, or preserve
        if (strokeDasharray !== undefined) {
          // Explicitly set (dashed)
          wireProperties.stroke_dasharray = strokeDasharray;
        } else if (properties.style && ('strokeDasharray' in properties.style)) {
          // Explicit request to remove (solid)
          // ensure it's removed from payload
          // do not set stroke_dasharray key at all → backend preserves or clears? we want clear, so set to empty string
          wireProperties.stroke_dasharray = '';
        } else if ((existingWireProps as any).stroke_dasharray) {
          // Preserve existing if not being changed
          wireProperties.stroke_dasharray = (existingWireProps as any).stroke_dasharray;
        }
        
        // Remove undefined keys to keep payload clean
        Object.keys(wireProperties).forEach((key) => {
          if (wireProperties[key] === undefined) delete wireProperties[key];
        });
        if (wireProperties.style_properties) {
          Object.keys(wireProperties.style_properties).forEach((k) => {
            if (wireProperties.style_properties[k] === undefined) delete wireProperties.style_properties[k];
          });
        }
      }
      
      // Apply local overrides for instant UX
      const { strokeDasharray, ...otherStyleProps } = properties.style || {};
      
      setEdges(prev => prev.map(e => {
        if (e.id === edgeId) {
          const newStyle: any = {
            ...e.style,
            ...otherStyleProps
          };
          if (strokeDasharray !== undefined) {
            newStyle.strokeDasharray = strokeDasharray;
          } else if (properties.style && ('strokeDasharray' in properties.style)) {
            delete newStyle.strokeDasharray;
          }
          
          return {
            ...e,
            style: newStyle,
            data: {
              ...(e.data || {}),
              algorithm: 'algorithm' in properties ? properties.algorithm : e.data?.algorithm,
              edgeLabels: 'edgeLabels' in properties ? properties.edgeLabels : e.data?.edgeLabels,
              wireProperties: {
                ...(e.data?.wireProperties || {}),
                ...(wireProperties || {})
              }
            }
          };
        }
        return e;
      }));

      // Force immediate visual update without deselecting to prevent panel flicker
      if (selectedEdge && selectedEdge.id === edgeId) {
        const newStyle: any = {
          ...selectedEdge.style,
          ...otherStyleProps
        };
        if (strokeDasharray !== undefined) {
          newStyle.strokeDasharray = strokeDasharray;
        } else if (properties.style && ('strokeDasharray' in properties.style)) {
          // remove strokeDasharray for solid
          delete newStyle.strokeDasharray;
        }
        const updatedEdge = {
          ...selectedEdge,
          type: 'editable-edge',
          style: newStyle,
          data: {
            ...(selectedEdge.data || {}),
            algorithm: 'algorithm' in properties ? properties.algorithm : selectedEdge.data?.algorithm,
            edgeLabels: 'edgeLabels' in properties ? properties.edgeLabels : selectedEdge.data?.edgeLabels,
            wireProperties: {
              ...(selectedEdge.data?.wireProperties || {}),
              ...(wireProperties || {})
            }
          }
        } as any;
        setSelectedEdge(updatedEdge);
      }
      
      // Debounce the updateConnection API call to prevent spamming the backend during rapid changes (e.g. color picker, thickness)
      if (edgePropertiesSaveTimersRef.current[edgeId]) {
        window.clearTimeout(edgePropertiesSaveTimersRef.current[edgeId]);
      }
      
      edgePropertiesSaveTimersRef.current[edgeId] = window.setTimeout(() => {
        updateConnectionMutation.mutateAsync({ connectionId, params: { wire_properties: wireProperties } })
          .then(() => {
            // Force immediate cache invalidation for all connection queries
            queryClient.invalidateQueries({ queryKey: ['projectConnections', projectId] });
            if (schemaType === 'terminal' && blockId) queryClient.invalidateQueries({ queryKey: ['connections', blockId] });
            if (shouldShowMainSchemaFeatures) queryClient.invalidateQueries({ queryKey: ['cableTerminalConnections', schemaId] });
          })
          .catch((error) => {
            console.error('❌ Failed to update edge properties:', error);
          });
        delete edgePropertiesSaveTimersRef.current[edgeId];
      }, 500);
    }
  }, [updateConnectionMutation, queryClient, selectedEdge, setSelectedEdge, edges, connectionEdges, projectId, schemaType, blockId, shouldShowMainSchemaFeatures, schemaId]);

  const handleEdgeLabelsUpdate = useCallback((edgeId: string, labels: EdgeLabelData[]) => {
    if (isReadOnly) return;
    updateEdgeProperties(edgeId, { edgeLabels: labels });
  }, [isReadOnly, updateEdgeProperties]);

  handleEdgeLabelsUpdateRef.current = handleEdgeLabelsUpdate;

  // Handle connection start - highlight available target handles - TEMPORARILY COMMENTED
  // const onConnectStart = useCallback((_event: any, _params: any) => {
  //   console.log('🔵 onConnectStart: starting connection, setting isConnecting = true');
  //   setIsConnecting(true);
  // }, []);

  // Handle connection end - remove highlight - TEMPORARILY COMMENTED
  // const onConnectEnd = useCallback((_event: any) => {
  //   console.log('🔵 onConnectEnd: ending connection, setting isConnecting = false');
  //   setIsConnecting(false);
  // }, []);

  // Handle connection creation (create visual connections)
  const onConnect = useCallback(async (params: any) => {
    // Режим только просмотр - запрещено создание соединений
    if (isReadOnly) {
      return;
    }
    
    // DEBUG: Log all connection params (enabled for debugging)
    // Debug logs removed - connection working correctly
    
    // Check if params is a valid ReactFlow connection object
    if (!params || typeof params !== 'object') {
      console.error('❌ onConnect: invalid params type:', typeof params);
      return;
    }
    
    // Check if this is a valid ReactFlow connection
    if (!params.source || !params.target || !params.sourceHandle || !params.targetHandle) {
      console.error('❌ onConnect: missing required connection fields:', {
        source: params.source,
        target: params.target,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle
      });
      return;
    }
    
    // Debug log removed - connection working correctly
    
    if (!params.source || !params.target || !params.sourceHandle || !params.targetHandle) {

      return;
    }
    
    try {
      // Extract connection info from params
      const { source, target, sourceHandle, targetHandle } = params;
      
      // Parse source 
      let sourceDeviceId: number;
      let sourceContactId: number;
      
      if (source === 'terminal-block-1') {
        // Terminal block source with format "deviceInstanceId-contactId-source" in sourceHandle
        const cleanHandle = sourceHandle.replace('-source', '');
        const parts = cleanHandle.split('-');
        if (parts.length === 2) {
          sourceDeviceId = parseInt(parts[0]); // device_instance_id 
          sourceContactId = parseInt(parts[1]); // device_contacts.id
        } else {
          throw new Error(`Invalid terminal handle format: ${sourceHandle}`);
        }
      } else if (source.startsWith('terminal-')) {
        // Terminal block source with format "terminal-instanceId-contactId"
        // Extract instance_id from node ID and contact_id from handle ID
        const nodeParts = source.split('-');
        if (nodeParts.length === 3) {
          sourceDeviceId = parseInt(nodeParts[1]); // instance_id from terminal-1233-3984
          // Extract contact_id from handle ID (format: "contactId-top-source" or "contactId-bottom-source")
          const cleanHandle = sourceHandle.replace('-top-source', '').replace('-bottom-source', '').replace('-top-target', '').replace('-bottom-target', '');
          sourceContactId = parseInt(cleanHandle);
          if (isNaN(sourceContactId)) {
            // Fallback: try to extract from node ID if handle parsing fails
            sourceContactId = parseInt(nodeParts[2]);
          }
        } else {
          throw new Error(`Invalid terminal ID format: ${source}`);
        }
      } else if (source.startsWith('cable-')) {
        // Cable source
        sourceDeviceId = parseInt(source.replace('cable-', ''));
        sourceContactId = parseInt(sourceHandle.replace('-source', '').replace('-target', ''));
      } else {
        sourceDeviceId = parseInt(source);
        sourceContactId = parseInt(sourceHandle.replace('-source', '').replace('-target', ''));
      }
      
      // Validate sourceDeviceId
      if (isNaN(sourceDeviceId)) {
        console.error('❌ Invalid source device ID:', source);
        return;
      }
      
      // Validate sourceContactId
      if (isNaN(sourceContactId)) {
        console.error('❌ Invalid source contact ID:', sourceHandle);
        return;
      }
      
      // Parse target  
      let targetDeviceId: number;
      let targetContactId: number;
      
      if (target === 'terminal-block-1') {
        // Terminal block target with format "deviceInstanceId-contactId" in targetHandle
        const parts = targetHandle.split('-');
        if (parts.length === 2) {
          targetDeviceId = parseInt(parts[0]); // device_instance_id 
          targetContactId = parseInt(parts[1]); // device_contacts.id
        } else {
          throw new Error(`Invalid terminal handle format: ${targetHandle}`);
        }
      } else if (target.startsWith('terminal-')) {
        // Terminal block target with format "terminal-instanceId-contactId"
        // Extract instance_id from node ID and contact_id from handle ID
        const nodeParts = target.split('-');
        if (nodeParts.length === 3) {
          targetDeviceId = parseInt(nodeParts[1]); // instance_id from terminal-1233-3984
          // Extract contact_id from handle ID (format: "contactId-top-target" or "contactId-bottom-target")
          const cleanHandle = targetHandle.replace('-top-source', '').replace('-bottom-source', '').replace('-top-target', '').replace('-bottom-target', '');
          targetContactId = parseInt(cleanHandle);
          if (isNaN(targetContactId)) {
            // Fallback: try to extract from node ID if handle parsing fails
            targetContactId = parseInt(nodeParts[2]);
          }
          // Debug log removed - connection working correctly
        } else {
          throw new Error(`Invalid terminal ID format: ${target}`);
        }
      } else if (target.startsWith('cable-')) {
        // Cable target
        targetDeviceId = parseInt(target.replace('cable-', ''));
        targetContactId = parseInt(targetHandle.replace('-source', '').replace('-target', ''));
        // Debug log removed - connection working correctly
      } else {
        // Other target (parse directly)
        targetDeviceId = parseInt(target);
        targetContactId = parseInt(targetHandle.replace('-source', '').replace('-target', ''));
        // Debug log removed - connection working correctly
      }
      
      // Validate targetDeviceId
      if (isNaN(targetDeviceId)) {
        console.error('❌ Invalid target device ID:', target);
        return;
      }
      
      // Validate targetContactId
      if (isNaN(targetContactId)) {
        console.error('❌ Invalid target contact ID:', targetHandle);
        return;
      }
      

      
      // Check if connection is between autoTerminal nodes
      const isTerminalToTerminal = source.startsWith('terminal-') && target.startsWith('terminal-');
      
      // Create connection data using contact IDs
      const connectionData: any = {
        schema_id: shouldShowMainSchemaFeatures ? Number(schemaId) : (blockId || 2),
        from_instance_id: sourceDeviceId,
        from_contact_id: sourceContactId,
        to_instance_id: targetDeviceId,
        to_contact_id: targetContactId,
        wire_type: 'signal'
      };
      
      // For terminal-to-terminal connections, set default type to 'smoothstep' (Г-образный со скруглёнными углами)
      if (isTerminalToTerminal) {
        connectionData.wire_properties = {
          type: 'smoothstep'
        };
      }
      
      // Debug log removed - connection working correctly
      
      // Не создаём соединения в режиме только просмотр
      if (!isReadOnly) {
        if (PERF_DEBUG) console.log('[PERF] onConnect: calling createConnectionMutation.mutate → refetch will trigger connectionEdges useMemo');
        createConnectionMutation.mutate(connectionData);
      }
      
    } catch (error) {
      // Не логируем ошибки в режиме только просмотр
      if (!isReadOnly) {
        console.error('❌ Error in onConnect:', error);
      }
    }
  }, [createConnectionMutation, blockId, terminalAssignments, isReadOnly, shouldShowMainSchemaFeatures, schemaId]);

  const edgeReconnectSuccessful = useRef(true);

  const onReconnectStart = useCallback(() => {
    edgeReconnectSuccessful.current = false;
  }, []);

  const onReconnect = useCallback((oldEdge: Edge, newConnection: Connection) => {
    edgeReconnectSuccessful.current = true;
    if (isReadOnly) return;

    const connectionId = oldEdge.data?.connectionId as number | undefined;
    if (!connectionId) return;

    deleteConnectionMutation.mutateAsync(connectionId).then(() => {
      onConnect(newConnection);
    }).catch(err => console.error("Error reconnecting edge:", err));
  }, [deleteConnectionMutation, onConnect, isReadOnly]);

  const onReconnectEnd = useCallback((_: MouseEvent | TouchEvent, edge: Edge) => {
    if (!edgeReconnectSuccessful.current && !isReadOnly) {
      const connectionId = edge.data?.connectionId as number | undefined;
      if (connectionId) {
        deleteConnectionMutation.mutateAsync(connectionId).catch(err => console.error("Error deleting edge on drop:", err));
      }
    }
    edgeReconnectSuccessful.current = true;
  }, [deleteConnectionMutation, isReadOnly]);

  // Handle node click for element info panel
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    const perfLog = (label: string, currentNodes: Node[]) => {
      if (PERF_DEBUG && currentNodes.length > 50) {
        const d = performance.now() - (event as any)._perfClickStart;
        if (d > 8) console.log(`[PERF] handleNodeClick ${label}: ${d.toFixed(1)}ms (${currentNodes.length} nodes)`);
      }
    };
    if (PERF_DEBUG) (event as any)._perfClickStart = performance.now();
    // Right click - toggle selection
    if (event.button === 2) {
      event.preventDefault()
      setSelectedNodes(prev => {
        const newSelection = new Set(prev)
        if (newSelection.has(node.id)) {
          newSelection.delete(node.id)
        } else {
          newSelection.add(node.id)
        }
        
        // Update visual state
        setNodes((currentNodes) => {
          perfLog('setNodes(right)', currentNodes);
          return currentNodes.map((n) => ({
            ...n,
            selected: newSelection.has(n.id)
          }))
        })
        
        return newSelection
      })
      return
    }
    
    // Ctrl/Cmd + Left click - toggle selection (multiple selection)
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault()
      setSelectedNodes(prev => {
        const newSelection = new Set(prev)
        if (newSelection.has(node.id)) {
          newSelection.delete(node.id)
        } else {
          newSelection.add(node.id)
        }
        
        // Update visual state
        setNodes((currentNodes) => {
          perfLog('setNodes(ctrl)', currentNodes);
          return currentNodes.map((n) => ({
            ...n,
            selected: newSelection.has(n.id)
          }))
        })
        
        return newSelection
      })
      return
    }
    
    // Left click - select only this node
    setSelectedNodes(new Set([node.id]))
    setSelectedNodeId(node.id)
    
    // Update visual state
    setNodes((currentNodes) => {
      perfLog('setNodes(click)', currentNodes);
      return currentNodes.map((n) => ({
        ...n,
        selected: n.id === node.id
      }))
    })
    
    // Handle terminal schema elements (cables, devices, and terminal instances)
    if (schemaType === 'terminal' && node.data) {
      // Handle terminal instances (which represent terminal blocks)
      if (node.type === 'terminalInstance') {
        const nodeData = node.data as {
          terminalBlock: any;
          assignment: any;
          deviceInstanceId: number;
          position: number;
        };
        
        setSelectedElement({
          id: node.id,
          type: 'terminal',
          deviceInstanceId: nodeData.deviceInstanceId,
          specificationId: nodeData.assignment?.specification_id || 0,
          deviceName: nodeData.terminalBlock?.name || '—',
          specName: nodeData.assignment?.specification?.name || 'Неизвестно',
          description: nodeData.terminalBlock?.description,
          isReserve: false,
          layoutId: nodeData.deviceInstanceId,
          terminalBlockId: nodeData.terminalBlock?.schema_id
        });
        
        // Auto-show the element info panel
        setShowElementInfo(true);
        return;
      }
      
      // Handle cables and devices
      const nodeData = node.data as {
        deviceInstanceId: number;
        specificationId: number;
        deviceName: string;
        specName: string;
        deviceType: string;
        deviceTypeId?: number;
        manufacturer?: string;
        model?: string;
        knxAddress?: string;
        description?: string;
        isReserve?: boolean;
        layoutId?: number;
      };
      
      // Determine element type
      const isCable = nodeData.deviceType === 'cable';
      
      setSelectedElement({
        id: node.id,
        type: isCable ? 'cable' : 'device',
        deviceInstanceId: nodeData.deviceInstanceId,
        specificationId: nodeData.specificationId,
        deviceName: nodeData.deviceName || '—',
        specName: nodeData.specName || 'Неизвестно',
        deviceTypeId: nodeData.deviceTypeId,
        manufacturer: nodeData.manufacturer,
        model: nodeData.model,
        knxAddress: nodeData.knxAddress,
        description: nodeData.description,
        isReserve: nodeData.isReserve,
        layoutId: nodeData.layoutId // Add layoutId for terminal schema removal
      });
      
      // Auto-show the element info panel
      setShowElementInfo(true);
    }
    
    // Handle main and breaker schema devices, cables, and auto terminals
    if (shouldShowMainSchemaFeatures && node.data) {
      // Handle auto terminals (terminal blocks on main schema)
      if (node.type === 'autoTerminal') {
        const nodeData = node.data as {
          terminal_instance_id: number;
          terminal_block_id: number;
          terminal_block_name: string;
          terminal_block_description?: string;
          terminal_position_in_block: number;
          terminal_contact_id: number;
          terminal_opposite_contact_id: number;
          terminal_contact_label: string;
          terminal_opposite_contact_label: string;
          terminal_specification_id: number;
          cable_instance_id: number;
          cable_contact_id: number;
          connection_id: number;
          source_schema_id: number;
          cable_contact_number: string;
        };
        
        const nextSelected = {
          id: node.id,
          type: 'terminal',
          deviceInstanceId: nodeData.terminal_block_id, // first line: TB id
          specificationId: nodeData.terminal_specification_id,
          deviceName: nodeData.terminal_block_name || '—',
          specName: String(nodeData.terminal_instance_id), // second line: device_instances id
          description: `Терминал №${nodeData.terminal_position_in_block}`,
          isReserve: false,
          terminalBlockId: nodeData.source_schema_id,
          terminalBlockDescription: nodeData.terminal_block_description || '',
          terminalInstanceId: nodeData.terminal_instance_id
        } as typeof selectedElement extends (infer T) ? T : any;
        setSelectedElement(nextSelected);
        
        // Auto-show the element info panel
        setShowElementInfo(true);
        return;
      }
      
      // Handle regular devices and cables
      const nodeData = node.data as {
        deviceInstanceId: number;
        specificationId: number;
        deviceName: string;
        specName: string;
        deviceType: string;
        deviceTypeId?: number;
        manufacturer?: string;
        model?: string;
        knxAddress?: string;
        description?: string;
        isReserve?: boolean;
      };
      
      // Determine element type
      const isCable = nodeData.deviceType === 'cable' || nodeData.deviceType === 'Кабель';
      
      setSelectedElement({
        id: node.id,
        type: isCable ? 'cable' : 'device',
        deviceInstanceId: nodeData.deviceInstanceId,
        specificationId: nodeData.specificationId,
        deviceName: nodeData.deviceName || '—',
        specName: nodeData.specName || 'Неизвестно',
        deviceTypeId: nodeData.deviceTypeId,
        manufacturer: nodeData.manufacturer,
        model: nodeData.model,
        knxAddress: nodeData.knxAddress,
        description: nodeData.description,
        isReserve: nodeData.isReserve
      });
      
      // Auto-show the element info panel
      setShowElementInfo(true);
    }
  }, [schemaType]);

  // Update nodes to mark selected node — только меняем ноды, у которых реально изменился selected (1–2 ноды вместо 1251)
  useEffect(() => {
    const t0 = PERF_DEBUG ? performance.now() : 0;
    setNodes((currentNodes) => {
      let changed = false;
      const next = currentNodes.map((node) => {
        const want = node.id === selectedNodeId;
        if (!!node.selected === want) return node;
        changed = true;
        return { ...node, selected: want };
      });
      if (PERF_DEBUG && t0 && currentNodes.length > 50 && changed) {
        const d = performance.now() - t0;
        if (d > 8) console.log(`[PERF] useEffect(selectedNodeId) setNodes(map): ${d.toFixed(1)}ms (${currentNodes.length} nodes)`);
      }
      return changed ? next : currentNodes;
    });
  }, [selectedNodeId, setNodes]);

  // Clear node selection when element info panel is closed
  useEffect(() => {
    if (!showElementInfo) {
      setSelectedNodeId(null);
    }
  }, [showElementInfo]);

  // Handle edge click for selection
  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.stopPropagation();
    const isAdd = event.shiftKey || event.ctrlKey || event.metaKey;
    setSelectedEdge(edge);
    setSelectedEdges(prev => isAdd ? (prev.some(e => e.id === edge.id) ? prev : [...prev, edge]) : [edge]);
    
    setSelectedNodes(new Set());
    // Снимаем выделение с нод — обновляем только те, у которых selected: true (обычно 0–1 нода)
    setNodes((currentNodes) => {
      const needUpdate = currentNodes.some(n => n.selected);
      if (!needUpdate) return currentNodes;
      const t0 = PERF_DEBUG ? performance.now() : 0;
      const next = currentNodes.map(n => n.selected ? { ...n, selected: false } : n);
      if (PERF_DEBUG && t0 && currentNodes.length > 50) {
        const d = performance.now() - t0;
        if (d > 8) console.log(`[PERF] onEdgeClick setNodes(clear): ${d.toFixed(1)}ms (${currentNodes.length} nodes)`);
      }
      return next;
    });
  }, [setNodes]);

  // Handle selection change from ReactFlow
  const lastEdgeIdsRef = React.useRef<string>("");
  const lastNodeIdsRef = React.useRef<string>("");
  const onSelectionChange = useCallback(({ nodes, edges }: { nodes: Node[], edges: Edge[] }) => {
    const newEdgeIdsKey = edges.map(e => e.id).join('|');
    const newNodeIdsKey = nodes.map(n => n.id).join('|');
    if (newEdgeIdsKey === lastEdgeIdsRef.current && newNodeIdsKey === lastNodeIdsRef.current) {
      return;
    }
    lastEdgeIdsRef.current = newEdgeIdsKey;
    lastNodeIdsRef.current = newNodeIdsKey;
    
    setSelectedNodes(new Set(nodes.map(n => n.id)));
    
    const t0 = PERF_DEBUG ? performance.now() : 0;
    const selectedIds = new Set(nodes.map(n => n.id));
    setNodes(prevNodes => {
      let changed = false;
      const next = prevNodes.map(node => {
        const want = selectedIds.has(node.id);
        if (!!node.selected === want) return node;
        changed = true;
        return { ...node, selected: want };
      });
      if (PERF_DEBUG && t0 && prevNodes.length > 50 && changed) {
        const d = performance.now() - t0;
        if (d > 8) console.log(`[PERF] onSelectionChange setNodes(map): ${d.toFixed(1)}ms (${prevNodes.length} nodes)`);
      }
      return changed ? next : prevNodes;
    });
  }, [setNodes]);

  // Removed built-in selection callbacks: using custom overlay selection only

  // Вспомогательная функция: пересекает ли отрезок прямоугольник
  function intersectsRectLine(rect: DOMRect, x1: number, y1: number, x2: number, y2: number): boolean {
    // Один из концов внутри
    if (
      x1 >= rect.left && x1 <= rect.right && y1 >= rect.top && y1 <= rect.bottom ||
      x2 >= rect.left && x2 <= rect.right && y2 >= rect.top && y2 <= rect.bottom
    ) return true;
    // Проверяем пересечение с каждой стороной прямоугольника
    const lines = [
      [rect.left, rect.top, rect.right, rect.top], // top
      [rect.right, rect.top, rect.right, rect.bottom], // right
      [rect.right, rect.bottom, rect.left, rect.bottom], // bottom
      [rect.left, rect.bottom, rect.left, rect.top], // left
    ];
    for (const [rx1, ry1, rx2, ry2] of lines) {
      if (segmentsIntersect(x1, y1, x2, y2, rx1, ry1, rx2, ry2)) return true;
    }
    return false;
  }
  // Алгоритм пересечения двух отрезков
  function segmentsIntersect(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, x4: number, y4: number): boolean {
    function ccw(ax: number, ay: number, bx: number, by: number, cx: number, cy: number) {
      return (cy - ay) * (bx - ax) > (by - ay) * (cx - ax);
    }
    return (
      ccw(x1, y1, x3, y3, x4, y4) !== ccw(x2, y2, x3, y3, x4, y4) &&
      ccw(x1, y1, x2, y2, x3, y3) !== ccw(x1, y1, x2, y2, x4, y4)
    );
  }

  // Custom box selection overlay handlers (relative to flowWrapperRef)
  useEffect(() => {
    if (!USE_CUSTOM_SELECTION_OVERLAY) return;
    if (!flowWrapperRef.current) return;
    const containerEl = flowWrapperRef.current;
    let startX = 0, startY = 0;
    let dragging = false;
    let shiftPressed = false;
    const previewScheduledRef = { current: false } as { current: boolean };
    const lastRectRef = { current: null as null | { left: number; right: number; top: number; bottom: number } };
    const getRelative = (e: MouseEvent) => {
      const rect = containerEl.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    // Live preview helper: highlight edges under current selection rect
    const applyPreview = (rect: { left: number; right: number; top: number; bottom: number }) => {
      const pathNodes = containerEl.querySelectorAll('.react-flow__edge-path');
      const cBox = containerEl.getBoundingClientRect();
      const previewIds: string[] = [];
      pathNodes.forEach((p: Element) => {
        const edgeWrapper = p.closest('.react-flow__edge') as HTMLElement | null;
        if (!edgeWrapper) return;
        const edgeId = edgeWrapper.getAttribute('data-id');
        if (!edgeId) return;
        const pBoxAbs = (p as HTMLElement).getBoundingClientRect();
        const left = pBoxAbs.left - cBox.left;
        const right = pBoxAbs.right - cBox.left;
        const top = pBoxAbs.top - cBox.top;
        const bottom = pBoxAbs.bottom - cBox.top;
        // Early reject by bbox
        let intersects = !(right <= rect.left || left >= rect.right || bottom <= rect.top || top >= rect.bottom);
        // If bbox intersects but path is very thin, refine by sparse sampling (max 30 points)
        if (intersects && (p instanceof SVGPathElement)) {
          try {
            const total = (p as SVGPathElement).getTotalLength();
            const samples = Math.min(30, Math.max(8, Math.floor(total / 20)));
            intersects = false;
            for (let i = 0; i <= samples; i++) {
              const d = (total * i) / samples;
              const pt = (p as SVGPathElement).getPointAtLength(d);
              const px = pt.x + pBoxAbs.left - cBox.left;
              const py = pt.y + pBoxAbs.top - cBox.top;
              if (px > rect.left && px < rect.right && py > rect.top && py < rect.bottom) { intersects = true; break; }
            }
          } catch {}
        }
        if (intersects && edgeId) previewIds.push(edgeId);
      });
      // Apply preview classes
      containerEl.querySelectorAll('.react-flow__edge').forEach(el => el.classList.remove('selected-preview'));
      previewIds.forEach(id => {
        const el = containerEl.querySelector(`[data-id="${id}"]`);
        if (el) el.classList.add('selected-preview');
      });
    };
    const onMouseDown = (e: any) => {
      const target = e.target as HTMLElement;
      if (e.button !== 2) return; // Only right mouse button for selection
      // Block only when starting from handle (connection gesture). Allow nodes/edges/pane.
      if (target.closest('.react-flow__handle')) return;

      dragging = true;
      shiftPressed = e.shiftKey;
      const { x, y } = getRelative(e);
      startX = x; startY = y;
      previewCacheRef.current = null; // reset preview cache at drag start
      lastPreviewIdsRef.current = new Set();
      setSelectionRect({ x1: x, y1: y, x2: x, y2: y, active: true });
      document.addEventListener('mousemove', onMouseMove as any, true);
      document.addEventListener('mouseup', onMouseUp as any, true);
      document.addEventListener('pointermove', onMouseMove as any, true);
      document.addEventListener('pointerup', onMouseUp as any, true);
    };
    const onMouseMove = (e: any) => {
      if (!dragging) return;
      const { x, y } = getRelative(e);
      setSelectionRect(prev => ({ ...prev, x2: x, y2: y }));
      // Live preview while dragging (throttled by rAF)
      lastRectRef.current = { left: Math.min(startX, x), right: Math.max(startX, x), top: Math.min(startY, y), bottom: Math.max(startY, y) };
      if (!previewScheduledRef.current) {
        previewScheduledRef.current = true;
        window.requestAnimationFrame(() => {
          previewScheduledRef.current = false;
          if (lastRectRef.current) applyPreview(lastRectRef.current);
        });
      }
    };
    const onMouseUp = (e: any) => {
      if (!dragging) return;
      dragging = false;
      const { x, y } = getRelative(e);
      setSelectionRect(prev => ({ ...prev, x2: x, y2: y, active: false }));
      document.removeEventListener('mousemove', onMouseMove as any, true);
      document.removeEventListener('mouseup', onMouseUp as any, true);
      document.removeEventListener('pointermove', onMouseMove as any, true);
      document.removeEventListener('pointerup', onMouseUp as any, true);
      // Clear preview classes; final selection will be applied separately
      containerEl.querySelectorAll('.react-flow__edge').forEach(el => el.classList.remove('selected-preview'));
      const rect = { left: Math.min(startX, x), right: Math.max(startX, x), top: Math.min(startY, y), bottom: Math.max(startY, y) } as DOMRect;
      // Skip tiny drags
      if (rect.right - rect.left < 3 && rect.bottom - rect.top < 3) return;

      // 1) Nodes by bounding box
      const selectedNodeIds: string[] = [];
      const currentNodes = nodesRef.current;
      currentNodes.forEach(node => {
        const el = document.querySelector(`[data-id="${node.id}"]`);
        if (!el) return;
        const elBox = el.getBoundingClientRect();
        const cBox = containerEl.getBoundingClientRect();
        const left = elBox.left - cBox.left;
        const right = elBox.right - cBox.left;
        const top = elBox.top - cBox.top;
        const bottom = elBox.bottom - cBox.top;
        if (right > rect.left && left < rect.right && bottom > rect.top && top < rect.bottom) {
          selectedNodeIds.push(node.id);
        }
      });
      // 2a) Edges by path bounding box intersection (fast path)
      const edgesToAdd: Edge[] = [];
      const pathNodes = containerEl.querySelectorAll('.react-flow__edge-path');
      pathNodes.forEach((p: Element) => {
        const edgeWrapper = p.closest('.react-flow__edge') as HTMLElement | null;
        if (!edgeWrapper) return;
        const edgeId = edgeWrapper.getAttribute('data-id');
        if (!edgeId) return;
        const pBoxAbs = (p as HTMLElement).getBoundingClientRect();
        const cBox = containerEl.getBoundingClientRect();
        const left = pBoxAbs.left - cBox.left;
        const right = pBoxAbs.right - cBox.left;
        const top = pBoxAbs.top - cBox.top;
        const bottom = pBoxAbs.bottom - cBox.top;
        let intersects = right > rect.left && left < rect.right && bottom > rect.top && top < rect.bottom;
        // If bbox doesn't intersect (thin curves), sample the path points for intersection
        if (!intersects && (p instanceof SVGPathElement)) {
          try {
            const total = (p as SVGPathElement).getTotalLength();
            const step = Math.max(4, Math.floor(total / 50));
            for (let d = 0; d <= total; d += step) {
              const pt = (p as SVGPathElement).getPointAtLength(d);
              const px = pt.x + pBoxAbs.left - cBox.left;
              const py = pt.y + pBoxAbs.top - cBox.top;
              if (px > rect.left && px < rect.right && py > rect.top && py < rect.bottom) {
                intersects = true;
                break;
              }
            }
          } catch {}
        }
        if (intersects) {
          const edge = edgesRef.current.find((e: Edge) => e.id === edgeId);
          if (edge && !edgesToAdd.some(e => e.id === edge.id)) {
            edgesToAdd.push(edge);
          }
        }
      });

      // 2b) Fallback: Edges by segment-rect intersection using handles (more precise)
      edgesRef.current.forEach(edge => {
        const sHandleId = `${edge.sourceHandle}`;
        const tHandleId = `${edge.targetHandle}`;
        const sNodeId = edge.source; const tNodeId = edge.target;
        const sHandle = document.querySelector(`[data-nodeid="${sNodeId}"][data-handleid="${sHandleId}"]`);
        const tHandle = document.querySelector(`[data-nodeid="${tNodeId}"][data-handleid="${tHandleId}"]`);
        if (!sHandle || !tHandle) return;
        const sBox = (sHandle as HTMLElement).getBoundingClientRect();
        const tBox = (tHandle as HTMLElement).getBoundingClientRect();
        const cBox = containerEl.getBoundingClientRect();
        const sx = (sBox.left + sBox.right) / 2 - cBox.left;
        const sy = (sBox.top + sBox.bottom) / 2 - cBox.top;
        const tx = (tBox.left + tBox.right) / 2 - cBox.left;
        const ty = (tBox.top + tBox.bottom) / 2 - cBox.top;
        if (intersectsRectLine(rect, sx, sy, tx, ty)) {
          if (!edgesToAdd.some(e => e.id === edge.id)) {
            edgesToAdd.push(edge);
          }
        }
      });
      // Apply selection (nodes + edges) and sync visual state
      const nextSelectedNodeIds = shiftPressed
        ? new Set<string>([...Array.from(selectedNodes), ...selectedNodeIds])
        : new Set<string>(selectedNodeIds);

      const nextSelectedEdges = shiftPressed
        ? [...selectedEdges, ...edgesToAdd.filter(e => !selectedEdges.some(pe => pe.id === e.id))]
        : edgesToAdd;

      setSelectedNodes(nextSelectedNodeIds);
      setSelectedEdges(nextSelectedEdges);

      const t0 = PERF_DEBUG ? performance.now() : 0;
      // Update ReactFlow node.selected for built-in selected styles
      setNodes((current) => {
        const next = current.map((n) => ({
          ...n,
          selected: nextSelectedNodeIds.has(n.id)
        }));
        if (PERF_DEBUG && t0 && current.length > 50) {
          const d = performance.now() - t0;
          if (d > 8) console.log(`[PERF] box selection setNodes(map): ${d.toFixed(1)}ms (${current.length} nodes)`);
        }
        return next;
      });

      // Visual class toggle for edges (built-in styles are tied to internal selection, so add CSS class)
      setTimeout(() => {
        document.querySelectorAll('.react-flow__edge').forEach(el => el.classList.remove('selected'));
        nextSelectedEdges.forEach(e => {
          const el = document.querySelector(`[data-id="${e.id}"]`);
          if (el) el.classList.add('selected');
        });
      }, 10);
    };
    document.addEventListener('mousedown', onMouseDown as EventListener, true);
    document.addEventListener('pointerdown', onMouseDown as EventListener, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown as EventListener, true);
      document.removeEventListener('pointerdown', onMouseDown as EventListener, true);
    };
  }, []);

  // Sync edgesRef when edges change (must be after connectionEdges declaration)
  useEffect(() => {
    edgesRef.current = connectionEdges || [];
  }, [connectionEdges]);

  // Removed ReactFlow selection drag stop handler (custom overlay covers this)

  // Handle keyboard events for edge and node deletion
  const onKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't handle Delete key if any modal is open
    if (showInstanceEditModal || showSpecEditModal) {
      return;
    }
    
    if (event.key === 'Delete') {
      event.preventDefault();
      
      // Delete selected edges
      if (selectedEdges.length > 0) {
        selectedEdges.forEach(edge => {
          // Parse different edge ID formats
          let numericId: number;
          if (edge.id.startsWith('connection-')) {
            numericId = parseInt(edge.id.replace('connection-', ''));
          } else if (edge.id.startsWith('device-terminal-')) {
            numericId = parseInt(edge.id.replace('device-terminal-', ''));
          } else if (edge.id.startsWith('auto-terminal-')) {
            numericId = parseInt(edge.id.replace('auto-terminal-', ''));
          } else {
            // Try to parse as is
            numericId = parseInt(edge.id);
          }
          
          if (!isNaN(numericId)) {
            deleteConnectionMutation.mutateAsync(numericId);
          }
        });
        
        // Clear edge selection after deletion
        setSelectedEdges([]);
        setSelectedEdge(null);
        setActivePopup(null);
      }
      
             // Delete selected nodes
       if (selectedNodes.size > 0) {
         
         // Delete selected nodes (remove from schema)
         const nodeDeletePromises = Array.from(selectedNodes).map(async nodeId => {
           const node = nodes.find(n => n.id === nodeId);
           if (!node) return Promise.resolve();
           
           // Remove from main schema using the same logic as removeFromMainSchema
           if (node.data && typeof node.data.deviceInstanceId === 'number') {
             const deviceInstanceId = node.data.deviceInstanceId;
             
             try {
               // Get device type from API before removal
               const { getDeviceInstanceById } = await import('../../api/deviceInstances');
               const deviceInstance = await getDeviceInstanceById(deviceInstanceId);
               const isCable = deviceInstance?.device_type === 'cable' || deviceInstance?.device_type === 'Кабель';
               
               // Remove device node from canvas immediately
               setNodes(prevNodes => prevNodes.filter(n => n.id !== nodeId));
               
               // Immediately update local canvas state based on device type
               if (isCable) {
                 setCablesOnCanvas(prev => {
                   const updated = new Set(prev);
                   updated.delete(deviceInstanceId);
                   return updated;
                 });
               } else {
                 setDevicesOnCanvas(prev => {
                   const updated = new Set(prev);
                   updated.delete(deviceInstanceId);
                   return updated;
                 });
               }
               
               // Delete all connections for this device in main schema
               if (schemaId) {
                 try {
                   const { deleteDeviceConnections } = await import('../../api/connections');
                   await deleteDeviceConnections(deviceInstanceId, Number(schemaId));
                 } catch (connectionError) {
                   console.error('❌ Error deleting device connections:', connectionError);
                 }
               }
               
               // Handle removal based on schema type - ALL schemas now use schema_layout deletion
               // Find layoutId from schemaLayoutResponse for the current schema
               const layoutItem = schemaLayoutResponse?.data?.find(item => item.device_instance_id === deviceInstanceId);
               
               if (layoutItem?.id) {
                 const { removeDeviceFromSchema } = await import('../../api/schemas');
                 await removeDeviceFromSchema(layoutItem.id);
                 
                 // Refresh schema layout and library data
                 queryClient.invalidateQueries({ queryKey: ['schemaLayout', schemaType, schemaId] });
                 queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId, electricalPanelId, schemaType, schemaId] });
                 queryClient.invalidateQueries({ queryKey: ['availableCablesMain', projectId, electricalPanelId, schemaType, schemaId] });
               } else {
                 console.warn('⚠️ No layout item found for device:', deviceInstanceId, 'in schema:', schemaId);
                 
                 // Fallback: force immediate refresh of appropriate library data based on device type
                 if (isCable) {
                   queryClient.refetchQueries({ queryKey: ['availableCablesMain', projectId] });
                 } else {
                   queryClient.refetchQueries({ queryKey: ['availableDevices', projectId] });
                 }
               }
               if (schemaId) {
                 queryClient.invalidateQueries({ queryKey: ['mainConnections', schemaId] });
               }
               
             } catch (error) {
               console.error('❌ Error removing device from main schema:', error);
             }
           }
           
           return Promise.resolve();
         });

         Promise.all(nodeDeletePromises).then(() => {
           // Clear node selection after deletion
           setSelectedNodes(new Set());
         }).catch(error => {
           console.error('Error during node deletion:', error);
         });
       }
    }
  }, [selectedEdges, selectedNodes, nodes, deleteConnectionMutation, schemaLayoutResponse, queryClient, projectId, schemaId, schemaType, electricalPanelId, showInstanceEditModal, showSpecEditModal]);

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onKeyDown]);

  // Handle bulk delete of selected elements
  const handleBulkDelete = useCallback(async () => {
    if (selectedNodes.size === 0 && selectedEdges.length === 0) return;

    // Delete selected edges
    const edgeDeletePromises = selectedEdges.map(edge => {
      let numericId: number;
      if (edge.id.startsWith('connection-')) {
        numericId = parseInt(edge.id.replace('connection-', ''));
      } else if (edge.id.startsWith('device-terminal-')) {
        numericId = parseInt(edge.id.replace('device-terminal-', ''));
      } else if (edge.id.startsWith('auto-terminal-')) {
        numericId = parseInt(edge.id.replace('auto-terminal-', ''));
      } else {
        numericId = parseInt(edge.id);
      }
      
      if (!isNaN(numericId)) {
        return deleteConnectionMutation.mutateAsync(numericId);
      }
      return Promise.resolve();
    });

    // Delete selected nodes (remove from schema)
    const nodeDeletePromises = Array.from(selectedNodes).map(nodeId => {
      const node = nodes.find(n => n.id === nodeId);
      if (!node) return Promise.resolve();
      
      // Remove from main schema
      if (node.data && typeof node.data.deviceInstanceId === 'number') {
        return updateDeviceMainSchemaStatus(node.data.deviceInstanceId, false);
      }
      
      return Promise.resolve();
    });

    try {
      await Promise.all([...edgeDeletePromises, ...nodeDeletePromises]);
      
      // Clear selection
      setSelectedNodes(new Set());
      setSelectedEdges([]);
      setSelectedEdge(null);
      setActivePopup(null);
    } catch (error) {
      console.error('Error during bulk delete:', error);
    }
  }, [selectedNodes, selectedEdges, nodes, deleteConnectionMutation]);





  // Handle node drag start - save initial positions for group movement
  const handleNodeDragStart = useCallback((_event: React.MouseEvent, node: Node) => {
    // Режим только просмотр - запрещено перемещение блоков
    if (isReadOnly) {
      return;
    }
    // If this is a group movement, save initial positions of all selected nodes
    if (selectedNodes.size > 1 && selectedNodes.has(node.id)) {
      const positions = new Map<string, { x: number; y: number }>();
      nodes.forEach(n => {
        if (selectedNodes.has(n.id)) {
          positions.set(n.id, { x: n.position.x, y: n.position.y });
        }
      });
      setInitialNodePositions(positions);
    }
  }, [selectedNodes, nodes, isReadOnly]);

  // Handle node drag - убираем, так как резкое примагничивание работает через enhancedOnNodesChange
  const handleNodeDrag = useCallback((_event: React.MouseEvent, _node: Node) => {
    // Убираем логику - резкое примагничивание работает через enhancedOnNodesChange
  }, []);

  // Резкое примагничивание - сразу применяем snapped позицию к ноде
  const enhancedOnNodesChange = useCallback((changes: any[]) => {
    // Look for node position changes during drag
    const positionChanges = changes.filter(change => 
      change.type === 'position' && 
      change.dragging && 
      isGridSnappingEnabled
    );
    
    if (positionChanges.length > 0) {
      const change = positionChanges[0];
      if (change.position) {
        const snappedPosition = snapToGrid(change.position.x, change.position.y);
        const t0 = PERF_DEBUG ? performance.now() : 0;
        // Сразу применяем примагниченную позицию к ноде
        setNodes(prevNodes => {
          const next = prevNodes.map(n => 
            n.id === change.id 
              ? { ...n, position: snappedPosition }
              : n
          );
          if (PERF_DEBUG && t0 && prevNodes.length > 50) {
            const d = performance.now() - t0;
            if (d > 8) console.log(`[PERF] enhancedOnNodesChange setNodes(drag): ${d.toFixed(1)}ms (${prevNodes.length} nodes)`);
          }
          return next;
        });
        
        // Обновляем позицию в change для дальнейшей обработки
        change.position = snappedPosition;
      }
    }
    
    // Call the original onNodesChange
    // Filter out meaningless position changes
    const meaningfulChanges = changes.filter(change => {
      if (change.type === 'position' && !change.dragging && !change.position) return false;
      return true;
    });
    
    if (meaningfulChanges.length > 0) {
      originalOnNodesChange(meaningfulChanges);
    }
  }, [originalOnNodesChange, isGridSnappingEnabled, snapToGrid, setNodes]);



  // Handle node drag stop
  const handleNodeDragStop = useCallback(async (_event: React.MouseEvent, node: Node) => {
    // Режим только просмотр - запрещено перемещение блоков
    if (isReadOnly) {
      return;
    }
    // Убираем очистку состояний - они больше не нужны при резком примагничивании
    // Apply grid snapping to node position
    const snappedPosition = snapToGrid(node.position.x, node.position.y);
    if (snappedPosition.x !== node.position.x || snappedPosition.y !== node.position.y) {
      setNodes(prevNodes => 
        prevNodes.map(n => 
          n.id === node.id 
            ? { ...n, position: snappedPosition }
            : n
        )
      );
      // Update node position for further processing
      node.position = snappedPosition;
    }
    // Handle group movement - if multiple nodes are selected, move all of them
    if (selectedNodes.size > 1 && selectedNodes.has(node.id)) {
      const initialPositions = initialNodePositions;
      if (initialPositions.size === 0) return;
      
      // Get the initial position of the dragged node
      const draggedNodeInitialPos = initialPositions.get(node.id);
      if (!draggedNodeInitialPos) return;
      
      // Calculate the delta movement
      const deltaX = node.position.x - draggedNodeInitialPos.x;
      const deltaY = node.position.y - draggedNodeInitialPos.y;
      
      // Move all selected nodes by the same delta
      setNodes((currentNodes) =>
        currentNodes.map((n) => {
          if (selectedNodes.has(n.id)) {
            const initialPos = initialPositions.get(n.id);
            if (initialPos) {
              const newPosition = {
                x: Math.round(initialPos.x + deltaX),
                y: Math.round(initialPos.y + deltaY)
              };
              const snappedPosition = snapToGrid(newPosition.x, newPosition.y);
              return {
                ...n,
                position: snappedPosition
              }
            }
          }
          return n
        })
      )
      
      // Save positions for all selected nodes - пропускаем в режиме только просмотр
      if (!isReadOnly) {
        const savePromises = Array.from(selectedNodes).map(async (nodeId) => {
          const initialPos = initialPositions.get(nodeId);
          if (!initialPos) return;
          
          // Calculate the new position using the same delta and apply grid snapping
          const newPosition = {
            x: Math.round(initialPos.x + deltaX),
            y: Math.round(initialPos.y + deltaY)
          };
          const snappedPosition = snapToGrid(newPosition.x, newPosition.y);
          const newX = snappedPosition.x;
          const newY = snappedPosition.y;
          
          // Find the node data to determine its type
          const selectedNode = nodes.find(n => n.id === nodeId);
          if (!selectedNode) return;
          
          else if (selectedNode.id.startsWith('cable-') && blockId) {
            const cableData = selectedNode.data as { deviceInstanceId: number; contacts: any[] }
            if (cableData && cableData.deviceInstanceId) {
              try {
                const schemaId = blockId
                await updateDevicePositionByIds(
                  schemaId,
                  cableData.deviceInstanceId,
                  newX,
                  newY
                )
              } catch (error) {
                // Тихая обработка ошибок в режиме только просмотр
                if (!isReadOnly) {
                  console.error('❌ Error saving cable position:', error)
                }
              }
            }
          } else if (shouldShowMainSchemaFeatures && schemaId && selectedNode.data?.deviceInstanceId) {
            try {
              const deviceInstanceId = selectedNode.data.deviceInstanceId as number
              await updateDevicePositionWithIsolation(
                schemaType as SchemaType,
                schemaId,
                deviceInstanceId,
                newX,
                newY
              )
            } catch (error) {
              // Тихая обработка ошибок в режиме только просмотр
              if (!isReadOnly) {
                console.error('❌ Error saving device position:', error)
              }
            }
          }
        })
        
        await Promise.all(savePromises)
      }
      
      // Clear initial positions after group movement
      setInitialNodePositions(new Map())
      return
    }
    
    // Single node movement (original logic) - пропускаем в режиме только просмотр
    if (!isReadOnly) {
      if (node.id.startsWith('cable-') && blockId) {
        // Handle cable position update for terminal schema
        const cableData = node.data as { deviceInstanceId: number; contacts: any[] };
        
        if (cableData && cableData.deviceInstanceId) {
          try {
            const schemaId = blockId;
            
            await updateDevicePositionByIds(
              schemaId, // schema_id (терминальная схема)
              cableData.deviceInstanceId, // device_instance_id
              Math.round(node.position.x),
              Math.round(node.position.y)
            );
          } catch (error) {
            // Тихая обработка ошибок в режиме только просмотр
            if (!isReadOnly) {
              console.error('❌ Error saving cable position:', error);
            }
          }
        }
      } else if (shouldShowMainSchemaFeatures && schemaId && node.data?.deviceInstanceId) {
        // Handle device position update for main schema with schema_type isolation
        try {
          const deviceInstanceId = node.data.deviceInstanceId as number;
          // NEW: Use isolated API function with schema_type
          await updateDevicePositionWithIsolation(
            schemaType as SchemaType, // schema_type для изоляции
            schemaId, // schema_id (основная схема)
            deviceInstanceId, // device_instance_id
            Math.round(node.position.x),
            Math.round(node.position.y)
          );
          // Device position saved to database with isolation
          
          // If this is a cable being dragged, we've already updated terminal positions in real-time
          // Terminal positions are automatically calculated relative to cable position,
          // so no need to save them separately to database
        } catch (error) {
          // Тихая обработка ошибок в режиме только просмотр
          if (!isReadOnly) {
            console.error('❌ Error saving device position:', error);
          }
        }
      }
    }
  }, [blockId, schemaType, schemaId, terminalBlock, selectedNodes, nodes, setNodes, isReadOnly, shouldShowMainSchemaFeatures]);

  // Handle electrical panel requirement for terminal schemas
  useEffect(() => {
    if (config.electricalPanelRequired && !selectedElectricalPanel && electricalPanels?.length) {
      // Auto-select first panel or show panel selector
      setSelectedElectricalPanel(electricalPanels[0].id);
    }
  }, [config.electricalPanelRequired, selectedElectricalPanel, electricalPanels]);
  

  

  
  // Special minimal layout for terminal schemas
  if (schemaType === 'terminal' && blockId) {
    return (
      <div className="schema-editor-shell h-screen flex flex-col bg-gray-100">
      {schemaEditorHeader}
      <div className="terminal-schema-editor flex flex-1 overflow-hidden min-h-0">
        {/* Narrow left toolbar - ALWAYS VISIBLE */}
        <div className="toolbar-panel bg-gray-800 w-16 flex flex-col items-center py-4 space-y-4">
          {/* Library Icon - different for each schema type */}
          <button
            onClick={() => !isReadOnly && setShowCableLibrary(!showCableLibrary)}
            disabled={isReadOnly}
            className={`w-12 h-12 rounded-lg flex items-center justify-center text-white transition-colors ${
              showCableLibrary ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
            } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={isReadOnly ? 'Режим только просмотр - добавление элементов запрещено' : (schemaType === 'terminal' ? 'Библиотека кабелей' : 'Библиотека элементов основной схемы')}
          >
            {schemaType === 'terminal' ? '🔌' : '📚'}
          </button>
          
          {/* Grid Snapping Tool */}
          <div className="relative">
            <button
              onClick={() => {
                console.log('🔗 Grid snapping toggle clicked');
                        setIsGridSnappingEnabled((prev: boolean) => {
          const newValue = !prev;
          console.log('📊 Grid snapping enabled:', newValue);
          return newValue;
        });
                // Показываем панельку при клике
                setShowGridSettings(true);
              }}
              onMouseEnter={() => setShowGridSettings(true)}
              onMouseLeave={() => {
                // Добавляем задержку перед скрытием панельки
                setTimeout(() => {
                  setShowGridSettings(false);
                }, 4000);
              }}
              className={`w-12 h-12 rounded-lg flex items-center justify-center text-white transition-colors ${
                isGridSnappingEnabled 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-gray-600 hover:bg-gray-500'
              }`}
              title="Примагничивание к сетке"
            >
              🔗
            </button>
            
            {/* Grid Settings Popup */}
            {showGridSettings && (
              <div 
                className="grid-settings-popup absolute left-full ml-2 top-0 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 min-w-48"
                onMouseEnter={() => setShowGridSettings(true)}
                onMouseLeave={() => setShowGridSettings(false)}
              >
                <div className="text-sm font-medium text-gray-700 mb-2">Шаг сетки:</div>
                <div className="space-y-2">
                  {[10, 20, 30, 50, 100].map((step) => (
                    <button
                      key={step}
                      onClick={() => setGridStep(step)}
                      className={`w-full px-2 py-1 text-xs rounded ${
                        gridStep === step 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      {step}px
                    </button>
                  ))}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Текущий шаг: {gridStep}px
                </div>
              </div>
            )}
          </div>
          
          {/* Search Tool */}
          <button
            onClick={() => setShowSearchPanel(!showSearchPanel)}
            className={`w-12 h-12 ${showSearchPanel ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-500'} rounded-lg flex items-center justify-center text-white transition-colors`}
            title="Поиск"
          >
            🔍
          </button>
          
          {/* Element Info Tool */}
          <button
            onClick={() => setShowElementInfo(!showElementInfo)}
            className={`w-12 h-12 ${showElementInfo ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-600 hover:bg-gray-500'} rounded-lg flex items-center justify-center text-white transition-colors`}
            title="Информация об элементе"
          >
            ℹ️
          </button>
          
          {/* Auto Layout */}
          <button
            className="w-12 h-12 bg-gray-600 hover:bg-gray-500 rounded-lg flex items-center justify-center text-white transition-colors"
            title="Авто-размещение"
          >
            📍
          </button>
          

          
          {/* DEBUG: Edge interaction area visualization */}
          <button
            onClick={() => setDebugEdgeInteraction(!debugEdgeInteraction)}
            className={`w-12 h-12 ${debugEdgeInteraction ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-600 hover:bg-gray-500'} rounded-lg flex items-center justify-center text-white transition-colors`}
            title="Отладка области взаимодействия с линиями"
          >
            🐛
          </button>
        </div>

        {/* Search Panel - BETWEEN toolbar and library */}
        {showSearchPanel && (
          <div className="search-panel bg-white border-r border-gray-200 w-80 flex flex-col shadow-lg h-full max-h-screen">
            <div className="panel-header bg-gray-50 px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Поиск по схеме
                </h3>
                <button
                  onClick={() => {
                    setShowSearchPanel(false);
                    setSearchQuery('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="panel-content flex-1 flex flex-col overflow-hidden">
              {/* Search Input */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchResults.length > 0) {
                        focusOnNode(searchResults[0].id);
                      }
                    }}
                    placeholder="Введите название элемента..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <div className="mt-2 text-sm text-gray-600">
                    Найдено: {searchResults.length}
                  </div>
                )}
              </div>
              
              {/* Search Results */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                {searchResults.length > 0 ? (
                  <div className="p-4 space-y-2">
                    {searchResults.map((result) => (
                      <div
                        key={result.id}
                        className="p-3 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => focusOnNode(result.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            focusOnNode(result.id);
                          }
                        }}
                        tabIndex={0}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">
                              {result.name}
                            </div>
                            {result.description && (
                              <div className="text-xs text-gray-600 mt-1">
                                {result.description}
                              </div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              Тип: {result.type === 'cable' ? 'Кабель' : 
                                    result.type === 'terminal_block' ? 'Клеммный блок' : 
                                    'Устройство'}
                            </div>
                          </div>
                          <div className="ml-2">
                            <span className="text-blue-600 text-sm">
                              →
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : searchQuery ? (
                  <div className="p-8 text-center text-gray-500">
                    Ничего не найдено
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    Начните вводить для поиска
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Element Info Panel - BETWEEN search and library */}
        {showElementInfo && selectedElement && (
          <div className="element-info-panel bg-white border-r border-gray-200 w-80 flex flex-col shadow-lg h-full max-h-screen">
            <div className="panel-header bg-gray-50 px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Информация об элементе
                </h3>
                <button
                  onClick={() => {
                    setShowElementInfo(false);
                    setSelectedElement(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="panel-content flex-1 flex flex-col p-4">
              {/* Compact Element Info */}
              <div className="space-y-3">
                {/* First Line: Instance ID and Position Label */}
                <div className="flex items-center gap-1">
                  {/* View Icon */}
                  <button
                    onClick={() => {
                      const url = `/projects/${projectId}/device-instances/${selectedElement.deviceInstanceId}`;
                      window.open(url, '_blank');
                    }}
                    className="flex-shrink-0 p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                    title="Просмотр экземпляра"
                  >
                    👁️
                  </button>
                  {/* Edit Icon */}
                  {!isReadOnly && (
                    <button
                      onClick={() => {
                        setShowInstanceEditModal(true);
                      }}
                      className="flex-shrink-0 p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Редактировать экземпляр"
                    >
                      ✏️
                    </button>
                  )}
                  
                  {/* Clickable Areas */}
                  <div className="flex-1 flex items-center">
                    {/* ID Click Area */}
                    <span
                      onClick={async () => {
                        const textToCopy = selectedElement.deviceInstanceId.toString();
                        try {
                          await navigator.clipboard.writeText(textToCopy);
                      
                        } catch (err) {
                          console.error('Failed to copy ID:', err);
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = textToCopy;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      }}
                      className="text-blue-600 font-mono font-medium hover:bg-blue-50 px-1 py-0.5 rounded cursor-pointer"
                      title="Кликните для копирования ID"
                    >
                      {selectedElement.deviceInstanceId}:
                    </span>
                    
                    {/* Name Click Area */}
                    <span
                      onClick={async () => {
                        const textToCopy = selectedElement.deviceName;
                        try {
                          await navigator.clipboard.writeText(textToCopy);
                      
                        } catch (err) {
                          console.error('Failed to copy name:', err);
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = textToCopy;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      }}
                      className="ml-2 text-gray-900 font-medium hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer"
                      title="Кликните для копирования названия"
                    >
                      {selectedElement.deviceName || '—'}
                    </span>
                  </div>
                </div>

                {/* Second Line: Specification ID and Name */}
                <div className="flex items-center gap-1">
                  {/* View Icon */}
                  <button
                    onClick={() => {
                      const url = `/projects/${projectId}/devices/${selectedElement.specificationId}`;
                      window.open(url, '_blank');
                    }}
                    className="flex-shrink-0 p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                    title="Просмотр спецификации"
                  >
                    👁️
                  </button>
                  {/* Edit Icon */}
                  {!isReadOnly && (
                    <button
                      onClick={async () => {
                        try {
                          // Check if specificationId is valid
                          if (!selectedElement.specificationId || selectedElement.specificationId <= 0) {
                            throw new Error('Invalid specification ID');
                          }
                          
                          const specData = await getSpecificationItem(selectedElement.specificationId);
                          setSelectedSpecification(specData);
                          // Clear selection to prevent DEL key from deleting canvas elements
                          setSelectedNodes(new Set());
                          setSelectedEdges([]);
                          setSelectedEdge(null);
                          setShowSpecEditModal(true);
                        } catch (error) {
                          console.error('Error loading specification:', error);
                          
                          // Show user-friendly error message with more details
                          const errorMsg = `Ошибка: Спецификация с ID ${selectedElement.specificationId} не найдена в базе данных.\n\nКабель: ${selectedElement.deviceName}\nТип: ${selectedElement.specName}\n\nПожалуйста, проверьте данные кабеля.`;
                          alert(errorMsg);
                        }
                      }}
                      className="flex-shrink-0 p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                      title="Редактировать спецификацию"
                    >
                      ✏️
                    </button>
                  )}
                  
                  {/* Clickable Areas */}
                  <div className="flex-1 flex items-center">
                    {/* ID Click Area */}
                    <span
                      onClick={async () => {
                        const textToCopy = selectedElement.specificationId.toString();
                        try {
                          await navigator.clipboard.writeText(textToCopy);
                      
                        } catch (err) {
                          console.error('Failed to copy spec ID:', err);
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = textToCopy;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      }}
                      className="text-green-600 font-mono font-medium hover:bg-green-50 px-1 py-0.5 rounded cursor-pointer"
                      title="Кликните для копирования ID спецификации"
                    >
                      {selectedElement.specificationId}:
                    </span>
                    
                    {/* Name Click Area */}
                    <span
                      onClick={async () => {
                        const textToCopy = selectedElement.specName;
                        try {
                          await navigator.clipboard.writeText(textToCopy);
                      
                        } catch (err) {
                          console.error('Failed to copy spec name:', err);
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = textToCopy;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      }}
                      className="ml-2 text-gray-900 hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer"
                      title="Кликните для копирования названия спецификации"
                    >
                      {selectedElement.specName || '—'}
                    </span>
                  </div>
                </div>

                {/* Third Line: Manufacturer and Model */}
                {selectedElementSpecification && (
                  <div className="flex items-center gap-1">
                    <div className="flex-1 flex items-center text-sm text-gray-600">
                      {selectedElementSpecification.manufacturer && (
                        <span className="mr-2">
                          <span className="font-medium">Производитель:</span>{' '}
                          <span
                            onClick={async () => {
                              const textToCopy = selectedElementSpecification.manufacturer!;
                              try {
                                await navigator.clipboard.writeText(textToCopy);
                              } catch (err) {
                                console.error('Failed to copy manufacturer:', err);
                                const textArea = document.createElement('textarea');
                                textArea.value = textToCopy;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                              }
                            }}
                            className="hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer"
                            title="Кликните для копирования производителя"
                          >
                            {selectedElementSpecification.manufacturer}
                          </span>
                        </span>
                      )}
                      {selectedElementSpecification.model && (
                        <span>
                          <span className="font-medium">Модель:</span>{' '}
                          <span
                            onClick={async () => {
                              const textToCopy = selectedElementSpecification.model!;
                              try {
                                await navigator.clipboard.writeText(textToCopy);
                              } catch (err) {
                                console.error('Failed to copy model:', err);
                                const textArea = document.createElement('textarea');
                                textArea.value = textToCopy;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                              }
                            }}
                            className="hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer"
                            title="Кликните для копирования модели"
                          >
                            {selectedElementSpecification.model}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Fourth Line: Specification ID and name for terminal auto nodes */}
                {selectedElement?.type === 'terminal' && selectedElement.specificationId > 0 && (
                  <div className="flex items-center gap-1">
                    {/* View Icon */}
                    <button
                      onClick={() => {
                        const url = `/projects/${projectId}/devices/${selectedElement.specificationId}`;
                        window.open(url, '_blank');
                      }}
                      className="flex-shrink-0 p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                      title="Просмотр спецификации"
                    >
                      👁️
                    </button>
                    {/* Edit Icon */}
                    {!isReadOnly && (
                      <button
                        onClick={async () => {
                          try {
                            const specData = await getSpecificationItem(selectedElement.specificationId);
                            setSelectedSpecification(specData);
                            // Clear selection to prevent DEL key from deleting canvas elements
                            setSelectedNodes(new Set());
                            setSelectedEdges([]);
                            setSelectedEdge(null);
                            setShowSpecEditModal(true);
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                        className="flex-shrink-0 p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                        title="Редактировать спецификацию"
                      >
                        ✏️
                      </button>
                    )}
                    <div className="flex-1 flex items-center">
                      <span className="text-green-600 font-mono font-medium hover:bg-green-50 px-1 py-0.5 rounded cursor-pointer">
                        {selectedElement.specificationId}:
                      </span>
                      <span className="ml-2 text-gray-900 hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer">
                        {selectedElementSpecification?.name || '—'}{selectedElementSpecification?.manufacturer ? `. ${selectedElementSpecification.manufacturer}.` : ''}{selectedElementSpecification?.model ? ` ${selectedElementSpecification.model}` : ''}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Remove from Canvas Button - скрыт в режиме только просмотр */}
              {!isReadOnly && (
                <div className="mt-6 pt-4 border-t">
                  <button
                    onClick={() => {
                      if (schemaType === 'terminal') {
                        // Remove from terminal schema
                        removeCableFromCanvas(selectedElement.deviceInstanceId, selectedElement.layoutId);
                      } else if (shouldShowMainSchemaFeatures) {
                        // Remove from main or breaker schema
                        removeFromMainSchema(selectedElement.deviceInstanceId);
                      }
                      setShowElementInfo(false);
                      setSelectedElement(null);
                    }}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    🗑️ Убрать с холста
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Universal Library Slide Panel - BETWEEN toolbar and canvas */}
        {showCableLibrary && !isReadOnly && (
          <div className="library-panel bg-white border-r border-gray-200 w-80 flex flex-col shadow-lg">
            <div className="panel-header bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  {schemaType === 'terminal' ? (
                    cableSearchQuery ? 
                      `Кабели (${filteredCables?.length || 0})` : 
                      'Библиотека кабелей'
                  ) : (
                    'Библиотека элементов основной схемы'
                  )}
                </h3>
                <button
                  onClick={() => setShowCableLibrary(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              {/* Search field for terminal schema cables */}
              {schemaType === 'terminal' && (
                <div className="mt-3">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Поиск кабелей..."
                      value={cableSearchQuery}
                      onChange={(e) => setCableSearchQuery(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    {cableSearchQuery && (
                      <button
                        onClick={() => setCableSearchQuery('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="panel-content flex-1 p-4 overflow-y-auto">
              {schemaType === 'terminal' ? (
                // Terminal schema - show cables
                filteredCables && filteredCables.length > 0 ? (
                  <div className="space-y-3">
                    {filteredCables.map((cable: any) => (
                      <div 
                        key={cable.id} 
                        className="cable-item p-3 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors select-none"
                        title={`Кабель ID: ${cable.id}`}
                        onClick={() => handleCableClick(cable)}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', JSON.stringify({
                            type: 'cable',
                            deviceInstanceId: cable.id,
                            specificationId: cable.specification_id,
                            positionLabel: cable.position_label,
                            specName: cable.spec_name
                          }));
                        }}
                      >
                        <div className="font-medium text-sm text-gray-900">
                          {cable.position_label}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {cable.spec_name}
                        </div>
                        {cable.length_meters && (
                          <div className="text-xs text-blue-600 mt-1">
                            Длина: {cable.length_meters} м
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-sm">
                      {cableSearchQuery ? (
                        // Search mode - no results
                        <div>
                          <div className="text-lg mb-2">🔍</div>
                          <div>Ничего не найдено по запросу</div>
                          <div className="text-xs mt-1 font-mono bg-gray-100 px-2 py-1 rounded">"{cableSearchQuery}"</div>
                        </div>
                      ) : (
                        // No search - regular empty states
                        availableCables ? 
                          (cablesOnCanvas.size > 0 ? 
                            'Все кабели размещены на холсте' : 
                            'Нет экземпляров кабелей'
                          ) : 
                          'Загрузка кабелей...'
                      )}
                    </div>
                  </div>
                )
              ) : (
                // Main schema - show device types from SchemaManager
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-700">Доступные типы устройств:</h4>
                  <div className="space-y-2">
                    {schemaManager.getAllowedDeviceTypes().map((deviceType) => (
                      <div 
                        key={deviceType} 
                        className="device-type-item p-3 border border-gray-200 rounded-lg hover:bg-green-50 cursor-pointer transition-colors select-none"
                        title={`Тип: ${deviceType}`}
                      >
                        <div className="font-medium text-sm text-gray-900">
                          {getDeviceTypeDisplayName(deviceType)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {deviceType}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Full Canvas */}
        <div 
          className="canvas-container flex-1 relative"
          ref={flowWrapperRef}
        >
          {/* Custom selection overlay (disabled by flag) */}
          {USE_CUSTOM_SELECTION_OVERLAY && selectionRect.active && (
            <div
              style={{
                position: 'absolute',
                left: Math.min(selectionRect.x1, selectionRect.x2),
                top: Math.min(selectionRect.y1, selectionRect.y2),
                width: Math.abs(selectionRect.x2 - selectionRect.x1),
                height: Math.abs(selectionRect.y2 - selectionRect.y1),
                background: 'rgba(59,130,246,0.1)',
                border: '2px solid #3b82f6',
                borderRadius: 4,
                zIndex: 9999,
                pointerEvents: 'none',
              }}
            />
          )}
          {/* Clean edge styling using ReactFlow built-in mechanisms */}
          <style>{`
            .react-flow__edge {
              pointer-events: all;
            }
            .react-flow__edge-path {
              cursor: pointer;
            }
            .react-flow__edge.selected .react-flow__edge-path {
              stroke-width: 3;
            }
            /* Live selection preview during drag */
            .react-flow__edge.selected-preview .react-flow__edge-path {
              stroke: #60a5fa !important; /* blue-400 */
              stroke-width: 3px !important;
              opacity: 0.7;
            }
            .react-flow__edge-interaction {
              stroke-width: 4px !important;
            }
            ${debugEdgeInteraction ? `
            .react-flow__edge-interaction {
              stroke: rgba(255, 0, 0, 0.3) !important;
              stroke-width: 4px !important;
            }
            ` : ''}
            .react-flow__handle {
              pointer-events: all;
            }
            /* Blinking animation for highlighted nodes */
            @keyframes nodeHighlight {
              0%, 100% {
                box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
                border-color: inherit;
              }
              25% {
                box-shadow: 0 0 20px 10px rgba(59, 130, 246, 0.6);
                border-color: #3b82f6;
              }
              50% {
                box-shadow: 0 0 30px 15px rgba(59, 130, 246, 0.8);
                border-color: #2563eb;
              }
              75% {
                box-shadow: 0 0 20px 10px rgba(59, 130, 246, 0.6);
                border-color: #3b82f6;
              }
            }
            .react-flow__node.highlighted > div {
              animation: nodeHighlight 0.5s ease-in-out 8;
              border-width: 3px !important;
            }
            /* Selected node styling */
            .react-flow__node.selected > div {
              box-shadow: 0 0 15px 5px rgba(59, 130, 246, 0.5);
              border: 2px solid #3b82f6 !important;
              border-radius: 8px;
            }
            
            /* Disable context menu for right click */
            .react-flow__pane {
              -webkit-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
              user-select: none;
            }
            
            .react-flow__pane * {
              -webkit-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
              user-select: none;
            }
            /* Block node dragging in read-only mode */
            ${isReadOnly ? `
              .react-flow__node {
                cursor: default !important;
                user-select: none !important;
                -webkit-user-drag: none !important;
                -moz-user-select: none !important;
              }
              .react-flow__node > div {
                cursor: default !important;
                pointer-events: auto !important;
              }
              .react-flow__node.dragging {
                cursor: default !important;
              }
            ` : ''}
          `}</style>
          {blockId ? (
            <>
              {(() => {
                // console.log('🔵 Before ReactFlow - edges:', connectionEdges.map((e: Edge) => ({ id: e.id, type: e.type })));
                return null;
              })()}
              <ReactFlow
                nodes={nodes}
                edges={edgesForFlow}
              onNodesChange={enhancedOnNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onReconnectStart={onReconnectStart}
              onReconnect={onReconnect}
              onReconnectEnd={onReconnectEnd}
              // onConnectStart={onConnectStart} - TEMPORARILY COMMENTED
              // onConnectEnd={onConnectEnd} - TEMPORARILY COMMENTED
              onNodeClick={handleNodeClick}
              onEdgeClick={onEdgeClick}
              onNodeDragStart={isReadOnly ? (e) => { e.preventDefault(); e.stopPropagation(); } : handleNodeDragStart}
              onNodeDrag={isReadOnly ? (e) => { e.preventDefault(); e.stopPropagation(); } : handleNodeDrag}
              onNodeDragStop={isReadOnly ? (e) => { e.preventDefault(); e.stopPropagation(); } : handleNodeDragStop}
              nodesDraggable={!isReadOnly}
              panOnDrag={isReadOnly ? [0, 1] : [0, 1]}
              nodesConnectable={!isReadOnly}
              onPaneClick={onPaneClick}
              onMouseDown={onMouseDown}
              onContextMenu={onContextMenu}
              onSelectionChange={onSelectionChange}
              onSelectionStart={onSelectionStart}
              onSelectionDragStart={onSelectionStart}
              onSelectionDrag={onSelectionChangePreview}
              onSelectionEnd={onSelectionEnd}
              // Prevent excessive re-renders by disabling internal selection change flood
              onMove={onMove}
              onDrop={handleCanvasDrop}
              onDragOver={handleDragOver}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
                              onInit={() => {
                  // console.log('🔵 ReactFlow initialized with edgeTypes:', reactFlowInstance.getEdges().map(e => ({ id: e.id, type: e.type })));
                }}
              fitView={false}
              minZoom={0.01}
              maxZoom={8}
              className="h-full w-full"
              elevateEdgesOnSelect={false}
              elevateNodesOnSelect={false}
              edgesFocusable={true}
              nodesFocusable={true}
              deleteKeyCode={null}
              selectionMode={SelectionMode.Partial}
              selectionOnDrag={false}
              selectNodesOnDrag={false}
              defaultEdgeOptions={{
                zIndex: 1000,
                animated: false,
                style: { stroke: '#007bff', strokeWidth: 2 }
              }}
            >
              <Controls>
              </Controls>
              <Background 
                variant={getBackgroundVariant(config.backgroundGrid)} 
                gap={12} 
                size={1} 
              />
            </ReactFlow>
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-700 mb-2">
                  Терминальная схема
                </h3>
                <p className="text-gray-500">
                  Блок клеммников не выбран
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Manual Route Editor - Terminal Schema (ветка мёртвая: при schemaType=terminal рендерится TerminalSchemaEditor) */}
        {showManualRouteEditor && selectedEdgeForRouting && (
          <ManualRouteEditor
            selectedEdge={selectedEdgeForRouting}
            onRouteUpdate={handleRouteUpdate}
            onClose={() => {
              setShowManualRouteEditor(false);
              setSelectedEdgeForRouting(null);
            }}
          />
        )}

        
        {/* Modal Components - Conditional based on element type */}
        {selectedElement?.type === 'cable' ? (
          <CableInstanceEditModal
            key={cableModalKey}
            isOpen={showInstanceEditModal}
            onClose={() => setShowInstanceEditModal(false)}
            selectedInstance={cableInstanceData || cableInstanceForModal}
            projectId={projectId}
            onSuccess={() => {
              // Invalidate schema layout to refresh nodes with updated reserve status
              queryClient.invalidateQueries({ queryKey: ['schemaLayout', schemaType, schemaId] });
              // Invalidate available devices/cables for libraries
              queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId] });
              queryClient.invalidateQueries({ queryKey: ['availableCables', projectId] });
              queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] });
              queryClient.invalidateQueries({ queryKey: ['cableInstance', selectedElement?.deviceInstanceId] });
              // For terminal schema, also trigger reload
              // Also refresh selected element data to update the info panel
              if (selectedElement) {
                // Force update selected element with fresh data from the modal edit
                setSelectedElement(null);
                // The panel will close but user can click again to see updated data
              }
            }}
          />
        ) : selectedElement?.type === 'terminal' ? (
          (() => {
            const selectedInstance = selectedElement ? {
              id: selectedElement.terminalInstanceId as number,
              position_label: selectedElement.deviceName,
              specification_id: selectedElement.specificationId,
              device_type: 'device',
              description: selectedElement.terminalBlockDescription || '',
              is_reserve: selectedElement.isReserve ? 1 : 0,
              project_id: projectId,
              created_at: new Date().toISOString()
            } as any : null;
            return (
              <DeviceInstanceEditModal
                key={`terminal-modal-${selectedElement.terminalInstanceId || selectedElement.deviceInstanceId}-${selectedElement.terminalBlockDescription || 'no-desc'}`}
                isOpen={showInstanceEditModal}
                onClose={() => setShowInstanceEditModal(false)}
                selectedInstance={selectedInstance}
                projectId={projectId}
                onSuccess={() => {
                  queryClient.invalidateQueries({ queryKey: ['schemaLayout', schemaType, schemaId] });
                  queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId] });
                  queryClient.invalidateQueries({ queryKey: ['availableCables', projectId] });
                  queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] });
                  if (selectedElement) setSelectedElement(null);
                }}
              />
            );
          })()
        ) : (
          <DeviceInstanceEditModal
            isOpen={showInstanceEditModal}
            onClose={() => setShowInstanceEditModal(false)}
            selectedInstance={selectedElement ? {
              id: selectedElement.deviceInstanceId,
              position_label: selectedElement.deviceName,
              specification_id: selectedElement.specificationId,
              device_type: 'device',
              device_type_id: selectedElement.deviceTypeId,
              spec_name: selectedElement.specName,
              manufacturer: selectedElement.manufacturer,
              model: selectedElement.model,
              knx_address: selectedElement.knxAddress,
              description: selectedElement.description || '',
              is_reserve: selectedElement.isReserve ? 1 : 0,
              project_id: projectId,
              created_at: new Date().toISOString()
            } as any : null}
            projectId={projectId}
            onSuccess={() => {
              // Invalidate schema layout to refresh nodes with updated reserve status
              queryClient.invalidateQueries({ queryKey: ['schemaLayout', schemaType, schemaId] });
              // Invalidate available devices/cables for libraries
              queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId] });
              queryClient.invalidateQueries({ queryKey: ['availableCables', projectId] });
              queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] });
              // For terminal schema, also trigger reload
              // Also refresh selected element data to update the info panel
              if (selectedElement) {
                setSelectedElement(null);
              }
            }}
          />
        )}
        
        {selectedSpecification?.device_type === 'cable' ? (
          <CableSpecEditModal
            isOpen={showSpecEditModal}
            onClose={() => {
              setShowSpecEditModal(false);
              setSelectedSpecification(null);
            }}
            selectedCable={selectedSpecification}
            projectId={projectId}
            onSuccess={() => {
              // Also refresh selected element data
              if (selectedElement) {
                setSelectedElement(null);
              }
            }}
          />
        ) : (
          <DeviceSpecEditModal
            isOpen={showSpecEditModal}
            onClose={() => {
              setShowSpecEditModal(false);
              setSelectedSpecification(null);
            }}
            selectedDevice={selectedSpecification}
            projectId={projectId}
            onSuccess={() => {
              // Also refresh selected element data
              if (selectedElement) {
                setSelectedElement(null);
              }
            }}
          />
        )}
      </div>
      </div>
    );
  }

  // Minimal layout for main, breaker, knx, rs485, and ethernet schemas  
  if (shouldShowMainSchemaFeatures && schemaId && (schemaType === 'main' || schemaType === 'breaker' || schemaType === 'knx' || schemaType === 'rs485' || schemaType === 'ethernet')) {
    return (
      <div className="schema-editor-shell h-screen flex flex-col bg-gray-100">
      {schemaEditorHeader}
      <div className="main-schema-editor flex flex-1 overflow-hidden min-h-0">
        {/* Narrow left toolbar - ALWAYS VISIBLE */}
        <div className="toolbar-panel bg-gray-800 w-16 flex flex-col items-center py-4 space-y-4">
          {/* Device Library Icon */}
          <button
            onClick={() => setShowCableLibrary(!showCableLibrary)}
            className={`w-12 h-12 rounded-lg flex items-center justify-center text-white transition-colors ${
              showCableLibrary ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
            title="Библиотека элементов основной схемы"
          >
            📚
          </button>

          {/* Tool buttons - using SchemaManager */}
          {schemaManager.getAvailableTools().map((tool) => (
            <div key={tool} className="relative flex flex-col items-center">
              <button
                className={`w-12 h-12 rounded-lg ${
                  tool === 'print' && isExportingPDF ? 'bg-blue-600 cursor-not-allowed' : 
                  tool === 'grid-snap' && isGridSnappingEnabled ? 'bg-blue-600 hover:bg-blue-700' :
                  'bg-gray-700 hover:bg-gray-600'
                } text-white text-xs flex items-center justify-center transition-colors`}
                title={tool === 'print' && isExportingPDF ? "Подготовка PDF..." : (tool === 'print' ? "Экспорт схемы в PDF на альбомных листах A4" : getToolDisplayName(tool))}
                disabled={tool === 'print' && isExportingPDF}
                onMouseEnter={() => tool === 'grid-snap' && setShowGridSettings(true)}
                onMouseLeave={() => tool === 'grid-snap' && setTimeout(() => setShowGridSettings(false), 4000)}
                onClick={async () => {
                  // NEW: Use SchemaManager to execute tools
                  try {
                    // Handle search button specially
                    if (tool === 'search') {
                      setShowSearchPanel(prev => !prev);
                      return;
                    }
                    
                    // Handle layers button specially
                    if (tool === 'layers') {
                      setShowLayersPanel(prev => !prev);
                      return;
                    }
                    
                    // Handle grid-snap button specially
                    if (tool === 'grid-snap') {
                      console.log('🔗 Grid snapping toggle clicked (toolbar)');
                              setIsGridSnappingEnabled((prev: boolean) => {
          const newValue = !prev;
          console.log('📊 Grid snapping enabled (toolbar):', newValue);
          return newValue;
        });
                      // Показываем панельку при клике
                      setShowGridSettings(true);
                      return;
                    }
                    
                    // Show loading indicator for print tool
                    if (tool === 'print') {
                      setIsExportingPDF(true);
                    }
                    
                    await schemaManager.executeTool(tool);
                  } catch (error) {
                    console.error(`Error executing tool ${tool}:`, error);
                  } finally {
                    // Hide loading indicator for print tool
                    if (tool === 'print') {
                      setIsExportingPDF(false);
                    }
                  }
                }}
              >
                {getToolDisplayName(tool).split(' ')[0]}
              </button>
              {tool === 'layers' && (hiddenNodesCount > 0 || anyLayerFilterOff) && (
                <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] leading-none rounded-full px-1.5 py-0.5 border border-white shadow">
                  {hiddenNodesCount > 0 ? hiddenNodesCount : '!'}
                </span>
              )}
              {tool === 'layers' && (
                <div className="mt-1 text-[10px] leading-tight text-amber-600 text-center w-12 select-none">
                  Скрыто: {hiddenNodesCount}
                </div>
              )}
              
              {/* Grid Settings Popup for grid-snap tool */}
              {tool === 'grid-snap' && showGridSettings && (
                <div 
                  className="grid-settings-popup absolute left-full ml-2 top-0 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 min-w-48"
                  onMouseEnter={() => setShowGridSettings(true)}
                  onMouseLeave={() => setShowGridSettings(false)}
                >
                  <div className="text-sm font-medium text-gray-700 mb-2">Шаг сетки:</div>
                  <div className="space-y-2">
                    {[10, 20, 30, 50, 100].map((step) => (
                      <button
                        key={step}
                        onClick={() => setGridStep(step)}
                        className={`w-full px-2 py-1 text-xs rounded ${
                          gridStep === step 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        {step}px
                      </button>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Текущий шаг: {gridStep}px
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {/* DEBUG: Edge interaction area visualization */}
          <button
            onClick={() => setDebugEdgeInteraction(!debugEdgeInteraction)}
            className={`w-12 h-12 ${debugEdgeInteraction ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'} rounded-lg flex items-center justify-center text-white transition-colors`}
            title="Отладка области взаимодействия с линиями"
          >
            🐛
          </button>
        </div>

        {/* Search Panel - BETWEEN toolbar and device library */}
        {showSearchPanel && (
          <div className="search-panel bg-white border-r border-gray-200 w-80 flex flex-col shadow-lg h-full max-h-screen">
            <div className="panel-header bg-gray-50 px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Поиск по схеме
                </h3>
                <button
                  onClick={() => {
                    setShowSearchPanel(false);
                    setSearchQuery('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="panel-content flex-1 flex flex-col overflow-hidden">
              {/* Search Input */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchResults.length > 0) {
                        focusOnNode(searchResults[0].id);
                      }
                    }}
                    placeholder="Введите название элемента..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <div className="mt-2 text-sm text-gray-600">
                    Найдено: {searchResults.length}
                  </div>
                )}
              </div>
              
              {/* Search Results */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                {searchResults.length > 0 ? (
                  <div className="p-4 space-y-2">
                    {searchResults.map((result) => (
                      <div
                        key={result.id}
                        className="p-3 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => focusOnNode(result.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            focusOnNode(result.id);
                          }
                        }}
                        tabIndex={0}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">
                              {result.name}
                            </div>
                            {result.description && (
                              <div className="text-sm text-gray-600">{result.description}</div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              {result.type === 'cable' ? 'Кабель' : 
                               result.type === 'device' ? 'Устройство' : 
                               'Терминальный блок'}
                            </div>
                          </div>
                          <div className="ml-2">
                            <span className="text-blue-600 text-sm">
                              →
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : searchQuery ? (
                  <div className="p-4 text-center text-gray-500">
                    Ничего не найдено
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    Начните вводить для поиска
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Layers Panel - BETWEEN toolbar and device library */}
        {showLayersPanel && (
          <div className="layers-panel bg-white border-r border-gray-200 w-80 flex flex-col shadow-lg h-full max-h-screen">
            <div className="panel-header bg-gray-50 px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Слои отображения
                </h3>
                <button
                  onClick={() => setShowLayersPanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="panel-content flex-1 flex flex-col overflow-hidden">
              {/* Device Types Filter */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Типы устройств</h4>
                <div className="space-y-2">
                  {deviceTypes.filter(Boolean).map((deviceType: any) => {
                    const filterKey = `show${deviceType.code.charAt(0).toUpperCase() + deviceType.code.slice(1)}s`;
                    return (
                      <label key={`device-type-${deviceType.id || deviceType.code}`} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={layerFilters[filterKey] ?? true}
                          onChange={(e) => {
                            setLayerFilters(prev => ({
                              ...prev,
                              [filterKey]: e.target.checked
                            }));
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{deviceType.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              
              {/* Reserve Filter */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Резервные устройства</h4>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={layerFilters.showReserve ?? true}
                    onChange={(e) => {
                      setLayerFilters(prev => ({
                        ...prev,
                        showReserve: e.target.checked
                      }));
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Показать резервные устройства</span>
                </label>
              </div>
              
              {/* Action Buttons */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const newFilters: Record<string, boolean> = {};
                      deviceTypes.filter(Boolean).forEach((deviceType: any) => {
                        const filterKey = `show${deviceType.code.charAt(0).toUpperCase() + deviceType.code.slice(1)}s`;
                        newFilters[filterKey] = true;
                      });
                      newFilters.showReserve = true;
                      setLayerFilters(newFilters);
                    }}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Показать все
                  </button>
                  <button
                    onClick={() => {
                      const newFilters: Record<string, boolean> = {};
                      deviceTypes.filter(Boolean).forEach((deviceType: any) => {
                        const filterKey = `show${deviceType.code.charAt(0).toUpperCase() + deviceType.code.slice(1)}s`;
                        newFilters[filterKey] = false;
                      });
                      newFilters.showReserve = false;
                      setLayerFilters(newFilters);
                    }}
                    className="flex-1 px-3 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
                  >
                    Скрыть все
                  </button>
                </div>
              </div>
              
              {/* Info */}
              <div className="p-4 text-xs text-gray-500">
                <p>Используйте фильтры для управления видимостью элементов на схеме.</p>
                <p className="mt-1">Скрытые элементы не отображаются и их связи также скрываются.</p>
              </div>
            </div>
          </div>
        )}

        {/* Device Library Slide Panel - BETWEEN toolbar and canvas */}
        {showCableLibrary && !isReadOnly && (
          <div className="device-library-panel bg-white border-r border-gray-200 w-80 flex flex-col shadow-lg">
            <div className="panel-header bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Библиотека элементов основной схемы
                </h3>
                <button
                  onClick={() => setShowCableLibrary(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            {/* Library Search Input */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="relative">
                <input
                  type="text"
                  value={librarySearchQuery}
                  onChange={(e) => setLibrarySearchQuery(e.target.value)}
                  placeholder="Поиск в библиотеке элементов..."
                  className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔍
                </div>
                {librarySearchQuery && (
                  <button
                    onClick={() => setLibrarySearchQuery('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
              {librarySearchQuery && (
                <div className="mt-2 text-xs text-gray-600">
                  Поиск: "{librarySearchQuery}"
                </div>
              )}
            </div>
            
            <div className="panel-content flex-1 p-4 overflow-y-auto">
              <div className="space-y-4">
                
                {/* Кабели секция */}
                <div className="section">
                  <button
                    onClick={() => setShowCablesSection(!showCablesSection)}
                    className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
                  >
                    <span>Кабели ({availableCablesFiltered.length})</span>
                    <span className="text-gray-400">{showCablesSection ? '▼' : '▶'}</span>
                  </button>
                  
                  {showCablesSection && (
                    <div className="space-y-2">
                      {availableCablesFiltered.length > 0 ? (
                        availableCablesFiltered.map((cable: any) => (
                          <div 
                            key={cable.id} 
                            className="cable-item p-3 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors select-none"
                            title={`Кабель ID: ${cable.id}`}
                            onClick={() => handleCableClick(cable)}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('text/plain', JSON.stringify({
                                type: 'cable',
                                deviceInstanceId: cable.id,
                                specificationId: cable.specification_id,
                                positionLabel: cable.position_label,
                                specName: cable.spec_name
                              }));
                            }}
                          >
                            <div className="font-medium text-sm text-gray-900">
                              {cable.position_label}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              {cable.spec_name}
                            </div>
                            {cable.length_meters && (
                              <div className="text-xs text-blue-600 mt-1">
                                Длина: {cable.length_meters} м
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4">
                          <div className="text-gray-400 text-sm">
                            {cablesOnCanvas.size > 0 ? 'Все кабели размещены на холсте' : 'Нет экземпляров кабелей'}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Device type sections */}
                {Object.entries(availableDevicesByType).map(([deviceType, devices]) => {
                  if (devices.length === 0) return null;
                  
                  return (
                    <div key={deviceType} className="section">
                      <button
                        onClick={() => setShowDeviceTypeSections(prev => ({
                          ...prev,
                          [deviceType]: !prev[deviceType]
                        }))}
                        className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
                      >
                        <span>{getDeviceTypeDisplayName(deviceType)} ({devices.length})</span>
                        <span className="text-gray-400">{showDeviceTypeSections[deviceType] ? '▼' : '▶'}</span>
                      </button>
                      
                      {showDeviceTypeSections[deviceType] && (
                        <div className="space-y-2">
                          {devices.map((device: any) => (
                            <div 
                              key={device.id} 
                              className="device-item p-3 border border-gray-200 rounded-lg hover:bg-green-50 cursor-pointer transition-colors select-none"
                              title={`Устройство ID: ${device.id}`}
                              onClick={() => handleDeviceClick(device)}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('text/plain', JSON.stringify({
                                  type: 'device',
                                  id: device.id,
                                  specification_id: device.specification_id,
                                  position_label: device.position_label,
                                  spec_name: device.spec_name,
                                  description: device.description,
                                  device_type: device.device_type,
                                  is_reserve: device.is_reserve
                                }));
                              }}
                            >
                              <div className="font-medium text-sm text-gray-900">
                                {device.position_label}
                              </div>
                              {device.description && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {device.description}
                                </div>
                              )}
                              <div className="text-xs text-blue-600 mt-1">
                                {device.spec_name}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}


              </div>
            </div>
          </div>
        )}

        {/* Canvas - FULL HEIGHT */}
        <div className="canvas-container flex-1 relative" style={{ backgroundColor: config.canvasColor }} ref={flowWrapperRef}>
          {/* Custom selection overlay (disabled by flag) */}
          {USE_CUSTOM_SELECTION_OVERLAY && selectionRect.active && (
            <div
              style={{
                position: 'absolute',
                left: Math.min(selectionRect.x1, selectionRect.x2),
                top: Math.min(selectionRect.y1, selectionRect.y2),
                width: Math.abs(selectionRect.x2 - selectionRect.x1),
                height: Math.abs(selectionRect.y2 - selectionRect.y1),
                background: 'rgba(59,130,246,0.1)',
                border: '2px solid #3b82f6',
                borderRadius: 4,
                zIndex: 9999,
                pointerEvents: 'none',
              }}
            />
          )}
          <style>{`
            .react-flow__edge {
              pointer-events: all;
            }
            .react-flow__edge-path {
              cursor: pointer;
            }
            .react-flow__edge.selected .react-flow__edge-path {
              stroke-width: 3;
            }
            .react-flow__edge-interaction {
              stroke-width: 4px !important;
            }
            ${debugEdgeInteraction ? `
            .react-flow__edge-interaction {
              stroke: rgba(255, 0, 0, 0.3) !important;
              stroke-width: 4px !important;
            }
            ` : ''}
            .react-flow__handle {
              pointer-events: all;
            }
            /* Blinking animation for highlighted nodes */
            @keyframes nodeHighlight {
              0%, 100% {
                box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
                border-color: inherit;
              }
              25% {
                box-shadow: 0 0 20px 10px rgba(59, 130, 246, 0.6);
                border-color: #3b82f6;
              }
              50% {
                box-shadow: 0 0 30px 15px rgba(59, 130, 246, 0.8);
                border-color: #2563eb;
              }
              75% {
                box-shadow: 0 0 20px 10px rgba(59, 130, 246, 0.6);
                border-color: #3b82f6;
              }
            }
            .react-flow__node.highlighted > div {
              animation: nodeHighlight 0.5s ease-in-out 8;
              border-width: 3px !important;
            }
            /* Selected node styling */
            .react-flow__node.selected > div {
              box-shadow: 0 0 15px 5px rgba(59, 130, 246, 0.5);
              border: 2px solid #3b82f6 !important;
              border-radius: 8px;
            }
            
            /* Disable context menu for right click */
            .react-flow__pane {
              -webkit-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
              user-select: none;
            }
            
            .react-flow__pane * {
              -webkit-user-select: none;
              -moz-user-select: none;
              -ms-user-select: none;
              user-select: none;
            }
            /* Block node dragging in read-only mode */
            ${isReadOnly ? `
              .react-flow__node {
                cursor: default !important;
                user-select: none !important;
                -webkit-user-drag: none !important;
                -moz-user-select: none !important;
              }
              .react-flow__node > div {
                cursor: default !important;
                pointer-events: auto !important;
              }
              .react-flow__node.dragging {
                cursor: default !important;
              }
            ` : ''}
          `}</style>
          
          {(() => {
            // console.log('🔵 Before ReactFlow (main) - edges:', connectionEdges.map((e: Edge) => ({ id: e.id, type: e.type })));
            return null;
          })()}
          <ReactFlow
            nodes={nodes}
            edges={edgesForFlow}
            onNodesChange={enhancedOnNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onReconnectStart={onReconnectStart}
            onReconnect={onReconnect}
            onReconnectEnd={onReconnectEnd}
            // onConnectStart={onConnectStart} - TEMPORARILY COMMENTED
            // onConnectEnd={onConnectEnd} - TEMPORARILY COMMENTED
            onNodeClick={handleNodeClick}
            onEdgeClick={onEdgeClick}
            onNodeDragStart={isReadOnly ? (e) => { e.preventDefault(); e.stopPropagation(); } : handleNodeDragStart}
            onNodeDragStop={isReadOnly ? (e) => { e.preventDefault(); e.stopPropagation(); } : handleNodeDragStop}
            nodesDraggable={!isReadOnly}
            panOnDrag={isReadOnly ? [0, 1] : [0, 1]}
            nodesConnectable={!isReadOnly}
            onPaneClick={onPaneClick}
            onMouseDown={onMouseDown}
            onContextMenu={onContextMenu}
            onSelectionChange={onSelectionChange}
            onSelectionStart={onSelectionStart}
            onSelectionDragStart={onSelectionStart}
            onSelectionDrag={onSelectionChangePreview}
            onSelectionEnd={onSelectionEnd}
            onMove={onMove}
            onDrop={handleCanvasDrop}
            onDragOver={handleDragOver}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView={false}
            minZoom={0.01}
            maxZoom={20}
            elevateEdgesOnSelect={false}
            elevateNodesOnSelect={false}
            edgesFocusable={true}
            nodesFocusable={true}
            selectionMode={SelectionMode.Partial}
            selectionOnDrag={false}
            selectNodesOnDrag={false}
            defaultEdgeOptions={{
              zIndex: 1000,
              animated: false,
              style: { stroke: '#007bff', strokeWidth: 2 },
              interactionWidth: 10 // Уменьшаем область взаимодействия
            }}
          >
            <Controls>
            </Controls>
            <Background 
              variant={getBackgroundVariant(config.backgroundGrid)} 
              gap={12} 
              size={1} 
            />
          </ReactFlow>
        </div>
        
        {/* Smart Line Panel - Main Schema */}
        {selectedEdgeForPanel && !isReadOnly && (
          <SmartLinePanel
            selectedEdge={selectedEdgeForPanel}
            activePopup={activePopup}
            setActivePopup={setActivePopup}
            updateEdgeProperties={(edgeId, props) => {
              // Map type to algorithm if type is provided
              if (props.type !== undefined) {
                switch (props.type) {
                  case 'step': props.algorithm = Algorithm.Step; break;
                  case 'straight': props.algorithm = Algorithm.Linear; break;
                  case 'smoothstep': props.algorithm = Algorithm.CatmullRom; break;
                  case 'bezier': props.algorithm = Algorithm.BezierCatmullRom; break;
                }
              }
              updateEdgeProperties(edgeId, props);
            }}
            isReadOnly={isReadOnly}
            projectId={projectId != null ? Number(projectId) : undefined}
            currentSchemaId={schemaId != null ? Number(schemaId) : undefined}
            connectionId={selectedConnectionVisibility?.connectionId}
            connectionVisibility={selectedConnectionVisibility != null ? { schema_id: selectedConnectionVisibility.schema_id, visibility_legacy: selectedConnectionVisibility.visibility_legacy, visible_on_schema_ids: selectedConnectionVisibility.visible_on_schema_ids } : undefined}
            onSaveVisibility={handleSaveVisibility}
            onDeleteConnection={(connectionId) => {
              let numericId: number;
              if (connectionId.startsWith('connection-')) {
                numericId = parseInt(connectionId.replace('connection-', ''));
              } else if (connectionId.startsWith('device-terminal-')) {
                numericId = parseInt(connectionId.replace('device-terminal-', ''));
              } else if (connectionId.startsWith('auto-terminal-')) {
                numericId = parseInt(connectionId.replace('auto-terminal-', ''));
              } else {
                numericId = parseInt(connectionId);
              }
              if (!isNaN(numericId)) {
                deleteConnectionMutation.mutateAsync(numericId).then(() => {
                  setSelectedEdge(null);
                  setActivePopup(null);
                });
              }
            }}
            onClose={() => {
              setSelectedEdge(null);
              setActivePopup(null);
            }}
            onEditRoute={(edge) => {
              if (edge.resetToAutomatic) {
                const connectionId = parseInt(edge.id.replace(/^(connection-|auto-terminal-|device-terminal-)/, ''));
                if (!isNaN(connectionId)) {
                  handleRouteUpdate(connectionId, [], false);
                }
              }
            }}
          />
        )}
        
        {/* Manual Route Editor - Main Schema */}
        {showManualRouteEditor && selectedEdgeForRouting && (
          <ManualRouteEditor
            selectedEdge={selectedEdgeForRouting}
            onRouteUpdate={handleRouteUpdate}
            onClose={() => {
              setShowManualRouteEditor(false);
              setSelectedEdgeForRouting(null);
            }}
          />
        )}


        {/* Element Info Panel for Main Schema - BETWEEN search and device library */}
        {showElementInfo && selectedElement && (
          <div className="element-info-panel bg-white border-r border-gray-200 w-80 flex flex-col shadow-lg h-full max-h-screen">
            <div className="panel-header bg-gray-50 px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Информация об элементе
                </h3>
                <button
                  onClick={() => {
                    setShowElementInfo(false);
                    setSelectedElement(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="panel-content flex-1 flex flex-col p-4">
              {/* Compact Element Info */}
              <div className="space-y-3">
                {/* First Line: Instance ID and Position Label */}
                <div className="flex items-center gap-1">
                  {/* View Icon */}
                  <button
                    onClick={() => {
                      const url = `/projects/${projectId}/device-instances/${selectedElement.deviceInstanceId}`;
                      window.open(url, '_blank');
                    }}
                    className="flex-shrink-0 p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                    title="Просмотр экземпляра"
                  >
                    👁️
                  </button>
                  {/* Edit Icon */}
                  {!isReadOnly && (
                    <button
                      onClick={() => {
                        setShowInstanceEditModal(true);
                      }}
                      className="flex-shrink-0 p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Редактировать экземпляр"
                    >
                      ✏️
                    </button>
                  )}
                  
                  {/* Clickable Areas */}
                  <div className="flex-1 flex items-center">
                    {/* ID Click Area */}
                    <span
                      onClick={async () => {
                        const textToCopy = selectedElement.deviceInstanceId.toString();
                        try {
                          await navigator.clipboard.writeText(textToCopy);
                      
                        } catch (err) {
                          console.error('Failed to copy ID:', err);
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = textToCopy;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      }}
                      className="text-blue-600 font-mono font-medium hover:bg-blue-50 px-1 py-0.5 rounded cursor-pointer"
                      title="Кликните для копирования ID"
                    >
                      {selectedElement.deviceInstanceId}:
                    </span>
                    
                    {/* Name Click Area */}
                    <span
                      onClick={async () => {
                        const textToCopy = selectedElement.deviceName;
                        try {
                          await navigator.clipboard.writeText(textToCopy);
                      
                        } catch (err) {
                          console.error('Failed to copy name:', err);
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = textToCopy;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      }}
                      className="ml-2 text-gray-900 font-medium hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer"
                      title="Кликните для копирования названия"
                    >
                      {selectedElement.deviceName || '—'}
                    </span>
                  </div>
                </div>

                {/* Second Line: Specification ID and Name */}
                <div className="flex items-center gap-1">
                  {/* View Icon */}
                  <button
                    onClick={() => {
                      const url = `/projects/${projectId}/devices/${selectedElement.specificationId}`;
                      window.open(url, '_blank');
                    }}
                    className="flex-shrink-0 p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                    title="Просмотр спецификации"
                  >
                    👁️
                  </button>
                  {/* Edit Icon */}
                  {!isReadOnly && (
                    <button
                      onClick={async () => {
                        try {
                          // Check if specificationId is valid
                          if (!selectedElement.specificationId || selectedElement.specificationId <= 0) {
                            throw new Error('Invalid specification ID');
                          }
                          
                          const specData = await getSpecificationItem(selectedElement.specificationId);
                          setSelectedSpecification(specData);
                          // Clear selection to prevent DEL key from deleting canvas elements
                          setSelectedNodes(new Set());
                          setSelectedEdges([]);
                          setSelectedEdge(null);
                          setShowSpecEditModal(true);
                        } catch (error) {
                          console.error('Error loading specification:', error);
                          
                          // Show user-friendly error message with more details
                          const errorMsg = `Ошибка: Спецификация с ID ${selectedElement.specificationId} не найдена в базе данных.\n\nЭлемент: ${selectedElement.deviceName}\nТип: ${selectedElement.specName}\n\nПожалуйста, проверьте данные элемента.`;
                          alert(errorMsg);
                        }
                      }}
                      className="flex-shrink-0 p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                      title="Редактировать спецификацию"
                    >
                      ✏️
                    </button>
                  )}
                  
                  {/* Clickable Areas */}
                  <div className="flex-1 flex items-center">
                    {/* ID Click Area */}
                    <span
                      onClick={async () => {
                        const textToCopy = selectedElement.specificationId.toString();
                        try {
                          await navigator.clipboard.writeText(textToCopy);
                      
                        } catch (err) {
                          console.error('Failed to copy spec ID:', err);
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = textToCopy;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      }}
                      className="text-green-600 font-mono font-medium hover:bg-green-50 px-1 py-0.5 rounded cursor-pointer"
                      title="Кликните для копирования ID спецификации"
                    >
                      {selectedElement.specificationId}:
                    </span>
                    
                    {/* Name Click Area */}
                    <span
                      onClick={async () => {
                        const textToCopy = selectedElement.specName;
                        try {
                          await navigator.clipboard.writeText(textToCopy);
                      
                        } catch (err) {
                          console.error('Failed to copy spec name:', err);
                          // Fallback for older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = textToCopy;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      }}
                      className="ml-2 text-gray-900 hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer"
                      title="Кликните для копирования названия спецификации"
                    >
                      {selectedElement.specName || '—'}
                    </span>
                  </div>
                </div>

                {/* Third Line: Manufacturer and Model */}
                {selectedElementSpecification && (
                  <div className="flex items-center gap-1">
                    <div className="flex-1 flex items-center text-sm text-gray-600">
                      {selectedElementSpecification.manufacturer && (
                        <span className="mr-2">
                          <span className="font-medium">Производитель:</span>{' '}
                          <span
                            onClick={async () => {
                              const textToCopy = selectedElementSpecification.manufacturer!;
                              try {
                                await navigator.clipboard.writeText(textToCopy);
                              } catch (err) {
                                console.error('Failed to copy manufacturer:', err);
                                const textArea = document.createElement('textarea');
                                textArea.value = textToCopy;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                              }
                            }}
                            className="hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer"
                            title="Кликните для копирования производителя"
                          >
                            {selectedElementSpecification.manufacturer}
                          </span>
                        </span>
                      )}
                      {selectedElementSpecification.model && (
                        <span>
                          <span className="font-medium">Модель:</span>{' '}
                          <span
                            onClick={async () => {
                              const textToCopy = selectedElementSpecification.model!;
                              try {
                                await navigator.clipboard.writeText(textToCopy);
                              } catch (err) {
                                console.error('Failed to copy model:', err);
                                const textArea = document.createElement('textarea');
                                textArea.value = textToCopy;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand('copy');
                                document.body.removeChild(textArea);
                              }
                            }}
                            className="hover:bg-gray-50 px-1 py-0.5 rounded cursor-pointer"
                            title="Кликните для копирования модели"
                          >
                            {selectedElementSpecification.model}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Fourth Line: Documentation URL */}
                {selectedElementSpecification?.documentation_url && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        window.open(selectedElementSpecification.documentation_url, '_blank');
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors text-sm font-medium"
                      title="Открыть документацию"
                    >
                      📖 Документация
                    </button>
                  </div>
                )}

                {/* Fifth Line: Terminal schema link on its own row */}
                {selectedElement?.type === 'terminal' && selectedElement.terminalBlockId && (
                  <div className="mt-2">
                    <button
                      onClick={() => {
                        const terminalSchemaUrl = `/projects/${projectId}/schemas/${selectedElement.terminalBlockId}`;
                        window.open(terminalSchemaUrl, '_blank');
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-md transition-colors text-sm font-medium"
                      title="Открыть схему терминалов"
                    >
                      🔗 Схема терминалов
                    </button>
                  </div>
                )}
              </div>

              {/* Remove button hidden for terminal auto nodes */}
              {/* Remove from Canvas Button - скрыт в режиме только просмотр и для terminal элементов */}
              {selectedElement?.type !== 'terminal' && !isReadOnly && (
                <div className="mt-6 pt-4 border-t">
                  <button
                    onClick={() => {
                      if (shouldShowMainSchemaFeatures) {
                        // Remove from main or breaker schema
                        removeFromMainSchema(selectedElement.deviceInstanceId);
                      }
                      setShowElementInfo(false);
                      setSelectedElement(null);
                    }}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    🗑️ Убрать с холста
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Modal Components for Main Schema - Conditional based on element type */}
        {selectedElement?.type === 'cable' ? (
          <CableInstanceEditModal
            key={cableModalKey}
            isOpen={showInstanceEditModal}
            onClose={() => setShowInstanceEditModal(false)}
            selectedInstance={cableInstanceData || cableInstanceForModal}
            projectId={projectId}
            onSuccess={() => {
              // Invalidate schema layout to refresh nodes with updated reserve status
              queryClient.invalidateQueries({ queryKey: ['schemaLayout', schemaType, schemaId] });
              // Invalidate available devices/cables for libraries
              queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId] });
              queryClient.invalidateQueries({ queryKey: ['availableCables', projectId] });
              queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] });
              queryClient.invalidateQueries({ queryKey: ['cableInstance', selectedElement?.deviceInstanceId] });
              // For terminal schema, also trigger reload
              // Refresh selected element data
              if (selectedElement) {
                setSelectedElement(null);
              }
            }}
          />
        ) : (
          <DeviceInstanceEditModal
            isOpen={showInstanceEditModal}
            onClose={() => setShowInstanceEditModal(false)}
            selectedInstance={selectedElement ? {
              id: selectedElement.deviceInstanceId,
              position_label: selectedElement.deviceName,
              specification_id: selectedElement.specificationId,
              device_type: 'device',
              device_type_id: selectedElement.deviceTypeId,
              spec_name: selectedElement.specName,
              manufacturer: selectedElement.manufacturer,
              model: selectedElement.model,
              knx_address: selectedElement.knxAddress,
              description: selectedElement.description || '',
              is_reserve: selectedElement.isReserve ? 1 : 0,
              project_id: projectId,
              created_at: new Date().toISOString()
            } as any : null}
            projectId={projectId}
            onSuccess={() => {
              // Invalidate schema layout to refresh nodes with updated reserve status
              queryClient.invalidateQueries({ queryKey: ['schemaLayout', schemaType, schemaId] });
              // Invalidate available devices/cables for libraries
              queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId] });
              queryClient.invalidateQueries({ queryKey: ['availableCables', projectId] });
              queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] });
              // For terminal schema, also trigger reload
              // Refresh selected element data
              if (selectedElement) {
                setSelectedElement(null);
              }
            }}
          />
        )}
        
        {selectedSpecification?.device_type === 'cable' ? (
          <CableSpecEditModal
            isOpen={showSpecEditModal}
            onClose={() => {
              setShowSpecEditModal(false);
              setSelectedSpecification(null);
            }}
            selectedCable={selectedSpecification}
            projectId={projectId}
            onSuccess={() => {
              // Invalidate schema layout to refresh nodes with updated reserve status
              queryClient.invalidateQueries({ queryKey: ['schemaLayout', schemaType, schemaId] });
              // Invalidate available devices/cables for libraries
              queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId] });
              queryClient.invalidateQueries({ queryKey: ['availableCables', projectId] });
              queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] });
              // For terminal schema, also trigger reload
              // Refresh selected element data
              if (selectedElement) {
                setSelectedElement(null);
              }
            }}
          />
        ) : (
          <DeviceSpecEditModal
            isOpen={showSpecEditModal}
            onClose={() => {
              setShowSpecEditModal(false);
              setSelectedSpecification(null);
            }}
            selectedDevice={selectedSpecification}
            projectId={projectId}
            onSuccess={() => {
              // Invalidate schema layout to refresh nodes with updated reserve status
              queryClient.invalidateQueries({ queryKey: ['schemaLayout', schemaType, schemaId] });
              // Invalidate available devices/cables for libraries
              queryClient.invalidateQueries({ queryKey: ['availableDevices', projectId] });
              queryClient.invalidateQueries({ queryKey: ['availableCables', projectId] });
              queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] });
              // For terminal schema, also trigger reload
              // Refresh selected element data
              if (selectedElement) {
                setSelectedElement(null);
              }
            }}
          />
        )}
      </div>
      </div>
    );
  }

  // Regular layout for other schema types
  return (
    <div className="schema-editor-shell universal-schema-editor h-screen flex flex-col bg-gray-100">
      {schemaEditorHeader}
      
      {/* Selection Control Panel */}
      {(selectedNodes.size > 0 || selectedEdges.length > 0) && (
        <div className="selection-panel bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-blue-800">
              Выбрано: {selectedNodes.size} нод, {selectedEdges.length} связей
            </span>
            <button
              onClick={() => {
                setSelectedNodes(new Set());
                setSelectedEdges([]);
                setSelectedEdge(null);
                setActivePopup(null);
              }}
              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              Снять выделение
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleBulkDelete()}
              className="text-xs px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
              title="Удалить выбранные элементы"
            >
              🗑️ Удалить
            </button>
          </div>
        </div>
      )}
      
      {/* Hidden Nodes Indicator */}
      {(hiddenNodesCount > 0 || anyLayerFilterOff) && (
        <div className="hidden-nodes-indicator bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-amber-800">
              {hiddenNodesCount > 0 ? `🔒 Скрыто элементов: ${hiddenNodesCount}` : '📋 Включены фильтры слоев (часть типов скрыта)'}
            </span>
            {hiddenNodesCount > 0 && (
              <div className="flex items-center space-x-2 text-xs text-amber-700">
                {Object.entries(hiddenNodesByType).map(([type, count]) => (
                  <span key={type} className="px-2 py-1 bg-amber-100 rounded">
                    {getDeviceTypeDisplayName(type)}: {count}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowLayersPanel(true)}
              className="text-xs px-3 py-1 bg-amber-500 text-white rounded hover:bg-amber-600"
              title="Открыть панель слоев"
            >
              📋 Управление слоями
            </button>
          </div>
        </div>
      )}
      

      
      {/* Create schema modal */}
      {showCreateSchemaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">
              Создать {config.name.toLowerCase()}
            </h3>
            
            <form onSubmit={handleCreateSchema} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название схемы
                </label>
                <input
                  type="text"
                  name="name"
                  defaultValue="Main Schema"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Описание
                </label>
                <textarea
                  name="description"
                  rows={3}
                  placeholder="Описание схемы (необязательно)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {config.electricalPanelRequired && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Электрощит
                  </label>
                  <select
                    name="electrical_panel_id"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Выберите щит</option>
                    {electricalPanels?.map((panel) => (
                      <option key={panel.id} value={panel.id}>
                        {panel.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="flex space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateSchemaModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  disabled={createSchemaMutation.isPending}
                >
                  {createSchemaMutation.isPending ? 'Создание...' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function for tool display names
function getToolDisplayName(tool: string): string {
  const toolNames: Record<string, string> = {
    'grid-snap': '🔗 Примагничивание',
    layers: '📋 Слои',
    connect: '🔗 Соединения',
    bus: '📊 Шины',
            print: '🖨️ Экспорт в PDF A4',
    search: '🔍 Поиск',
    'auto-layout': '📍 Авто-размещение',
    'terminal-block': '🔌 Блок клеммников',
    jumper: '🔄 Перемычки',
    'electrical-panel-selector': '🏠 Выбор щита',
    'knx-topology': '🌐 Топология KNX',
    'knx-programming': '⚙️ Программирование',
    'bus-topology': '🔗 Топология шины',
    'rs485-settings': '⚙️ Настройки RS-485',
    'network-topology': '🌐 Топология сети',
    'ethernet-settings': '⚙️ Настройки Ethernet'
  };
  
  return toolNames[tool] || tool;
}

// Helper function for device type display names
function getDeviceTypeDisplayName(deviceType: string): string {
  const deviceTypeNames: Record<string, string> = {
    lighting: '💡 Светильники',
    outlet: '🔌 Розетки',
    switch: '🎛️ Выключатели',
    sensor: '📡 Датчики',
    actuator: '🔧 Актуаторы',
    breaker: '⚡ Автоматы',
    cable: '🔗 Кабели',
    terminal: '🔌 Клеммники',
    reserve: '🟠 Резерв',
    other: '📦 Прочие'
  };
  
  return deviceTypeNames[deviceType] || deviceType;
}

function UniversalSchemaEditor({ forceSchemaType }: UniversalSchemaEditorProps) {
  const { projectId, schemaId } = useParams<{ 
    projectId: string; 
    schemaId: string; 
  }>();
  const location = useLocation();
  
  // Parse URL parameters
  const urlParams = new URLSearchParams(location.search);
  const blockIdFromUrl = urlParams.get('blockId');
  const focusDeviceInstanceId = urlParams.get('focus') ?? undefined;
  
  // Parse IDs first
  const parsedProjectId = projectId ? parseInt(projectId, 10) : 0;
  const parsedSchemaId = schemaId && !isNaN(parseInt(schemaId, 10)) ? parseInt(schemaId, 10) : undefined;
  const parsedBlockId = blockIdFromUrl ? parseInt(blockIdFromUrl, 10) : undefined;
  
  // NEW: Single useQuery to load all schema data at once
  const { data: schemaData, isLoading: isSchemaLoading, error: schemaError } = useQuery({
    queryKey: ['schemaWithTerminalBlock', parsedSchemaId, parsedProjectId],
    queryFn: async () => {
      if (!parsedSchemaId) return null;
      
      // Load schema info first
      const schema = await getSchema(parsedSchemaId);
      
      let terminalBlock = null;
      
      return {
        schema,
        terminalBlock
      };
    },
    enabled: !!parsedSchemaId && !forceSchemaType,
    staleTime: 5 * 60 * 1000, // 5 minutes - prevent excessive refetching
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: false, // Prevent refetch on component mount
    refetchOnReconnect: false // Prevent refetch on reconnect
  });
  
  // Show loading while schema is being loaded
  if (!forceSchemaType && !!parsedSchemaId && isSchemaLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Загрузка схемы...</h3>
          <p className="text-gray-500">Определение типа схемы</p>
        </div>
      </div>
    );
  }
  
  // Show error if schema not found
  if (!forceSchemaType && !!parsedSchemaId && schemaError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h3 className="text-lg font-medium text-red-700 mb-2">Ошибка</h3>
          <p className="text-red-500">Схема не найдена (ID: {parsedSchemaId})</p>
        </div>
      </div>
    );
  }
  
  // Extract data from loaded schema
  const schemaInfo = schemaData?.schema;

  
  // Determine schema type: forced > database schema_type > path fallback
  const schemaType = forceSchemaType || 
    schemaInfo?.schema_type || 
    (parsedBlockId ? 'terminal' : getSchemaTypeFromPath(location.pathname));
  

  
  // Extract electrical panel ID from schema (for main schemas) or URL state (for terminal schemas)
  const electricalPanelId = schemaInfo?.electrical_panel_id || location.state?.electricalPanelId;
  
  // For terminal schemas, use blockId from URL parameter
  const blockId = schemaType === 'terminal' ? parsedBlockId : undefined;
  

  
  // Validate project ID
  if (!parsedProjectId) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Ошибка</h3>
          <p className="text-gray-500">Некорректный ID проекта</p>
        </div>
      </div>
    );
  }

  // Auto-show create schema modal when no schema ID is provided or when /create is in URL
  const shouldShowCreateModal = (!parsedSchemaId && (schemaType === 'main' || schemaType === 'breaker')) || location.pathname.includes('/create');
  
  // TEMPORARY: Test minimal terminal schema to isolate React Error #310
  if (schemaType === 'terminal') {

    
    const TerminalSchemaEditor = React.lazy(() => import('./TerminalSchemaEditor'));
    return (
      <Suspense fallback={<div>Loading terminal schema editor...</div>}>
        <TerminalSchemaEditor 
          projectId={parsedProjectId} 
          schemaId={parsedSchemaId || 1} 
          blockId={blockId}
        />
      </Suspense>
    );
  }

  return (
    <ReactFlowProvider>
      <SchemaEditorContent
        schemaType={schemaType as SchemaType}
        projectId={parsedProjectId}
        schemaId={parsedSchemaId || 1}
        electricalPanelId={electricalPanelId}
        blockId={blockId}
        shouldShowCreateModal={shouldShowCreateModal}
        initialFocusNodeId={focusDeviceInstanceId}
        initialSchema={schemaInfo}
      />
    </ReactFlowProvider>
  );
}

export default UniversalSchemaEditor; 